/**
 * Inventory (BAR-124) — mock-parity build, 12 Jul 2026.
 *
 * Three pages, faithful to the official mock (docs/plans/bar-124-inventory/
 * inventory-map-mock.html): landing (message card, stat tiles, filter bar,
 * family fold groups) → cluster page (card, Simple/Detailed) → obligation
 * page (sources, answers, upload/sign/withdraw walks).
 *
 * Data honesty: everything rendered comes from inventory_service (graph +
 * the signed catalogue file). Numbers the platform does not have yet
 * (profile switch-offs, next-due dates, owners) show as em-dashes or say
 * "not wired yet" — never invented.
 *
 * Tokens: Tailwind theme names from tailwind.config.js (fg/fg-secondary/
 * fg-tertiary, surface(-sunken), border-default/strong, accent-copper,
 * bg-warm, shadow-bark-*); serif headings use font-heading (DM Serif
 * Display — the mock's --serif). Exception, flagged: the mock's
 * --warning-ink has no app token yet; text-warning stands in.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { plural } from '../../utils/plural';
import { formatDateTime } from '../../utils/formatDateTime';
import DialogShell from '../ui/DialogShell';
import ConfirmDialog from '../ui/ConfirmDialog';
import InventoryHelp from './InventoryHelp';

/* the Map is lazy: force-graph + d3-force stay out of the initial bundle */
const MapGraphView = React.lazy(() => import('./mapGraph/MapGraphView'));

const API = process.env.REACT_APP_INVENTORY_API || 'http://localhost:8091';

type NationalLayer = { country: string; country_name: string;
    status_note: string | null; instrument: string | null;
    provision: string | null; activation: string | null;
    text: string | null;
    pack_signed?: boolean; signed_by?: string | null;
    signed_at?: string | null; attachment_count?: number };

/* dense date cells go through the ONE locale-aware policy (U-06 —
   PR #598 review): '2026-09-30' → '30 Sep' / 'Sep 30' per locale, year
   added by the util when it is not this year */
const fmtDay = (iso: string): string =>
    formatDateTime(`${iso}T00:00:00`, { context: 'date-compact' });

type ClusterRow = {
    id: string; name: string; obligations: number; answered: number; flagged: number;
    citations: number; source_instruments: string | null; signed: boolean; signed_at: string | null;
};
type OblRow = {
    wiring_waiting?: number;
    id: string; title: string; obligation_type: string; applies_when: string;
    plain_language: string | null; adoption: 'suggested' | 'accepted' | 'dismissed';
    change_status: string | null; citations: number; answers: number; confirmed: number;
    answered_by?: { id: string; name: string; owner?: string | null }[];
    /* the report cycle (6 Aug): signed word + computed date; a candidate
       only marks the row — the verdict lives in Actions */
    duty_owner?: string | null;
    cycle?: string | null; cycle_offset_days?: number | null;
    cycle_waiting?: boolean; next_due?: string | null;
    due_kind?: 'due' | 'period_end' | 'event' | 'none' | 'set' | null;
    lands_weekend?: boolean; override_passed?: boolean;
    override_by?: string | null; override_reason?: string | null;
    /* the two-layer fields (29 Jul, LR edition) — null on flat clusters */
    row_class?: string | null; parent_id?: string | null;
    /* ALWAYS a list from the engine — a row can hang under several
       parents (a condition qualifying twenty duties). parent_id is the
       old single slot and holds only the first of them. */
    parent_ids?: string[] | null; source_label?: string | null;
    profile_switch?: unknown; provisional?: boolean | null; expired_parts?: string | null;
    children?: string[];
};
type Citation = {
    /* nullable since the eighteen-cluster reader: leverage-ratio's vocabulary
       has no tier, and an instrument can be absent on a prose-only citation */
    instrument: string | null; provision: string | null; tier: string | null;
    pin_status: string | null;
    /* the ruling law as proof (16 Jul): the dated hashed copy behind the row */
    snapshot?: { id: string; version: string | null; captured: string | null;
                 hash: string | null; stored: string | null; used_by: number;
                 viewable?: boolean } | null;
};
type Answer = {
    id: string; name: string; origin: string | null; data_origin: string | null;
    confirmed_by: string | null; kind: string | null;
};
type OblDetail = {
    id: string; title: string; catalogue_title: string | null;
    national_layer?: NationalLayer | null;
    national_layers?: NationalLayer[];
    bank_cycle?: { cycle?: string | null; cycle_offset_days?: number | null;
        cycle_waiting?: boolean; next_due?: string | null;
        due_kind?: 'due' | 'period_end' | 'event' | 'none' | 'set' | null;
        lands_weekend?: boolean; override_passed?: boolean;
        override_date?: string | null; override_by?: string | null;
        override_reason?: string | null } | null;
    /* the duty's owner rides the TOP level, decoupled from the cycle —
       a cycle failure must never erase the accountable person, and a
       failed read is an explicit flag, never "no owner" (PR #608 review) */
    owner?: string | null; owner_unavailable?: boolean;
    /* who named this owner and when — the natural second question on an
       accountability line (PR #608 round 2) */
    owner_set_by?: string | null; owner_set_at?: string | null;
    plain_language: string; obligation_type: string;
    applies_when: string; change_status: string | null; citations: Citation[]; answers: Answer[];
    flag: { note: string; source: string | null; method: string | null; at: string | null } | null;
    adoption: 'suggested' | 'accepted' | 'dismissed';
    applies_to?: { entity_id: string; name: string; level: string }[];
};
type ArtifactRow = {
    id: string; name: string; kind: string; policy_type: string | null;
    data_origin: string | null; added_by?: string | null; answers: number;
    description?: string | null;
    obligations?: { id: string; title: string }[]; clusters?: string[];
    support_clusters?: string[]; role?: string | null; supports_only?: boolean;
};
type UploadReview = {
    id: string; name: string; kind: string | null; content_hash: string; storage_location: string;
    size_bytes: number; filename: string; obligations: { id: string; title: string }[];
};
type ProfileData = {
    id: string | null;
    facts: Record<string, string | null>;
    identity?: Record<string, string | null>;
    signed: boolean; signed_at: string | null;
    signed_by?: string | null;
    changed_since_signing?: string[];
    records: { id: string; outcome: string; scope: string | null; note: string | null; by: string | null; at: string | null }[];
};
type SearchResults = {
    clusters: { id: string; name: string; obligations: number }[];
    obligations: { id: string; title: string; obligation_type: string; cluster_id: string; cluster_name: string }[];
    artifacts: { id: string; name: string; kind: string; data_origin: string | null }[];
};

/* Family grouping = the mock's approved mapping (legal framework that creates
   each cluster). Navigation metadata, kept in the client until the loader
   carries it. */
const FAMILIES: { key: string; title: string }[] = [
    { key: 'pru', title: 'Prudential' },
    { key: 'crisis', title: 'Crisis & safety net' },
    { key: 'fincrime', title: 'Financial crime' },
    { key: 'conduct', title: 'Conduct & customers' },
    { key: 'markets', title: 'Markets & instruments' },
    { key: 'digital', title: 'Digital & operational resilience' },
    { key: 'group', title: 'Group structure' },
    { key: 'other', title: 'Other frameworks' },
];
const FAMILY_OF: Record<string, string> = {
    'CLUSTER-IRRBB': 'pru', 'CLUSTER-CREDIT-RISK': 'pru', 'CLUSTER-ESG': 'pru',
    'CLUSTER-FIT-PROPER': 'pru', 'CLUSTER-ICAAP-ILAAP': 'pru', 'CLUSTER-INT-GOV': 'pru',
    'CLUSTER-LARGE-EXP': 'pru', 'CLUSTER-LEVERAGE': 'pru', 'CLUSTER-LIQUIDITY': 'pru',
    'CLUSTER-MARKET-RISK': 'pru', 'CLUSTER-OP-RISK': 'pru', 'CLUSTER-OUTSOURCING': 'pru',
    'CLUSTER-OWN-FUNDS': 'pru', 'CLUSTER-PILLAR3': 'pru', 'CLUSTER-REMUNERATION': 'pru',
    'CLUSTER-RAS': 'pru', 'CLUSTER-STRESS-TESTING': 'pru', 'CLUSTER-SUP-REPORTING': 'pru',
    'CLUSTER-DGS': 'crisis', 'CLUSTER-RECOVERY': 'crisis', 'CLUSTER-RESOLUTION-MREL': 'crisis',
    'CLUSTER-AML-CFT': 'fincrime', 'CLUSTER-SANCTIONS': 'fincrime',
    'CLUSTER-CONSUMER-CREDIT': 'conduct', 'CLUSTER-MIFID': 'conduct', 'CLUSTER-PSD2': 'conduct',
    'CLUSTER-CROWDFUNDING': 'conduct', 'CLUSTER-IDD': 'conduct',
    'CLUSTER-BMR': 'markets', 'CLUSTER-EMIR-CLEARING': 'markets', 'CLUSTER-EMIR-REPORTING': 'markets',
    'CLUSTER-MAR': 'markets', 'CLUSTER-COVERED-BONDS': 'markets', 'CLUSTER-SECURITISATION': 'markets',
    'CLUSTER-SFTR': 'markets',
    'CLUSTER-DORA': 'digital', 'CLUSTER-FICOD': 'group',
    'CLUSTER-NPL-SERVICERS': 'other', 'CLUSTER-WHISTLEBLOW': 'other', 'CLUSTER-MICA': 'other',
};

/* item 11 (6 Aug): the screen vocabulary lives in the ENGINE
   (/api/inventory/vocabulary — vocabulary.py is the one home, the
   one-name rule extended). The maps below are the BAKED-IN FALLBACK for
   a dead engine only; hydrateVocabulary() merges the served words in
   place at boot. Change words in the engine, never here. */
const KIND_DESC_BASE: Record<string, string> = {
    Policy: 'board-approved rules and charters',
    Model: 'the calculation engines',
    Data: 'the datasets the models consume',
    Deliverable: 'what gets produced and filed',
};
let _vocabLoaded = false;
async function hydrateVocabulary(): Promise<boolean> {
    if (_vocabLoaded) return false;
    try {
        const v = await get<any>('/api/inventory/vocabulary');
        Object.assign(CLUSTER_DESC, v.clusters || {});
        Object.assign(TYPE_DESC, v.types || {});
        for (const [k, kv] of Object.entries<any>(v.kinds || {})) {
            if (kv?.label) KIND_LABEL[k] = kv.label;
            if (kv?.plural) KIND_PLURAL[k] = kv.plural;
            if (kv?.desc) KIND_DESC_BASE[k] = kv.desc;
        }
        Object.assign(ORIGIN_LABEL, v.origins || {});
        _vocabLoaded = true;
        return true;
    } catch { return false; }
}

/* Cluster one-liners — the engine's copy wins at boot (see above). */
const CLUSTER_DESC: Record<string, string> = {
    'CLUSTER-IRRBB': 'interest-rate risk in the banking book — EVE and NII measurement, outlier tests, behavioural models',
    'CLUSTER-AML-CFT': 'customer due diligence, transaction monitoring, suspicious-activity reports to Finanspolisen',
    'CLUSTER-BMR': 'use of interest-rate benchmarks in contracts; fallback plans',
    'CLUSTER-CONSUMER-CREDIT': 'creditworthiness assessment, pre-contract information, marketing rules',
    'CLUSTER-CREDIT-RISK': 'credit-granting standards, monitoring, provisioning, NPE handling',
    'CLUSTER-DGS': 'depositor information + data files to Riksgälden (insättningsgarantin)',
    'CLUSTER-EMIR-CLEARING': 'clearing obligation + risk-mitigation techniques for OTC derivatives',
    'CLUSTER-EMIR-REPORTING': 'derivative trade reporting to a trade repository',
    'CLUSTER-ESG': 'ESG risks in strategy, governance and risk appetite',
    'CLUSTER-FIT-PROPER': 'suitability of board members and key function holders',
    'CLUSTER-ICAAP-ILAAP': 'internal capital + liquidity adequacy assessments; SREP dialogue with FI',
    'CLUSTER-DORA': 'ICT risk management, incident reporting, resilience testing, third-party register',
    'CLUSTER-INT-GOV': 'board oversight, three lines of defence, internal control functions',
    'CLUSTER-LARGE-EXP': 'limits + reporting on single-counterparty concentration',
    'CLUSTER-LEVERAGE': 'non-risk-based capital floor; calculation and reporting',
    'CLUSTER-LIQUIDITY': 'LCR + NSFR requirements; liquidity risk management',
    'CLUSTER-MARKET-RISK': 'capital + management of trading-book and FX risk',
    'CLUSTER-MIFID': 'investor protection: suitability, inducements, best execution',
    'CLUSTER-NPL-SERVICERS': 'rules for buying and servicing non-performing loans',
    'CLUSTER-OP-RISK': 'operational-risk management + capital, incl. legal risk',
    'CLUSTER-OUTSOURCING': 'governance of outsourced functions; register; exit plans',
    'CLUSTER-OWN-FUNDS': 'quality and level of capital: CET1, buffers, distributions',
    'CLUSTER-PSD2': 'strong customer authentication, incident reporting, third-party access',
    'CLUSTER-PILLAR3': 'public disclosure of risk, capital and remuneration',
    'CLUSTER-RECOVERY': 'recovery plan: indicators, scenarios, recovery options',
    'CLUSTER-REMUNERATION': 'pay policies; deferral and instruments for material risk takers',
    'CLUSTER-RESOLUTION-MREL': 'resolvability data to Riksgälden; MREL requirement',
    'CLUSTER-RAS': 'board-set risk appetite statement cascaded into limits',
    'CLUSTER-SANCTIONS': 'screening customers and payments against sanctions lists',
    'CLUSTER-SECURITISATION': 'risk retention, due diligence, transparency for securitisations',
    'CLUSTER-STRESS-TESTING': 'firm-wide stress-testing programme + supervisory exercises',
    'CLUSTER-SUP-REPORTING': 'COREP/FINREP + cross-risk returns; risk-specific returns live in each risk area',
    'CLUSTER-WHISTLEBLOW': 'internal reporting channels + protection of reporters',
    /* ── AI-PROPOSED DRAFTS (12 Jul, Kawi's (a)) — UNVERIFIED, not from the
       pipeline; Kawi reviews these 7; replace with verified pipeline texts ── */
    'CLUSTER-COVERED-BONDS': 'issuing covered bonds — cover pool, register, investor protection',
    'CLUSTER-CROWDFUNDING': 'rules for operating a crowdfunding platform',
    'CLUSTER-FICOD': 'extra group supervision when banking and insurance mix',
    'CLUSTER-IDD': 'rules for selling or advising on insurance products',
    'CLUSTER-MAR': 'insider dealing, unlawful disclosure and market manipulation',
    'CLUSTER-MICA': 'rules for crypto-asset services and issuance',
    'CLUSTER-SFTR': 'reporting of securities-financing transactions (repos, lending)',
};
const TYPE_ORDER = ['Governance', 'Methodology', 'Quantitative limit', 'Process', 'Reporting', 'Disclosure'];
/* platform-generic wording — never names a specific supervisor; the
   supervisor's name comes from the institution profile once wired
   (profile contract, fact 2). Cluster one-liners below ARE tenant
   content (Demo Bank AB is Swedish), so they may name FI/Riksgälden. */
const TYPE_DESC: Record<string, string> = {
    Governance: 'what the board must decide and see',
    Methodology: 'how it must be done, and on what basis',
    'Quantitative limit': 'hard thresholds the results must respect',
    Process: 'activities that must run and leave a record',
    Reporting: 'what goes to the supervisor, and when',
    Disclosure: 'what must be published',
};
const KIND_LABEL: Record<string, string> = {
    Policy: 'Governing document', Model: 'Model', Data: 'Data', Deliverable: 'Record / filing',
};
/* machine origins never render raw (Kawi, 16 Jul: "BARK proposed", not
   ai_proposed) — display words only; the stored value is untouched */
const ORIGIN_LABEL: Record<string, string> = {
    ai_proposed: 'BARK proposed', human_confirmed: 'confirmed', load_asserted: 'loaded',
};
const KIND_PLURAL: Record<string, string> = {
    Policy: 'Governing documents', Model: 'Models', Data: 'Data', Deliverable: 'Records & filings',
};
/* short legal refs + national-source detection — display maps ported from the mock */
function shortRef(instrument: string | null): string {
    return (instrument || '(source not named)')
        .replace('CRD (Directive 2013/36/EU)', 'CRD')
        .replace('CRR (Regulation (EU) No 575/2013)', 'CRR')
        .replace('EBA Guidelines on IRRBB and CSRBB', 'EBA/GL/2022/14')
        .replace('EBA Guidelines on SREP', 'EBA/GL/2022/03')
        .replace('RTS on supervisory shock scenarios and common assumptions for IRRBB SOT', 'RTS 2024/856')
        .replace('RTS on standardised methodology for IRRBB', 'RTS 2024/857')
        .replace('ITS on Pillar 3 disclosures', 'P3 ITS 2021/637')
        .replace('EBA Single Rulebook Q&A', 'EBA Q&A');
}
// PROFILE-DRIVEN FOLLOW-UP: knows only Swedish national-source patterns today;
// must read the tenant's supervisor/country from the institution profile
const isNational = (instrument: string | null) => /FFFS|FI dnr|Finansinspektionen/i.test(instrument || '');

/* the session token rides on every service call — required once the service
   enforces auth (the signature gate); harmless while it does not */
let _token: string | null = null;
export function setInventoryToken(t: string | null) { _token = t; }

/* which bank the user is working AS — sent on every call as X-Institution so
   the engine keys the profile, queue and group on it (Kawi, 18 Jul: "how do i
   even switch between instances/banks?"). Persisted, so a reload stays on the
   same bank; the shared law corpus (clusters, map) ignores the header. Read
   from storage at module load, so the very first fetch already carries it. */
const INSTITUTION_KEY = 'bark_inventory_institution';
let _institution: string | null =
    (typeof localStorage !== 'undefined' && localStorage.getItem(INSTITUTION_KEY)) || null;
export function activeInstitution(): string | null { return _institution; }
export function setInventoryInstitution(id: string | null): void {
    _institution = (id || '').trim() || null;
    if (typeof localStorage === 'undefined') return;
    if (_institution) localStorage.setItem(INSTITUTION_KEY, _institution);
    else localStorage.removeItem(INSTITUTION_KEY);
}
export function authHeaders(): Record<string, string> {
    const h: Record<string, string> = _token ? { Authorization: `Bearer ${_token}` } : {};
    if (_institution) h['X-Institution'] = _institution;
    return h;
}

/* who am I / what may I — from the engine (Kawi, 20 Jul: roles + the
   user's standing entity choice live in the BACKEND). Default is the
   signer view: in dev (auth off) the engine says is_signer=true anyway,
   and if /me ever fails the server gates still refuse a non-signer. */
export type Me = { uid: string; email?: string | null; name?: string | null;
    is_signer: boolean; active_instance?: string; chosen?: string | null };
export const MeContext = React.createContext<Me>({ uid: 'anonymous', is_signer: true });
/* the badge follows the ACT, not the clock (Kawi, 6 Aug: a decided row
   left "· 1" standing for up to a minute): any signed verdict pokes this
   and the panel's badge refetches at once. Module-level, so a page
   component reaches it without prop drilling; the minute clock stays as
   the fallback for changes made elsewhere. */
export type ProfileFocus = { dp_id: string; key: string; label: string;
    value: string; quote: string; artifact_name?: string | null };

let _badgePoke: (() => void) | null = null;
export function pokeVerdictsBadge(): void { _badgePoke?.(); }
export function _setBadgePoke(fn: (() => void) | null): void { _badgePoke = fn; }

export async function fetchMe(): Promise<Me | null> {
    try {
        const r = await fetch(`${API}/api/inventory/me`, { headers: authHeaders() });
        if (!r.ok) return null;
        return await r.json();
    } catch { return null; }
}
/* the standing choice is stored server-side too — awaited BEFORE any
   reload, or the write dies with the page */
export async function rememberChoice(id: string | null): Promise<void> {
    if (!id) return;
    try {
        await fetch(`${API}/api/inventory/me/active-instance`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ id }),
        });
    } catch { /* the browser memory still carries it */ }
}

async function get<T>(path: string): Promise<T> {
    const r = await fetch(`${API}${path}`, { headers: authHeaders() });
    if (!r.ok) throw new Error(`${r.status} ${path}`);
    return r.json();
}

/* the rename act — one engine call, throws the engine's own words on refusal */
async function renameCall(path: string, name: string, by: string): Promise<void> {
    const r = await fetch(`${API}${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name, by }),
    });
    if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
}

/* ---------- shared pieces (mock: .page/.pagesub/.nextcard/.exmark/.dot) ---------- */

const PageTitle = ({ children }: { children: React.ReactNode }) => (
    <h1 className="font-heading font-normal text-[32px] leading-[1.15] tracking-[-0.02em] text-primary mb-1.5 [text-wrap:balance]">
        {children}
    </h1>
);

/* Inline rename — the BAR-32 design (EditableProjectTitle): click the title
   or the pencil, Enter saves, Escape cancels, blur saves, double-submit
   guarded. Here every rename is a signed act on record (engine rule). */
function EditableTitle({ value, onSave }: { value: string; onSave: (name: string) => Promise<void> }) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [saveErr, setSaveErr] = useState('');
    const commitInFlightRef = useRef(false);
    useEffect(() => {
        if (isEditing) { inputRef.current?.focus(); inputRef.current?.select(); }
    }, [isEditing]);
    useEffect(() => { if (!isEditing) setDraft(value); }, [value, isEditing]);
    const cancel = () => { setDraft(value); setSaveErr(''); setIsEditing(false); };
    const commit = async () => {
        if (commitInFlightRef.current) return;
        const trimmed = draft.trim();
        if (!trimmed || trimmed === value) { cancel(); return; }
        commitInFlightRef.current = true;
        setSubmitting(true);
        try {
            await onSave(trimmed);
            setIsEditing(false);
        } catch (e) {
            setSaveErr(`Failed to rename (${String(e)}). Nothing was changed.`);
        } finally {
            setSubmitting(false);
            commitInFlightRef.current = false;
        }
    };
    if (isEditing) {
        return (
            <>
                <input ref={inputRef} type="text" value={draft}
                    onChange={e => { setDraft(e.target.value); setSaveErr(''); }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); commit(); }
                        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                    }}
                    onBlur={commit} disabled={submitting} maxLength={200}
                    aria-label="Rename"
                    className="w-full max-w-[720px] mb-1.5 px-2 py-0.5 font-heading font-normal text-[32px] leading-[1.15] tracking-[-0.02em] text-primary bg-surface-sunken border border-border-default rounded-md focus:outline-none focus:border-accent focus:shadow-bark-focus transition-shadow disabled:opacity-60" />
                {/* the error lands HERE, not in a browser alert (S-04) */}
                {saveErr && <span className="block text-[12.5px] text-danger mb-1.5">{saveErr}</span>}
            </>
        );
    }
    return (
        <span className="group inline-flex items-baseline gap-1.5 min-w-0">
            <h1 onClick={() => { setDraft(value); setIsEditing(true); }} title="Rename"
                className="font-heading font-normal text-[32px] leading-[1.15] tracking-[-0.02em] text-primary mb-1.5 [text-wrap:balance] cursor-text hover:bg-bg-warm rounded px-1 -mx-1 transition-colors">
                {value}
            </h1>
            <button type="button" onClick={() => { setDraft(value); setIsEditing(true); }}
                aria-label="Rename" title="Rename"
                className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-fg-tertiary opacity-60 hover:opacity-100 hover:bg-bg-warm focus:opacity-100 focus:outline-none transition-opacity">
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
        </span>
    );
}
const PageSub = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[13px] text-fg-tertiary mb-[26px]">{children}</p>
);
const Dot = ({ tone }: { tone: 'g' | 'a' | 'mute' }) => (
    <span className={`inline-block w-2 h-2 rounded-full flex-none ${
        tone === 'g' ? 'bg-success' : tone === 'a' ? 'bg-warning' : 'bg-border-strong'}`} />
);
/* Object-identity chips (DESIGN.md chip-cluster/chip-obligation/chip-artifact):
   fonts and layout are identical everywhere — the chip colour alone says
   what you hold. cluster = navy, obligation = teal, artifact = copper. */
const CHIP_STYLE: Record<'cluster' | 'obligation' | 'artifact', string> = {
    cluster: 'bg-primary-tint text-primary',
    obligation: 'bg-secondary-tint text-secondary-darker',
    artifact: 'bg-accent-copper-tint text-accent-copper-ink',
};
const IdChip = ({ kind, children }: { kind: 'cluster' | 'obligation' | 'artifact'; children: React.ReactNode }) => (
    <span className={`font-mono text-[11px] font-medium rounded-sm px-2 py-1 whitespace-nowrap ${CHIP_STYLE[kind]}`}>
        {children}
    </span>
);

const ExMark = () => (
    <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-accent-copper bg-accent-copper-tint rounded-sm px-[5px] py-px ml-1 align-middle">
        example
    </span>
);
/* GLOBAL search life (Kawi, 21 Jul: "why dont you give me the searching
   ongoing icon or something? ive asked 100 times") — a module store, so the
   signal survives tab switches and bank remounts. Every long search
   registers here; the pulse renders in the tab bar and the page heads. */
const runningSearches = new Map<string, string>();
const searchWatchers = new Set<() => void>();
let searchSeq = 0;
function searchStarted(label: string): string {
    const id = `run-${++searchSeq}`;
    runningSearches.set(id, label);
    searchWatchers.forEach(w => w());
    return id;
}
function searchEnded(id: string) {
    runningSearches.delete(id);
    searchWatchers.forEach(w => w());
}
export function useRunningSearches(): string[] {
    const [, tick] = useState(0);
    useEffect(() => {
        const w = () => tick(x => x + 1);
        searchWatchers.add(w);
        return () => { searchWatchers.delete(w); };
    }, []);
    return Array.from(runningSearches.values());
}
export const SearchPulse = () => {
    const runs = useRunningSearches();
    if (!runs.length) return null;
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-fg-secondary whitespace-nowrap">
            <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse shrink-0" />
            Searching {runs[0]}{runs.length > 1 ? ` · +${runs.length - 1} more` : ''}…
        </span>
    );
};

/* the standing sentence (Kawi, 21 Jul, mock blessed: "build") — one grey
   line under the title that says WHAT the page is, in the bank's words.
   Same spot on every object page; the register and map keep their subs. */
const LaneLabel = ({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) => (
    <div className="flex items-center gap-3 text-[11px] font-semibold tracking-[0.07em] uppercase text-fg-tertiary mt-[26px] mb-2.5">
        {children}
        {right && <span className="ml-auto normal-case tracking-normal">{right}</span>}
    </div>
);

function NextCard({ warn, kicker, title, detail, action }: {
    warn?: boolean; kicker: string; title: string; detail: string;
    action?: { label: string; onClick: () => void };
}) {
    return (
        <div className={`flex justify-between items-center gap-6 flex-wrap border rounded-lg shadow-bark-md px-[22px] py-5 mt-6 mb-[30px] ${
            warn ? 'bg-warning-bg border-warning' : 'bg-surface border-border-default'}`}>
            <div>
                <div className={`text-[11px] font-semibold tracking-[0.07em] uppercase ${
                    warn ? 'text-warning-ink' : 'text-secondary-hover'}`}>{kicker}</div>
                <div className="text-[15px] font-semibold text-fg mt-1">{title}</div>
                <div className="text-[13px] text-fg-tertiary mt-[3px]">{detail}</div>
            </div>
            {action && (
                <button onClick={action.onClick}
                    className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover">
                    {action.label}
                </button>
            )}
        </div>
    );
}

function SegButtons({ options, value, onChange, small }: {
    options: { key: string; label: string }[]; value: string;
    onChange: (k: string) => void; small?: boolean;
}) {
    return (
        <div className="inline-grid grid-flow-col auto-cols-fr border border-border-strong rounded-md overflow-hidden">
            {options.map((o, i) => (
                <button key={o.key} onClick={() => onChange(o.key)}
                    className={`${small ? 'text-[11px] px-3.5 py-[5px]' : 'text-[13px] px-[13px] py-2'} font-semibold ${
                        i > 0 ? 'border-l border-border-default' : ''} ${
                        value === o.key ? 'bg-primary text-white' : 'bg-surface text-fg-secondary hover:bg-surface-sunken'}`}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function Loading({ error }: { error?: string }) {
    return (
        <div className="p-10 text-fg-tertiary text-[13px]">
            {error ? `Could not load the inventory: ${error}. Is the inventory service running?` : 'Loading…'}
        </div>
    );
}

/* ONE row shape for every connection on an entry page (Kawi, 7 Aug:
   "governs by this and arrow....nobody understands"). The relationship
   is a quiet word in a fixed column, the name is the only link, and the
   arrow always means the same thing — open this. Rows with no page to
   open (a loaded regulation, a run) simply have no link. */
function ConnRow({ rel, name, id, onOpen, act }: {
    rel: string; name: string; id: string;
    onOpen?: () => void; act?: React.ReactNode;
}) {
    return (
        <div className="flex items-baseline gap-4 px-3.5 py-2.5 text-[13px]">
            <span className="w-[104px] shrink-0 text-[11.5px] text-fg-tertiary">{rel}</span>
            <span className="min-w-0">
                {onOpen
                    ? <button onClick={onOpen}
                        className="font-semibold text-primary hover:underline text-left">{name} →</button>
                    : <b className="text-fg font-semibold">{name}</b>}
            </span>
            <span className="ml-auto flex items-baseline gap-4 text-[11px] whitespace-nowrap">
                <span className="font-mono text-fg-tertiary">{id}</span>
                {act}
            </span>
        </div>
    );
}

/* what a MEMBER sees where a signer has buttons (mock blessed 20 Jul) */
const WaitingForSigner = () => (
    <p className="text-[11.5px] text-accent-copper mt-2">Waiting for a signer to decide.</p>
);

/* switching banks reloads the app onto the chosen one — the surest way to
   clear every page's cached data so no bank ever shows another's facts. Behind
   a tiny indirection so a test can assert the switch without a real reload. */
export const nav = { reload: () => window.location.reload() };

type Bank = { id: string; name: string; approved_facts: number; is_default: boolean };

/* The bank switcher (Kawi, 18 Jul: "how do i even switch between
   instances/banks?"). The button shows the bank you are working AS; the menu
   lists every bank (shared law corpus, its own profile each), and lets you
   switch, add a new one, or open the current bank's profile. */
export function BankSwitcher({ activeName, signer, onSwitch }:
    { activeName: string; signer: string;
      /* no-reload switching (Kawi, 20 Jul: the login flash) — the parent
         refreshes in place */
      onSwitch: (id: string | null) => void | Promise<void> }) {
    const [banks, setBanks] = useState<Bank[] | null>(null);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState('');
    const [err, setErr] = useState('');
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const [busy, setBusy] = useState(false);
    /* remove-from-list (Kawi, 20 Jul: "delete in the front end at least") —
       archives the bank; every record stays in the database */
    const [rmFor, setRmFor] = useState<Bank | null>(null);
    const boxRef = useRef<HTMLDivElement | null>(null);

    const load = () => {
        setErr('');
        get<{ institutions: Bank[]; active: string }>('/api/inventory/institutions')
            .then(d => { setBanks(d.institutions); setActive(d.active); })
            .catch(e => setErr(String(e)));
    };
    // first open loads the list once; the `banks === null` guard stops any reload
    useEffect(() => { if (open && banks === null) load(); }, [open, banks]);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const switchTo = async (id: string) => {
        if (id === active) { setOpen(false); return; }   // already here — no surprise flip (Kawi, 20 Jul)
        setInventoryInstitution(id);
        await rememberChoice(id);   // the backend keeps your standing (per user)
        setActive(id);              // the ✓ follows without a reboot
        setOpen(false);
        await onSwitch(id);         // in-place refresh — the app does NOT reboot
    };

    const createBank = async () => {
        const name = newName.trim();
        if (!name || busy) return;
        setBusy(true); setErr('');
        try {
            const r = await fetch(`${API}/api/inventory/institutions`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ name, by: signer }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            const bank: Bank = await r.json();
            setInventoryInstitution(bank.id);
            await rememberChoice(bank.id);
            setAdding(false); setNewName(''); setBusy(false); setOpen(false);
            setActive(bank.id); setBanks(null);   // fresh list next open
            await onSwitch(bank.id);   // land on the new, empty bank — no reboot
        } catch (e) {
            setErr(String(e)); setBusy(false);
        }
    };

    return (
        <div ref={boxRef} className="relative">
            <button onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
                className="text-[11px] font-semibold text-fg-tertiary border border-border-default rounded-md px-2 py-1.5 hover:bg-surface-sunken whitespace-nowrap">
                {activeName || 'Institution profile'} ▾
            </button>
            {open && (
                <div role="menu" className="absolute right-0 mt-1 w-72 bg-surface border border-border-default rounded-lg shadow-bark-md z-30 py-1.5 text-[13px]">
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-fg-tertiary">Working as</div>
                    {banks === null && !err && <div className="px-3 py-2 text-fg-tertiary">Loading…</div>}
                    {err && <div className="px-3 py-2 text-warning">Could not load banks: {err}</div>}
                    {banks && banks.map(b => (
                        <React.Fragment key={b.id}>
                            <div role="menuitem"
                                className={`group w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-surface-sunken cursor-pointer ${b.id === active ? 'text-fg font-semibold' : 'text-fg-secondary'}`}
                                onClick={() => switchTo(b.id)}>
                                <span className="w-3 shrink-0">{b.id === active ? '✓' : ''}</span>
                                <span className="flex-1 truncate">{b.name}</span>
                                <span className="text-fg-tertiary text-[11px] tabular-nums">{b.approved_facts} facts</span>
                                {/* remove-from-list — EVERY bank (Kawi, 20 Jul: "and get to
                                    the empty"); removing the one you stand in jumps to the next
                                    living bank, and after the last one to day zero */}
                                {(
                                    <button aria-label={`remove ${b.name}`}
                                        onClick={ev => { ev.stopPropagation(); setRmFor(b); setErr(''); }}
                                        className="text-[11px] text-fg-tertiary opacity-0 group-hover:opacity-100 hover:underline shrink-0">
                                        remove</button>
                                )}
                            </div>
                            {rmFor?.id === b.id && (
                                <div className="px-3 py-2 bg-bg-warm text-[11.5px] text-fg-secondary">
                                    Removes <b>{b.name}</b> from the app — every record stays in the database.
                                    <div className="flex gap-2 mt-1.5">
                                        <button onClick={async ev => {
                                            ev.stopPropagation();
                                            try {
                                                const r = await fetch(`${API}/api/inventory/institutions/${b.id}/archive`, {
                                                    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                                    body: JSON.stringify({ by: signer }),
                                                });
                                                if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
                                                if (b.id === active || b.id === activeInstitution()) {
                                                    // you removed the ground you stand on — jump to
                                                    // the next living bank, or to day zero
                                                    const d = await fetch(`${API}/api/inventory/institutions`, { headers: authHeaders() })
                                                        .then(x => x.json()).catch(() => ({ institutions: [] }));
                                                    const next = (d.institutions || []).find((x: Bank) => x.id !== b.id);
                                                    setInventoryInstitution(next ? next.id : null);
                                                    await rememberChoice(next ? next.id : null);
                                                    setRmFor(null); setOpen(false);
                                                    setActive(next ? next.id : ''); setBanks(null);
                                                    await onSwitch(next ? next.id : null);
                                                    return;
                                                }
                                                setRmFor(null); load();   // the list refreshes without it
                                            } catch (e) { setErr(String(e)); }
                                        }}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover">
                                            Remove — as {signer}</button>
                                        <button onClick={ev => { ev.stopPropagation(); setRmFor(null); }}
                                            className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                    </div>
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                    <div className="my-1 border-t border-border-default" />
                    {/* "Open this bank's profile" RETIRED 21 Jul (Kawi: debris)
                        — the profile's doors are /institution and the Actions
                        rows; the switcher only switches */}
                    <button role="menuitem" onClick={() => setAdding(true)}
                        className="w-full text-left px-3 py-2 text-fg-secondary hover:bg-surface-sunken">＋ Add a bank</button>
                </div>
            )}
            {adding && (
                <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8"
                    onMouseDown={e => { if (e.target === e.currentTarget) setAdding(false); }}>
                    <div className="bg-surface rounded-lg shadow-bark-md max-w-md w-full my-8 p-5">
                        <div className="font-heading font-normal text-[22px] text-primary mb-1">Add a bank</div>
                        <p className="text-[12px] text-fg-tertiary mb-3">A fresh, empty profile. The law corpus is shared — the new bank fills its own profile through the one queue.</p>
                        <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') createBank(); }}
                            placeholder="Bank legal name"
                            className="w-full text-[13px] h-9 px-3 border border-border-strong rounded-md bg-surface text-fg" />
                        {err && <div className="text-warning text-[12px] mt-2">{err}</div>}
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => { setAdding(false); setErr(''); }}
                                className="h-9 px-4 rounded-md text-[13px] font-semibold text-fg-secondary hover:bg-surface-sunken">Cancel</button>
                            <button onClick={createBank} disabled={busy || !newName.trim()}
                                className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                {busy ? 'Creating…' : 'Create bank'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------- root: the section tab bar + page stack ---------- */

export default function InventoryPanel() {
    /* tab row per the panel decision (Kawi, 16 Jul): Actions first —
       everything needing a named person; Clusters = the law side;
       Register = the bank's entries. Coverage folded INTO Clusters
       (Kawi, 21 Jul merge mock: "ok build") — the landing IS the score. */
    /* Map returns as a tab (Kawi, 30 Jul: "combine it in the app") — this
       reopens the 21 Jul fold-into-Clusters decision for the NEW estate-wide
       graph only; the old Coverage surface stays dead */
    const [tab, setTab] = useState<'actions' | 'inventory' | 'artifacts' | 'map'>('actions');
    const [cluster, setCluster] = useState<ClusterRow | null>(null);
    const [obligation, setObligation] = useState<string | null>(null);
    const [artifactId, setArtifactId] = useState<string | null>(null);
    /* "Supports this cluster" lands ON the support shelf (Kawi, 21 Jul:
       "when i click it just takes me to the register") */
    const [regFocusSupport, setRegFocusSupport] = useState(false);
    const [addCluster, setAddCluster] = useState(false);
    const [addObligation, setAddObligation] = useState(false);
    const [addDocument, setAddDocument] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    /* item 14 (6 Aug): a conflict door carries WHICH fact — the profile
       page opens with the side-by-side card for it */
    const [profileFocus, setProfileFocus] = useState<ProfileFocus | null>(null);
    const [recordsFor, setRecordsFor] = useState<ClusterRow | null>(null);
    const [q, setQ] = useState('');
    const { userData, authToken } = useAuth();
    const signer = userData?.name || userData?.email || 'unknown user';
    useEffect(() => { setInventoryToken(authToken); }, [authToken]);
    /* the tenant button shows the institution's real name once approved
       (BAR-124, 18 Jul: it was hardcoded 'Demo Bank AB'). Reads the node
       identity, refetched whenever the profile is opened/closed */
    const [tenantName, setTenantName] = useState<string>('');
    /* switching banks refreshes IN PLACE (Kawi, 20 Jul: the reload flashed
       the login page) — the epoch remounts every data-holding page */
    const [bankEpoch, setBankEpoch] = useState(0);
    useEffect(() => {
        get<{ identity?: Record<string, string> }>('/api/inventory/profile')
            .then(d => setTenantName(d.identity?.legal_name || ''))
            .catch(() => setTenantName(''));
    }, [profileOpen, bankEpoch]);
    /* who am I — role + the server-side standing (Kawi, 20 Jul). A stored
       server choice beats the browser memory: your standing follows you. */
    const [me, setMe] = useState<Me>({ uid: 'anonymous', is_signer: true });
    /* the Actions tab wears the live count of waiting verdicts — and keeps
       itself fresh (Kawi, 20 Jul: "i dont have notifications"): rechecked on
       window focus and once a minute, so new work shows without a reload */
    const [verdictsN, setVerdictsN] = useState(0);
    /* a running search counts on the badge too — it is standing work */
    const globalRuns = useRunningSearches();
    useEffect(() => {
        const check = () => get<{ total?: number }>('/api/inventory/actions/verdicts')
            .then(d => setVerdictsN(d.total || 0)).catch(() => undefined);
        check();
        const t = setInterval(check, 60_000);
        window.addEventListener('focus', check);
        _setBadgePoke(check);   // an in-app act refreshes the badge at once
        return () => {
            clearInterval(t); window.removeEventListener('focus', check);
            _setBadgePoke(null);
        };
    }, [bankEpoch]);
    /* the engine's words arrive once; one tick repaints with them */
    const [, setVocabTick] = useState(0);
    useEffect(() => {
        hydrateVocabulary().then(changed => { if (changed) setVocabTick(x => x + 1); });
    }, []);
    useEffect(() => {
        fetchMe().then(m => {
            if (!m) return;
            setMe(m);
            if (m.chosen && m.chosen !== activeInstitution()) {
                setInventoryInstitution(m.chosen);
                setBankEpoch(e => e + 1);   // adopt the server choice in place
            }
        });
        /* self-heal a ghost bank (5 Aug: a browser kept working AS a bank
           that no longer exists after a DB rebuild — every per-bank read
           came back empty, silently). If the stored choice is not in the
           bank list, drop it and remount on the default. Only a SUCCESSFUL
           list read may clear — a fetch error never drops a choice. */
        const stored = activeInstitution();
        if (stored) {
            get<{ institutions: { id: string }[] }>('/api/inventory/institutions')
                .then(d => {
                    if (!(d.institutions || []).some(b => b.id === stored)) {
                        setInventoryInstitution(null);
                        setBankEpoch(e => e + 1);
                    }
                })
                .catch(() => undefined);
        }
    }, []);

    const home = () => { setObligation(null); setArtifactId(null); setAddCluster(false); setAddObligation(false); setAddDocument(false); setProfileOpen(false); setHelpOpen(false); setRecordsFor(null); setCluster(null); setRegFocusSupport(false); };

    // search results navigate by id — the cluster row is fetched here so a click
    // always lands, even before the results page finished its own cluster fetch
    const openByIds = (clusterId: string, oblId: string | null) => {
        setQ(''); home();
        // cluster and obligation pages live on the Clusters tab — from any
        // other tab (register entry links, search) the tab branch would win
        // and silently show the filtered register instead (caught 4 Aug)
        setTab('inventory');
        get<{ clusters: ClusterRow[] }>('/api/inventory/clusters').then(d => {
            const c = d.clusters.find(x => x.id === clusterId);
            if (c) { setCluster(c); if (oblId) setObligation(oblId); }
        }).catch(() => undefined);
    };

    let page: React.ReactNode;
    if (helpOpen) {
        page = <InventoryHelp onBack={() => setHelpOpen(false)} />;
    } else if (q.trim()) {
        page = <SearchPage q={q}
            onCluster={id => openByIds(id, null)}
            onObligation={(clusterId, oblId) => openByIds(clusterId, oblId)}
            onArtifact={id => { setQ(''); setArtifactId(id); }} />;
    } else if (profileOpen) {
        /* the tenant button lives on every tab — the profile overrides
           whatever tab is open (real bug caught by the discovery test:
           on Actions the tab branch used to win and the click died) */
        page = <ProfilePage onBack={home} signer={signer}
            focus={profileFocus} onFocusDone={() => setProfileFocus(null)} />;
    } else if (tab === 'actions') {
        page = <ActionsPage signer={signer}
            onEntry={id => { setTab('inventory'); setArtifactId(id); }}
            onCluster={c => { setTab('inventory'); setCluster(c); }}
            onObligation={(cid, oid) => { setTab('inventory'); openByIds(cid, oid); }}
            onProfile={(focus?: ProfileFocus) => { setTab('inventory'); home();
                /* plain onClick passes the click EVENT here — only a real
                   payload (has dp_id) may focus the card */
                setProfileFocus(focus && typeof (focus as any).dp_id === 'string' ? focus : null);
                setProfileOpen(true); }} />;
    } else if (addDocument && !artifactId) {
        /* drop-first, as a PAGE (Kawi, 6 Aug: the modal got ugly — big
           acts get room); after signing, onDone lands on the new
           document's own page where BARK's read continues */
        page = <AddOwnPage signer={signer} onBack={() => setAddDocument(false)}
            onDone={id => { setAddDocument(false); if (id) setArtifactId(id); }} />;
    } else if (tab === 'artifacts' && !artifactId) {
        /* the register as a first-class tab (Kawi, 15 Jul: hard to find
           behind a tile) — the document-first front door. The register
           cards land HERE too (Kawi, 17 Jul: the page jumped but the
           marker didn't move — there is only this one road now); with a
           cluster still open the register arrives filtered to it */
        page = <ArtifactsRegisterPage signer={signer} onBack={() => { home(); setTab('inventory'); }} onOpen={setArtifactId}
            onAddDocument={() => setAddDocument(true)}
            clusterFilter={cluster} onClusterBack={() => setTab('inventory')}
            focusSupport={regFocusSupport} />;
    } else if (artifactId) {
        page = <ArtifactPage id={artifactId} signer={signer} onBack={() => setArtifactId(null)}
            onOpenObligation={openByIds} onOpenCluster={cid => openByIds(cid, null)}
            onOpenArtifact={setArtifactId}
            onProfile={(focus?: ProfileFocus) => { setTab('inventory'); home();
                /* plain onClick passes the click EVENT here — only a real
                   payload (has dp_id) may focus the card */
                setProfileFocus(focus && typeof (focus as any).dp_id === 'string' ? focus : null);
                setProfileOpen(true); }} />;
    } else if (tab === 'map') {
        /* register-first openByIds lands on the Clusters tab itself —
           no tab-setting here (Kawi's 4 Aug wiring) */
        page = (
            <React.Suspense fallback={<Loading />}>
                <MapGraphView authHeaders={authHeaders}
                    onOpenCluster={id => openByIds(id, null)}
                    onOpenObligation={(cid, oid) => openByIds(cid, oid)}
                    onOpenArtifact={setArtifactId} />
            </React.Suspense>
        );
    } else if (addCluster) {
        page = <AddClusterPage onBack={home} />;
    } else if (addObligation && cluster) {
        page = <AddObligationPage cluster={cluster} onBack={() => setAddObligation(false)} />;
    } else if (obligation && cluster) {
        page = <ObligationPage id={obligation} signer={signer} onArtifact={setArtifactId}
            crumb={{ cluster, onCluster: () => setObligation(null), onList: home }}
            onActions={() => { setTab('actions'); home(); }} />;
    } else if (recordsFor) {
        page = <RecordsPage cluster={recordsFor} onBack={() => setRecordsFor(null)} />;
    } else if (cluster) {
        page = <ClusterPage cluster={cluster} onBack={home} onOpen={setObligation}
            onRecords={() => setRecordsFor(cluster)} signer={signer}
            onArtifacts={support => { setRegFocusSupport(!!support); setTab('artifacts'); }} onAddObligation={() => setAddObligation(true)}
            onAddDocument={() => setAddDocument(true)} onOpenArtifact={setArtifactId} />;
    } else {
        page = <ClustersList onOpen={setCluster} bank={tenantName} signer={signer}
            onAddCluster={() => setAddCluster(true)} onArtifact={setArtifactId} />;
    }

    return (
        <MeContext.Provider value={me}>
        <div>
            {/* the section tab bar — the mock's top nav, scoped to what exists in the app */}
            <div className="flex items-center gap-0.5 px-6 py-2.5 bg-surface border-b border-border-default sticky top-0 z-10">
                {([['actions', (verdictsN + globalRuns.length) > 0 ? `Actions · ${verdictsN + globalRuns.length}` : 'Actions'],
                   ['inventory', 'Clusters'], ['artifacts', 'Register'], ['map', 'Map']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => { setTab(k); home(); }}
                        aria-current={tab === k ? 'page' : undefined}
                        className={`text-[13px] font-semibold px-3.5 py-[7px] rounded-md ${
                            tab === k ? 'bg-primary-tint text-primary' : 'text-fg-tertiary hover:bg-surface-sunken'}`}>
                        {label}
                    </button>
                ))}
                {['Regulation', 'Limits'].map(label => (
                    <span key={label} title="Not in the app yet"
                        className="text-[13px] font-semibold px-3.5 py-[7px] text-border-strong cursor-not-allowed">
                        {label}
                    </span>
                ))}
                {/* the help door (Kawi, 6 Aug: "a page where everything is
                    explained") — quiet, present on every tab */}
                <button onClick={() => { home(); setHelpOpen(true); }}
                    className="text-[13px] font-semibold px-3.5 py-[7px] rounded-md text-fg-tertiary hover:bg-surface-sunken">
                    How it works</button>
                {/* the running-search pulse — visible on EVERY tab (21 Jul) */}
                <span className="ml-auto"><SearchPulse /></span>
                {/* the one search — top of the section, present on every inventory page */}
                <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Search the inventory…"
                    className="ml-3 text-[13px] h-8 px-3 w-72 border border-border-strong rounded-md bg-surface text-fg" />
                {/* the bank switcher — shows the active bank, switches/adds
                    banks; its profile menu item retired 21 Jul (the doors are
                    /institution and the Actions rows) */}
                <BankSwitcher activeName={tenantName} signer={signer}
                    onSwitch={() => { setQ(''); home(); setTenantName(''); setBankEpoch(e => e + 1); }} />
            </div>
            <div key={bankEpoch}>{page}</div>
        </div>
        </MeContext.Provider>
    );
}

const Wrap = ({ children }: { children: React.ReactNode }) => (
    <div className="max-w-[1280px] mx-auto px-7 pt-[30px] pb-[60px] font-body text-fg">{children}</div>
);

/* ---------- global search results — one page, chip-grouped by what you hold ---------- */

function SearchPage({ q, onCluster, onObligation, onArtifact }: {
    q: string;
    onCluster: (clusterId: string) => void;
    onObligation: (clusterId: string, oblId: string) => void;
    onArtifact: (id: string) => void;
}) {
    const [hits, setHits] = useState<SearchResults | null>(null);
    useEffect(() => {
        const t = setTimeout(() => {
            get<SearchResults>(`/api/inventory/search?q=${encodeURIComponent(q.trim())}`)
                .then(setHits).catch(() => setHits({ clusters: [], obligations: [], artifacts: [] }));
        }, 200);
        return () => clearTimeout(t);
    }, [q]);
    if (!hits) return <Loading />;

    const total = hits.clusters.length + hits.obligations.length + hits.artifacts.length;
    const rowCls = 'w-full flex items-center gap-4 px-3.5 py-[11px] text-[13px] bg-surface border border-border-default border-b-0 text-left hover:bg-surface-sunken';
    return (
        <Wrap>
            <PageTitle>Search</PageTitle>
            <PageSub>{total === 0 ? `Nothing in the inventory matches “${q.trim()}”.` : `${total} matches for “${q.trim()}” across the inventory.`}</PageSub>
            <div>
                {hits.clusters.map(c => (
                    <button key={c.id} className={rowCls} onClick={() => onCluster(c.id)}>
                        <IdChip kind="cluster">CLUSTER</IdChip>
                        <span className="font-medium text-fg">{c.name}</span>
                        <span className="ml-auto text-[11px] text-fg-tertiary">{c.obligations} obligations · open →</span>
                    </button>
                ))}
                {hits.obligations.map(o => (
                    <button key={o.id} className={rowCls}
                        onClick={() => onObligation(o.cluster_id, o.id)}>
                        <IdChip kind="obligation">{o.id}</IdChip>
                        <span className="font-medium text-fg">{o.title}</span>
                        <span className="ml-auto text-[11px] text-fg-tertiary">{o.cluster_name} · open →</span>
                    </button>
                ))}
                {hits.artifacts.map(a => (
                    <button key={a.id} className={rowCls} onClick={() => onArtifact(a.id)}>
                        <IdChip kind="artifact">{a.id}</IdChip>
                        <span className="font-medium text-fg">{a.name}</span>
                        <span className="ml-auto text-[11px] text-fg-tertiary">{KIND_LABEL[a.kind] || a.kind} · open →</span>
                    </button>
                ))}
                {total > 0 && <div className="border-b border-border-default" />}
            </div>
        </Wrap>
    );
}

/* ---------- Actions — everything that needs a named person ---------- */
/* The panel's name (16 Jul, 4–1): actions become records. Three groups,
   all computed from real state — flags to review, adoption signatures
   waiting, standing open work. No email machinery yet (second step). */
function ActionsPage({ onCluster, onObligation, onProfile, onEntry, signer }: {
    onCluster: (c: ClusterRow) => void;
    onObligation: (clusterId: string, oblId: string) => void;
    onProfile: (focus?: ProfileFocus) => void;
    onEntry?: (id: string) => void;
    signer: string;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [clusters, setClusters] = useState<ClusterRow[] | null>(null);
    const [obls, setObls] = useState<Record<string, OblRow[]> | null>(null);
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [err, setErr] = useState<string>();
    useEffect(() => {
        get<{ clusters: ClusterRow[] }>('/api/inventory/clusters').then(async d => {
            setClusters(d.clusters);
            const detailed = d.clusters.filter(c => c.obligations > 0);
            const all = await Promise.all(detailed.map(c =>
                get<{ obligations: OblRow[] }>(`/api/inventory/clusters/${c.id}/obligations`)
                    .then(r => [c.id, r.obligations] as const)));
            setObls(Object.fromEntries(all));
        }).catch(e => setErr(String(e)));
        get<ProfileData>('/api/inventory/profile').then(setProfile).catch(() => setProfile(null));
    }, []);
    /* the one inbox (Kawi, 20 Jul: "if it waits for a named person, it is
       HERE") — every waiting verdict, with a jump to its spot. Hooks live
       ABOVE the early return — hook order is sacred. */
    type PackCard = { code: string; country_name: string; status_note?: string | null;
        attachments: { obligation_id: string; title: string;
            cluster_id?: string | null; instrument?: string | null;
            activation?: string | null }[] };
    type FactGroup = { fact_id: string; key: string; label: string; value: string;
        source_title?: string | null;
        duties: { id: string; title: string; cluster_id?: string | null }[] };
    type Verdicts = { profile_cards: number; group_waiting: number; changes_waiting: number;
        wiring: { obligation_id: string; title: string; cluster_id?: string | null; waiting: number }[];
        fact_groups?: FactGroup[];
        review_requests?: { id: string; kind: string; ref: string; words: string;
            by: string; at: string | null }[];
        doc_proposals?: { dp_id: string; kind: string; ref: string; ref_label: string;
            artifact_id: string; artifact_name: string; why: string; quote: string;
            stance?: string | null; value?: string | null; profile_value?: string | null;
            created_at: string | null }[];
        pack?: PackCard | null;
        packs?: PackCard[];
        cycles?: { obligation_id: string; title: string; cluster_id?: string | null;
            cycle: string; offset_days?: number | null; why?: string; quote?: string }[];
        cycles_error?: boolean;
        total: number };
    const [verd, setVerd] = useState<Verdicts | null>(null);
    /* the personal watchlist (6 Aug): shown state is computed server-side
       from the signed truth; 'soon' = a date within 30 days */
    type WatchRow = { kind: string; ref_id: string; title: string; cluster_id?: string | null;
        change_status?: string | null; answered?: boolean; next_due?: string | null;
        due_kind?: string | null; soon?: boolean; lifecycle_status?: string; read_note?: string | null };
    const [watchRows, setWatchRows] = useState<WatchRow[]>([]);
    const running = useRunningSearches();
    const [verdReload, setVerdReload] = useState(0);
    /* refetch when a run ends — the spinner row turns into its verdict
       cards WITHOUT leaving the tab */
    useEffect(() => {
        get<Verdicts>('/api/inventory/actions/verdicts').then(setVerd).catch(() => setVerd(null));
        get<{ items: WatchRow[] }>(`/api/inventory/watchlist?by=${encodeURIComponent(signer)}`)
            .then(d => setWatchRows(d.items || [])).catch(() => setWatchRows([]));
    }, [running.length, verdReload, signer]);
    /* the grouped signature (26 Jul, mock panel 2): one fact, its reader
       duties, ONE approve — confirmed in a modal with a visible Cancel */
    const [groupConfirm, setGroupConfirm] = useState<FactGroup | null>(null);
    const [groupBusy, setGroupBusy] = useState(false);
    const [groupErr, setGroupErr] = useState('');
    /* Cancel on a group card collapses it for this visit — nothing recorded */
    const [setAside, setSetAside] = useState<string[]>([]);
    /* "Ruled out — and why" — a page of its own, reached from here */
    const [showOff, setShowOff] = useState(false);
    /* the country pack waiting for its signature (6 Aug: "national layer
       is hidden....not ok" — it waits for a named person, so it is HERE) */
    const [packOpen, setPackOpen] = useState<string | null>(null);
    const [packBusy, setPackBusy] = useState(false);
    const [packErr, setPackErr] = useState('');
    /* the report-cycle verdicts (6 Aug): accept writes the cycle onto the
       law row; reject is durable, with the reason on record */
    const [cycBusy, setCycBusy] = useState<string | null>(null);
    const [cycRej, setCycRej] = useState<string | null>(null);
    const [cycNote, setCycNote] = useState('');
    const [cycErr, setCycErr] = useState('');
    /* ONE signature over the group (6 Aug: "I'm not ok with signing 21
       cards") — the fold lists every reading, unticking leaves that row
       waiting; per-row reject lives inside the fold */
    const [cycOpen, setCycOpen] = useState(false);
    const [cycOff, setCycOff] = useState<string[]>([]);   // unticked ids
    const signCycles = async (ids: string[]) => {
        setCycBusy('group'); setCycErr('');
        const r = await fetch(`${API}/api/inventory/actions/accept-cycles`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ obligation_ids: ids, by: signer }),
        }).catch(() => null);
        setCycBusy(null);
        if (!r || !r.ok) { setCycErr(r ? ((await r.json().catch(() => ({}))).detail || `${r.status}`) : 'network error'); return; }
        setCycOpen(false); setCycOff([]);
        pokeVerdictsBadge();
        setVerdReload(x => x + 1);
    };
    const decideCycle = async (oid: string, verdict: 'accept' | 'reject', note = '') => {
        setCycBusy(oid); setCycErr('');
        const r = await fetch(`${API}/api/inventory/obligations/${oid}/cycle`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ verdict, note, by: signer }),
        }).catch(() => null);
        setCycBusy(null);
        if (!r || !r.ok) { setCycErr(r ? ((await r.json().catch(() => ({}))).detail || `${r.status}`) : 'network error'); return; }
        setCycRej(null); setCycNote('');
        pokeVerdictsBadge();
        setVerdReload(x => x + 1);
    };
    const signPack = async (code: string) => {
        setPackBusy(true); setPackErr('');
        const r = await fetch(`${API}/api/inventory/packs/${code}/sign`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ by: signer }),
        }).catch(() => null);
        setPackBusy(false);
        if (!r || !r.ok) { setPackErr(r ? ((await r.json().catch(() => ({}))).detail || `${r.status}`) : 'network error'); return; }
        setPackOpen(null);
        pokeVerdictsBadge();
        setVerdReload(x => x + 1);
    };
    const approveGroup = async (g: FactGroup) => {
        setGroupBusy(true); setGroupErr('');
        try {
            const r = await fetch(`${API}/api/inventory/actions/approve-group`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ items: [{ kind: 'profile_fact', id: g.fact_id }], by: signer }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            const d = await r.json();
            if (d.failed?.length) throw new Error(d.failed[0].why || 'refused');
            setGroupConfirm(null);
            setVerdReload(x => x + 1);
            pokeVerdictsBadge();
        } catch (e) { setGroupErr(String(e).replace(/^Error:\s*/, '')); }
        setGroupBusy(false);
    };
    /* document-read proposals (shape A, 5 Aug): accepting IS the signed
       link act; dismissing is durable. One busy row at a time. */
    const [dpBusy, setDpBusy] = useState<string | null>(null);
    const [dpErr, setDpErr] = useState<Record<string, string>>({});
    /* the quote folds behind "why" here too (Kawi, 6 Aug: the inbox was
       the last place still showing the full text) */
    const [dpWhy, setDpWhy] = useState<string | null>(null);
    const decideDp = async (id: string, verdict: 'accepted' | 'rejected') => {
        setDpBusy(id); setDpErr(e => ({ ...e, [id]: '' }));
        try {
            const r = await fetch(`${API}/api/inventory/doc-proposals/${id}/decide`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ verdict, by: signer }),
            });
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `${r.status}`);
            setVerdReload(x => x + 1);
            pokeVerdictsBadge();
        } catch (e) { setDpErr(er => ({ ...er, [id]: String(e).replace(/^Error:\s*/, '') })); }
        setDpBusy(null);
    };
    if (showOff) return <OffAndWhyPage onBack={() => setShowOff(false)} onObligation={onObligation} />;
    if (!clusters || !obls) return <Loading error={err} />;

    type Item = { key: string; title: string; sub: string; onOpen: () => void };
    const review: Item[] = [];
    const waiting: Item[] = [];
    const open: Item[] = [];
    clusters.filter(c => c.obligations > 0).forEach(c => {
        const rows = obls[c.id] || [];
        rows.filter(r => r.change_status && !['green', 'none'].includes(r.change_status)).forEach(r =>
            review.push({ key: r.id, title: r.title,
                sub: `${c.name} · answered stopped counting until a named person clears it`,
                onOpen: () => onObligation(c.id, r.id) }));
        /* the bulk-adoption nag died with the ceremony (Kawi, 22 Jul:
           "kill the decide on 34 steps") — standing work exists only in
           clusters the bank has TAKEN; examples never nag */
        const liveRows = rows.filter(r => r.adoption !== 'dismissed');
        const un = liveRows.filter(r => r.confirmed === 0).length;
        if (un > 0 && c.signed) open.push({ key: `${c.id}-open`,
            title: `${c.name} — ${un} of ${liveRows.length} obligations unanswered`,
            sub: 'standing work — one register entry can cover several',
            onOpen: () => onCluster(c) });
    });
    if (profile) {
        const unc = Object.values(profile.facts).filter(v => !v).length;
        if (unc > 0 || !profile.signed) open.push({ key: 'profile',
            title: `Institution profile — ${unc} fact${plural(unc, { one: '', other: 's' })} unconfirmed${profile.signed ? '' : ' · unsigned'}`,
            sub: 'the switchboard behind what applies — fail-closed until signed',
            onOpen: onProfile });
    }
    const total = review.length + waiting.length + open.length;

    const groupBlock = (title: string, desc: string, items: Item[]) => items.length > 0 && (
        <div className="mb-[22px]">
            <div className="px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.06em] uppercase text-primary">
                {title}
                <span className="font-mono text-[11px] font-medium text-fg-tertiary normal-case ml-1">{items.length}</span>
                <span className="text-[11px] font-medium text-fg-tertiary normal-case tracking-normal ml-3">{desc}</span>
            </div>
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
                {items.map(it => (
                    <button key={it.key} onClick={it.onOpen}
                        className="w-full flex justify-between items-center gap-4 px-3.5 py-3 text-[13px] text-left hover:bg-surface-sunken">
                        <span className="min-w-0">
                            <span className="block font-semibold text-fg">{it.title}</span>
                            <span className="block text-[11px] text-fg-tertiary">{it.sub}</span>
                        </span>
                        <span className="text-[11px] font-semibold text-link whitespace-nowrap">Open →</span>
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <Wrap>
            <PageTitle>Actions</PageTitle>
            <PageSub>Everything that needs a named person. Each action, once done, becomes a record.</PageSub>
            {/* the running searches — FIRST in the inbox (Kawi, 21 Jul: "my
                eyes is naturally drawn to the actions row"); each row becomes
                verdict cards here when its run finishes */}
            {running.length > 0 && (
                <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs mb-4">
                    {running.map(label => (
                        <div key={label} className="flex items-center gap-3 px-4 py-3 text-[13px] border-b border-divider last:border-b-0">
                            <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin shrink-0" />
                            <span className="font-semibold text-fg">Searching {label}…</span>
                            <span className="text-[11.5px] text-fg-tertiary">findings land here as verdict cards when it finishes</span>
                        </div>
                    ))}
                </div>
            )}
            {review.length > 0
                ? <NextCard warn kicker="Needs attention"
                    title={`${review.length} obligation${review.length > 1 ? 's' : ''} need${review.length > 1 ? '' : 's'} review`}
                    detail="the law or your profile moved — review and clear, on record"
                    action={{ label: 'Open →', onClick: review[0].onOpen }} />
                : total > 0
                ? <NextCard kicker="Next for you" title={`${total} open action${total > 1 ? 's' : ''}`}
                    detail="ordered by what bites first — signatures before standing work"
                    action={{ label: 'Open →', onClick: (waiting[0] || open[0]).onOpen }} />
                : null}
            {total === 0 && (
                <div className="border border-dashed border-border-strong rounded-lg bg-surface-sunken px-6 py-10 text-center">
                    <b className="block text-[15px] text-secondary-hover">No open actions ✓</b>
                    <span className="block text-[13px] text-fg-tertiary mt-1">Everything is answered, signed and quiet.</span>
                </div>
            )}
            {/* the grouped signature (26 Jul, mock panel 2): the waiting
                profile facts, BY FACT — each with the duties reading it,
                one approve per group; per-item flow stays one link away */}
            {verd && (verd.fact_groups?.length ?? 0) > 0 && (
                <>
                    <LaneLabel right="one signature per fact — every obligation reading it listed">
                        Needs your signature — grouped by fact</LaneLabel>
                    {(verd.fact_groups || []).filter(g => !setAside.includes(g.fact_id)).map(g => (
                        <div key={g.fact_id} className="border-t border-primary mt-2 pt-3.5 mb-5">
                            <p className="font-heading text-[18px] text-fg">{g.label} = {prettyValue(g.key, g.value)}</p>
                            <p className="text-[13px] text-fg-secondary mt-1.5 mb-2.5">
                                {g.duties.length > 0
                                    ? 'If this fact is wrong, the obligations below watch the wrong value. Confirm the fact once:'
                                    : 'No obligation reads this fact yet — the value itself still needs your signature.'}
                            </p>
                            {g.duties.map(d2 => (
                                <button key={d2.id}
                                    onClick={() => d2.cluster_id && onObligation(d2.cluster_id, d2.id)}
                                    className="w-full flex justify-between items-baseline gap-4 py-1.5 border-b border-divider text-left text-[13px] text-fg-secondary hover:bg-surface-sunken">
                                    <span>{d2.title}</span>
                                    <span className="text-[11.5px] text-fg-tertiary whitespace-nowrap">flagged for review if this changes</span>
                                </button>
                            ))}
                            <p className="mt-3.5 flex gap-4 items-center flex-wrap">
                                {isSigner
                                    ? <button onClick={() => { setGroupConfirm(g); setGroupErr(''); }}
                                        className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover">
                                        Approve group — as {signer}</button>
                                    : <WaitingForSigner />}
                                <button onClick={() => setSetAside(a => [...a, g.fact_id])}
                                    className="text-[12.5px] text-fg-tertiary underline hover:text-fg-secondary">Cancel</button>
                                <button onClick={() => onProfile()}
                                    className="text-[12.5px] text-fg-tertiary underline hover:text-fg-secondary">rule rows one by one</button>
                            </p>
                        </div>
                    ))}
                </>
            )}
            {groupConfirm && (
                <DialogShell open onClose={() => { if (!groupBusy) { setGroupConfirm(null); setGroupErr(''); } }}
                    ariaLabel="Approve group" backdropPolicy={groupBusy ? 'static' : 'dismiss'}
                    overlayClassName="flex items-center justify-center p-4"
                    panelClassName="w-full max-w-md border border-border-default overflow-hidden p-5">
                    <p className="text-[15px] font-semibold text-fg">
                        Approve: {groupConfirm.label} = {prettyValue(groupConfirm.key, groupConfirm.value)}</p>
                    <p className="text-[13px] text-fg-secondary mt-1.5">
                        One signature approves this fact for your bank
                        {groupConfirm.duties.length > 0
                            ? <> — {groupConfirm.duties.length} obligation{groupConfirm.duties.length > 1 ? 's' : ''} watch{groupConfirm.duties.length > 1 ? '' : 'es'} it,
                              and a later change flags them for review</>
                            : ''}. The approval is a record in your name.</p>
                    {groupErr && <p className="text-[12.5px] text-danger mt-2">{groupErr}</p>}
                    <p className="mt-4 flex items-center gap-3">
                        <button onClick={() => approveGroup(groupConfirm)} disabled={groupBusy}
                            className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                            {groupBusy ? 'Signing…' : `Approve group — as ${signer}`}</button>
                        <button onClick={() => { setGroupConfirm(null); setGroupErr(''); }} disabled={groupBusy}
                            className="text-[12.5px] text-fg-tertiary hover:underline">Cancel</button>
                    </p>
                </DialogShell>
            )}
            {verd?.cycles_error && (
                <p className="mb-3 px-4 py-2.5 rounded-lg border border-border-default bg-surface-sunken text-[12.5px] text-warning-ink">
                    The cycle cards could not be read just now — they are not gone; reload in a moment.</p>
            )}
            {verd && ((verd.group_waiting + verd.changes_waiting) > 0 || verd.wiring.length > 0
                || (verd.doc_proposals || []).length > 0
                /* review requests OPEN the lane too (audit, 6 Aug: a lone
                   request was counted on the badge but rendered nowhere) */
                || (verd.review_requests || []).length > 0
                || (verd.packs || (verd.pack ? [verd.pack] : [])).length > 0
                || (verd.cycles || []).length > 0
                || (verd.profile_cards > 0 && !(verd.fact_groups && verd.fact_groups.length > 0))) && (
                <>
                    <LaneLabel right="candidates count as nothing until you decide">Your verdicts</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mb-7">
                        {(verd.packs || (verd.pack ? [verd.pack] : [])).map(pk => (
                            <div key={pk.code} className="px-4 py-2.5 text-[13px]">
                                <div className="flex items-baseline gap-2.5">
                                    <span className="font-bold text-accent-copper min-w-[20px] text-center">1</span>
                                    <span className="min-w-0"><b className="font-semibold">the {pk.country_name} pack waits for a signature</b> —
                                        {' '}{pk.attachments.length} national attachment{plural(pk.attachments.length, { one: '', other: 's' })} to your obligations; they count as nothing until a named person signs the pack</span>
                                    <button onClick={() => setPackOpen(o => (o === pk.code ? null : pk.code))}
                                        className="ml-auto text-[12px] font-semibold text-link whitespace-nowrap">
                                        {packOpen === pk.code ? 'close ✕' : 'review and sign →'}</button>
                                </div>
                                {packOpen === pk.code && (
                                    <div className="mt-2 border-t border-divider pt-2">
                                        {pk.attachments.map(a2 => (
                                            <div key={a2.obligation_id} className="flex gap-2.5 items-baseline py-1 text-[12.5px]">
                                                <button onClick={() => a2.cluster_id && onObligation(a2.cluster_id, a2.obligation_id)}
                                                    className="text-left font-medium text-primary hover:underline truncate">{a2.title}</button>
                                                <span className="text-[11px] text-fg-tertiary whitespace-nowrap ml-auto">
                                                    {a2.instrument || a2.activation || ''}</span>
                                            </div>
                                        ))}
                                        <div className="flex gap-3 items-center mt-2">
                                            {isSigner ? (
                                                <button onClick={() => signPack(pk.code)} disabled={packBusy}
                                                    className="h-8 px-3.5 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                                    {packBusy ? 'Signing…' : `Sign the ${pk.country_name} pack — as ${signer}`}</button>
                                            ) : <WaitingForSigner />}
                                            <button onClick={() => setPackOpen(null)}
                                                className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
                                            {packErr && <span className="text-[11px] text-warning-ink">{packErr}</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {(verd.cycles || []).length > 0 && (() => {
                            const cycs = verd.cycles || [];
                            const ticked = cycs.filter(c => !cycOff.includes(c.obligation_id));
                            return (
                                <div className="px-4 py-2.5 text-[13px]">
                                    <div className="flex items-baseline gap-2.5">
                                        <span className="font-bold text-accent-copper min-w-[20px] text-center">{cycs.length}</span>
                                        <span className="min-w-0"><b className="font-semibold">report cycles</b> — BARK read
                                            {' '}{cycs.length} obligation{plural(cycs.length, { one: '', other: 's' })} for your bank; one signature covers what you leave ticked</span>
                                        <button onClick={() => setCycOpen(o => !o)}
                                            className="ml-auto text-[12px] font-semibold text-link whitespace-nowrap">
                                            {cycOpen ? 'close ✕' : 'review and sign →'}</button>
                                    </div>
                                    {cycOpen && (
                                        <div className="mt-2 border-t border-divider pt-1">
                                            {cycs.map(cy => (
                                                <React.Fragment key={cy.obligation_id}>
                                                    <div className="flex gap-2.5 items-baseline py-1.5 text-[12.5px]">
                                                        <input type="checkbox" className="shrink-0 relative top-[1px]"
                                                            checked={!cycOff.includes(cy.obligation_id)}
                                                            onChange={() => setCycOff(s => s.includes(cy.obligation_id)
                                                                ? s.filter(x => x !== cy.obligation_id) : [...s, cy.obligation_id])} />
                                                        <span className="min-w-0">
                                                            <span className="font-medium text-fg">{cy.title}</span>
                                                            {' — '}<b className="font-semibold">{cy.cycle}</b>
                                                            {cy.offset_days ? <> · due {cy.offset_days} days after period end</> : null}
                                                            {(cy.quote || cy.why) && (
                                                                <span className="block text-[11px] text-fg-tertiary">
                                                                    {cy.quote && <>“{cy.quote}”</>}{cy.quote && cy.why && ' — '}{cy.why}</span>
                                                            )}
                                                        </span>
                                                        <button onClick={() => { setCycRej(cycRej === cy.obligation_id ? null : cy.obligation_id); setCycNote(''); }}
                                                            className="ml-auto text-[11px] text-fg-tertiary hover:underline whitespace-nowrap">reject…</button>
                                                    </div>
                                                    {cycRej === cy.obligation_id && (
                                                        <div className="flex gap-2 items-center pb-1.5 pl-6">
                                                            <input value={cycNote} onChange={e => setCycNote(e.target.value)}
                                                                placeholder="reason — required" autoFocus
                                                                className={`text-[11px] border rounded-md px-2.5 h-7 w-64 bg-surface text-fg ${
                                                                    cycNote.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                                            <button disabled={!cycNote.trim() || cycBusy === cy.obligation_id}
                                                                onClick={() => decideCycle(cy.obligation_id, 'reject', cycNote.trim())}
                                                                className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                                                                Reject — as {signer}</button>
                                                            <button onClick={() => { setCycRej(null); setCycNote(''); }}
                                                                className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            <div className="flex gap-3 items-center mt-2 pb-1">
                                                {isSigner ? (
                                                    <button onClick={() => signCycles(ticked.map(c => c.obligation_id))}
                                                        disabled={cycBusy === 'group' || ticked.length === 0}
                                                        className="h-8 px-3.5 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                                        {cycBusy === 'group' ? 'Signing…'
                                                            : `Accept ${ticked.length} reading${plural(ticked.length, { one: '', other: 's' })} — as ${signer}`}</button>
                                                ) : <WaitingForSigner />}
                                                {cycOff.length > 0 && (
                                                    <span className="text-[11px] text-fg-tertiary">{cycOff.length} unticked — stays waiting</span>
                                                )}
                                                <button onClick={() => { setCycOpen(false); setCycOff([]); }}
                                                    className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
                                                {cycErr && <span className="text-[11px] text-warning-ink">{cycErr}</span>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        {verd.profile_cards > 0 && !(verd.fact_groups && verd.fact_groups.length > 0) && (
                            <button onClick={() => onProfile()} className="w-full flex items-baseline gap-2.5 px-4 py-2.5 text-left text-[13px] hover:bg-surface-sunken">
                                <span className="font-bold text-accent-copper min-w-[20px] text-center">{verd.profile_cards}</span>
                                <span><b className="font-semibold">profile fact card{plural(verd.profile_cards, { one: '', other: 's' })}</b> waiting in the queue</span>
                                <span className="ml-auto text-[12px] font-semibold text-link whitespace-nowrap">open the waiting card →</span>
                            </button>
                        )}
                        {(verd.group_waiting + verd.changes_waiting) > 0 && (
                            <button onClick={() => onProfile()} className="w-full flex items-baseline gap-2.5 px-4 py-2.5 text-left text-[13px] hover:bg-surface-sunken">
                                <span className="font-bold text-accent-copper min-w-[20px] text-center">{verd.group_waiting + verd.changes_waiting}</span>
                                <span><b className="font-semibold">group items</b> — entities, relations and structure changes</span>
                                <span className="ml-auto text-[12px] font-semibold text-link whitespace-nowrap">open the group →</span>
                            </button>
                        )}
                        {(verd.review_requests || []).map(rr => (
                            <div key={rr.id}
                                className="w-full flex items-baseline gap-2.5 px-4 py-2.5 text-left text-[13px]">
                                <span className="font-bold text-accent-copper min-w-[20px] text-center">1</span>
                                <span className="min-w-0"><b className="font-semibold">review asked</b> on {rr.ref || rr.kind} — “{rr.words}”
                                    <span className="block text-[11px] text-fg-tertiary">{rr.by} · {(rr.at || '').slice(0, 10)}</span></span>
                                {/* the closing half (audit, 6 Aug: requests
                                    piled here forever) — a named person says
                                    it was looked at; the row goes on record */}
                                {isSigner && (
                                    <button disabled={dpBusy === rr.id}
                                        onClick={async () => {
                                            setDpBusy(rr.id);
                                            const r = await fetch(`${API}/api/inventory/review-requests/${rr.id}/resolve`, {
                                                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                                body: JSON.stringify({ by: signer }),
                                            }).catch(() => null);
                                            setDpBusy(null);
                                            if (r && r.ok) { pokeVerdictsBadge(); setVerdReload(x => x + 1); }
                                        }}
                                        className="ml-auto text-[11px] font-semibold text-fg-tertiary hover:underline whitespace-nowrap disabled:opacity-50">
                                        {dpBusy === rr.id ? 'closing…' : `looked at it — close, as ${signer}`}</button>
                                )}
                            </div>
                        ))}
                        {/* BARK read an uploaded document (shape A, 5 Aug):
                            each finding is a row with its evidence quoted;
                            accept = the signed link act, dismiss = durable.
                            Profile facts change only on the profile page —
                            here they get the door. */}
                        {(verd.doc_proposals || []).map(dp => (
                            <div key={dp.dp_id} className="w-full px-4 py-2.5 text-[13px]">
                                <div className="flex items-baseline gap-2.5">
                                    <span className="font-bold text-accent-copper min-w-[20px] text-center">1</span>
                                    <span className="min-w-0">
                                        {dp.artifact_name && <b className="font-semibold">{dp.artifact_name}</b>}
                                        {dp.kind === 'not_applies'
                                            ? <><b className="font-semibold">{dp.ref_label}</b> — BARK's reading: it does not apply to your bank</>
                                            : dp.kind === 'answer'
                                            ? <> — BARK read it: looks like it also answers <b className="font-semibold">{dp.ref_label}</b></>
                                            : dp.stance === 'fill'
                                            ? <> — BARK read it: it states <b className="font-semibold">{dp.ref_label}</b>: <b className="font-semibold">{dp.value}</b>; the profile has nothing here yet</>
                                            : dp.stance === 'conflict'
                                            ? <> — BARK read it: it says <b className="font-semibold">{dp.ref_label}</b> is <b className="font-semibold">{dp.value}</b>; the profile says <b className="font-semibold">{dp.profile_value}</b></>
                                            : dp.kind === 'comes_from'
                                            ? <> — BARK read it: it is governed by <b className="font-semibold">{dp.ref_label}</b></>
                                            : <> — BARK read it: it states something about <b className="font-semibold">{dp.ref_label}</b></>}
                                        {dpWhy === dp.dp_id && dp.quote && (
                                            <span className="block text-[12px] text-fg-secondary mt-0.5">“{dp.quote}”</span>
                                        )}
                                    </span>
                                    {/* links live at the right edge in rows
                                        (Kawi, 6 Aug) — prose keeps inline links */}
                                    {dp.quote && (
                                        <button onClick={() => setDpWhy(w => w === dp.dp_id ? null : dp.dp_id)}
                                            className="text-[11px] font-semibold text-link hover:underline ml-auto whitespace-nowrap self-start">
                                            {dpWhy === dp.dp_id ? 'hide' : 'why →'}</button>
                                    )}
                                </div>
                                <div className="flex gap-3.5 items-center mt-1.5 pl-[30px]">
                                    {dp.kind === 'not_applies' ? (
                                        <button onClick={() => decideDp(dp.dp_id, 'accepted')} disabled={dpBusy === dp.dp_id}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                            {dpBusy === dp.dp_id ? 'Signing…' : `Remove from your list — as ${signer}`}</button>
                                    ) : dp.kind === 'comes_from' ? (
                                        <button onClick={() => decideDp(dp.dp_id, 'accepted')} disabled={dpBusy === dp.dp_id}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                            {dpBusy === dp.dp_id ? 'Signing…' : `Add the connection — as ${signer}`}</button>
                                    ) : dp.kind === 'answer' ? (
                                        <button onClick={() => decideDp(dp.dp_id, 'accepted')} disabled={dpBusy === dp.dp_id}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                            {dpBusy === dp.dp_id ? 'Signing…' : `Add the link — as ${signer}`}</button>
                                    ) : dp.stance === 'fill' ? (
                                        <button onClick={() => decideDp(dp.dp_id, 'accepted')} disabled={dpBusy === dp.dp_id}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                            {dpBusy === dp.dp_id ? 'Signing…' : `Use this — as ${signer}`}</button>
                                    ) : (
                                        <button onClick={() => onProfile({ dp_id: dp.dp_id, key: dp.ref,
                                            label: dp.ref_label, value: dp.value || '', quote: dp.quote || '',
                                            artifact_name: dp.artifact_name })}
                                            className="text-[12px] font-semibold text-link hover:underline">Open the profile →</button>
                                    )}
                                    <button onClick={() => decideDp(dp.dp_id, 'rejected')} disabled={dpBusy === dp.dp_id}
                                        className="text-[11px] font-semibold text-fg-tertiary hover:underline">
                                        {dp.kind === 'not_applies' ? 'keep it' : 'dismiss'}</button>
                                    {dpErr[dp.dp_id] && <span className="text-[11px] text-warning-ink">{dpErr[dp.dp_id]}</span>}
                                </div>
                            </div>
                        ))}
                        {verd.wiring.map(w2 => (
                            <button key={w2.obligation_id}
                                onClick={() => w2.cluster_id && onObligation(w2.cluster_id, w2.obligation_id)}
                                className="w-full flex items-baseline gap-2.5 px-4 py-2.5 text-left text-[13px] hover:bg-surface-sunken">
                                <span className="font-bold text-accent-copper min-w-[20px] text-center">{w2.waiting}</span>
                                <span><b className="font-semibold">wiring proposal{plural(w2.waiting, { one: '', other: 's' })}</b> on {w2.title}</span>
                                <span className="ml-auto text-[12px] font-semibold text-link whitespace-nowrap">open the obligation →</span>
                            </button>
                        ))}
                    </div>
                </>
            )}
            {groupBlock('Needs review', 'flags from monitoring and profile changes', review)}
            {groupBlock('Waiting for a signature', 'suggestions only count after a named person signs', waiting)}
            {groupBlock('Open', 'standing work, no deadline attached', open)}
            {watchRows.length > 0 && (
                <>
                    <LaneLabel right="your personal pins — computed live, nothing stored">Watchlist</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mb-7">
                        {watchRows.map(w => (
                            <div key={`${w.kind}:${w.ref_id}`} className="flex gap-2.5 items-baseline px-4 py-2.5 text-[13px]">
                                <button onClick={() => w.kind === 'obligation' && w.cluster_id
                                        ? onObligation(w.cluster_id, w.ref_id) : onEntry?.(w.ref_id)}
                                    className="font-medium text-primary hover:underline text-left truncate">{w.title}</button>
                                <span className="text-[11.5px] text-fg-tertiary min-w-0 truncate">
                                    {w.kind === 'obligation'
                                        ? <>{w.answered ? 'answered' : 'open'}
                                            {w.change_status && !['green', 'none'].includes(w.change_status) && ' · review needed'}</>
                                        : <>{w.lifecycle_status === 'retired' ? 'retired' : 'in the register'}</>}
                                </span>
                                {w.next_due && (
                                    <span className={`ml-auto text-[11.5px] whitespace-nowrap ${
                                        w.soon ? 'font-semibold text-warning-ink' : 'text-fg-tertiary'}`}>
                                        {fmtDay(w.next_due)}{w.soon && ' — within 30 days'}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
            <div className="mt-[34px] pt-4 border-t border-border-default flex gap-[26px] items-center text-[11px] text-fg-tertiary">
                <span>Reminders live on your watchlist — watch any obligation or entry from its page; email comes later.</span>
                {/* the mirror of every signature above: what stands OFF, and why */}
                <button onClick={() => setShowOff(true)}
                    className="ml-auto text-[11px] font-semibold text-link hover:underline whitespace-nowrap">
                    Ruled out — and why →</button>
            </div>
        </Wrap>
    );
}

/* ---------- "Ruled out — and why" (named by Kawi, 5 Aug) ---------- */
/* Every duty currently switched off for the active bank, grouped by cause,
   each with its reason, the named person and the record. Read-only — the
   acts (restore, re-bind) live on the duty pages. */
type OffRow = {
    obligation_id: string; title: string; cluster_id: string | null;
    why: string | null; decided_by: string | null; decided_at: string | null;
    record_id: string | null; cause: string;
};
function OffAndWhyPage({ onBack, onObligation }: {
    onBack: () => void; onObligation: (clusterId: string, oblId: string) => void;
}) {
    const [d, setD] = useState<{ dismissed: OffRow[]; scoped: OffRow[];
        profile_fact: OffRow[]; total: number } | null>(null);
    const [err, setErr] = useState<string>();
    useEffect(() => {
        get<{ dismissed: OffRow[]; scoped: OffRow[]; profile_fact: OffRow[]; total: number }>(
            '/api/inventory/off-and-why').then(setD).catch(e => setErr(String(e)));
    }, []);
    if (!d) return <Loading error={err} />;
    const section = (title: string, desc: string, rows: OffRow[]) => rows.length > 0 && (
        <div className="mb-[22px]">
            <div className="px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.06em] uppercase text-primary">
                {title}
                <span className="font-mono text-[11px] font-medium text-fg-tertiary normal-case ml-1">{rows.length}</span>
                <span className="text-[11px] font-medium text-fg-tertiary normal-case tracking-normal ml-3">{desc}</span>
            </div>
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
                {rows.map(r => (
                    <button key={`${r.obligation_id}-${r.record_id || ''}`}
                        onClick={() => r.cluster_id && onObligation(r.cluster_id, r.obligation_id)}
                        className="w-full flex justify-between items-center gap-4 px-3.5 py-3 text-[13px] text-left hover:bg-surface-sunken">
                        <span className="min-w-0">
                            <span className="block font-semibold text-fg">{r.title}</span>
                            <span className="block text-[12px] text-fg-secondary">{r.why || '—'}</span>
                            <span className="block text-[11px] text-fg-tertiary">
                                {r.decided_by || 'unnamed'} · {(r.decided_at || '').slice(0, 10) || '—'}
                                {r.record_id ? ` · ${r.record_id}` : ''}</span>
                        </span>
                        <span className="text-[11px] font-semibold text-link whitespace-nowrap">Open →</span>
                    </button>
                ))}
            </div>
        </div>
    );
    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <span className="text-fg font-semibold">Actions</span>
            </div>
            <PageTitle>Ruled out — and why</PageTitle>
            <PageSub>
                Every obligation currently ruled out for your bank — by a person, a fact
                or a scope decision — each with its written reason, the named person and
                the record. Ruling out always takes a signature; watching a fact never
                rules anything out.
            </PageSub>
            {d.total === 0 && (
                <div className="border border-dashed border-border-strong rounded-lg bg-surface-sunken px-6 py-10 text-center">
                    <b className="block text-[15px] text-secondary-hover">Nothing is ruled out ✓</b>
                    <span className="block text-[13px] text-fg-tertiary mt-1">
                        Every suggested obligation stands in your register.</span>
                </div>
            )}
            {section('Dismissed by your bank', 'a written reason and a signature — reversible on the obligation page', d.dismissed)}
            {section('Scoped to a group entity', 'bound to named entities — off at the whole-bank default', d.scoped)}
            {section('Switched off by a profile fact', 'no fact can switch an obligation off yet — watching only flags', d.profile_fact)}
        </Wrap>
    );
}

/* ---------- page 1: landing — the clusters, scored ---------- */

type MapData = {
    nodes: { id: string; name: string; kind: string; data_origin: string | null;
             obligation_type?: string | null; instruments?: string[] | null }[];
    edges: { source: string; target: string; kind: string; origin: string | null }[];
};

/* The landing on the ACCEPT axis (Kawi, 22 Jul, mock blessed "ok
   build"): the split that means something is SIGNED vs NOT — never
   BARK's internal mapped/unmapped state ("i dont want irrbb and LE
   separated from the other 38, none of them are signed or accepted").
   Two places: "Your clusters" = signed rows, each with its score bar;
   "Examples" = the full menu, ONE unsegmented list. Signing a cluster
   (the sign act on its page) moves it up. The Coverage tab, tiles,
   filters, fold and the "coarse watch"/"scaffold" words are all dead. */
function ClustersList({ onOpen, onAddCluster, onArtifact, bank, signer }: {
    onOpen: (c: ClusterRow) => void; onAddCluster: () => void;
    onArtifact: (id: string) => void; bank: string; signer: string;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [rows, setRows] = useState<ClusterRow[] | null>(null);
    const [mapd, setMapd] = useState<MapData | null>(null);
    const [err, setErr] = useState<string>();
    const [reload, setReload] = useState(0);
    const [arts, setArts] = useState<ArtifactRow[] | null>(null);
    /* the examples modal (blessed variant A, examples-modal-vs-dropdown):
       the landing stays pure — examples appear on demand, visible Close */
    const [exOpen, setExOpen] = useState(false);
    const [adoptFor, setAdoptFor] = useState<string | null>(null);
    const [adoptBusy, setAdoptBusy] = useState(false);
    const [adoptErr, setAdoptErr] = useState('');
    const adoptAct = async (clusterId: string) => {
        setAdoptBusy(true); setAdoptErr('');
        try {
            const r = await fetch(`${API}/api/inventory/clusters/${clusterId}/accept`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ by: signer }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            setAdoptFor(null);
            setReload(x => x + 1);   // the adopted cluster moves up to "Your clusters"
        } catch (e) { setAdoptErr(String(e).replace(/^Error:\s*/, '')); }
        setAdoptBusy(false);
    };

    useEffect(() => {
        get<{ clusters: ClusterRow[] }>('/api/inventory/clusters')
            .then(d => setRows(d.clusters)).catch(e => setErr(String(e)));
        // the reliance clause only — who carries the most counting answers
        get<MapData>('/api/inventory/map').then(setMapd).catch(() => setMapd(null));
        // register-first verdict (Kawi, 4 Aug): the entry count leads the line
        get<{ artifacts: ArtifactRow[] }>('/api/inventory/artifacts')
            .then(d => setArts(d.artifacts)).catch(() => setArts([]));
    }, [reload]);
    if (!rows) return <Loading error={err} />;

    /* the score counts only what the bank has SIGNED — examples never
       count (same rule as the register) */
    const yours = rows.filter(r => r.signed);
    const yoursCount = (arts || []).filter(a => a.data_origin === 'real').length;
    const duties = yours.reduce((n, c) => n + c.obligations, 0);
    const answered = yours.reduce((n, c) => n + c.answered, 0);
    const flagged = yours.reduce((n, c) => n + c.flagged, 0);
    /* one bar for green everywhere (panel, 17 Jul): reliance counts only
       claims that are human-confirmed AND on a real entry */
    const relCount = new Map<string, number>();
    const realIds = new Set((mapd?.nodes || [])
        .filter(n => n.data_origin === 'real').map(n => n.id));
    (mapd?.edges || []).forEach(e => {
        if (e.kind === 'SATISFIES' && e.origin === 'human_confirmed' && realIds.has(e.source))
            relCount.set(e.source, (relCount.get(e.source) || 0) + 1);
    });
    const topRel = Array.from(relCount.entries()).sort((a, b) => b[1] - a[1])[0];
    const topRelNode = topRel && (mapd?.nodes || []).find(n => n.id === topRel[0]);
    /* your clusters: flagged first, widest gap next; examples: mapped
       first (they have a page to open), then A–Z — order, not segments */
    const yoursSorted = [...yours].sort((a, b) =>
        ((b.flagged > 0 ? 1 : 0) - (a.flagged > 0 ? 1 : 0))
        || ((b.obligations - b.answered) - (a.obligations - a.answered))
        || a.name.localeCompare(b.name));
    const examples = rows.filter(r => !r.signed).sort((a, b) =>
        ((b.obligations > 0 ? 1 : 0) - (a.obligations > 0 ? 1 : 0))
        || a.name.localeCompare(b.name));

    return (
        <Wrap>
            <PageTitle>Clusters</PageTitle>
            <PageSub>
                Everything the bank could owe a regulator. A cluster is a family of obligations
                from one legal framework — a fact of the law, not a taxonomy we invented.
            </PageSub>

            {/* the verdict sentence — the page leads with what you walk away knowing */}
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs px-4 py-3 mb-4 text-[15px]">
                {yours.length === 0
                    ? <>
                        <b>{bank ? `${bank}: ` : ''}nothing signed yet.</b>
                        <span className="block text-[12.5px] text-fg-tertiary mt-0.5">
                            {rows.length} example clusters wait under Browse — adopt one there, or open it first.
                        </span>
                    </>
                    : <>
                        <b>{bank ? `${bank}: ` : ''}{yoursCount} register {plural(yoursCount, { one: 'entry answers', other: 'entries answer' })} {answered} of {duties} obligations.</b>{' '}
                        {flagged > 0
                            ? <span className="text-warning-ink font-semibold">{flagged} flagged — drawn amber below.</span>
                            : 'Nothing flagged.'}
                        <span className="block text-[12.5px] text-fg-tertiary mt-0.5">
                            {yours.length} cluster{plural(yours.length, { one: '', other: 's' })} signed ·{' '}
                            {topRelNode
                                ? <>most relied on:{' '}
                                    <button onClick={() => onArtifact(topRelNode.id)}
                                        className="text-link font-semibold hover:underline">
                                        {topRelNode.name || topRelNode.id}</button> — answers {topRel[1]}</>
                                : 'nothing carries reliance yet'}
                        </span>
                    </>}
            </div>

            <LaneLabel>Your clusters <span className="normal-case tracking-normal font-medium">— what your bank signed. Each row is its own score.</span></LaneLabel>
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs mb-5">
                {yours.length === 0 && (
                    <p className="px-4 py-3.5 text-[13px] text-fg-tertiary">
                        Nothing here yet. When you adopt a cluster from the examples (Browse, below),
                        it moves up here and its score bar starts.</p>
                )}
                {yoursSorted.map(c => {
                    const open = c.obligations - c.answered;
                    const pct = c.obligations > 0 ? Math.round((c.answered / c.obligations) * 100) : 0;
                    return (
                        <button key={c.id} onClick={() => onOpen(c)}
                            className="w-full grid grid-cols-[minmax(140px,200px)_1fr_minmax(150px,190px)_60px] gap-3.5 items-center px-4 py-3 text-left border-b border-divider last:border-b-0 hover:bg-surface-sunken">
                            {/* the description stays when a cluster moves up (Kawi, 22 Jul) */}
                            <span className="min-w-0">
                                <span className="text-[13.5px] font-bold text-fg block truncate">{c.name}</span>
                                {CLUSTER_DESC[c.id] && (
                                    <span className="block text-[11px] font-normal text-fg-tertiary truncate">{CLUSTER_DESC[c.id]}</span>
                                )}
                            </span>
                            <span className="flex h-3.5 rounded border border-divider overflow-hidden bg-surface-sunken">
                                {/* real palette tokens only — alpha-modifier classes
                                    (bg-x/40) do not exist in this config and paint
                                    NOTHING (Kawi's screenshot, 21 Jul). Open = GREY,
                                    not black (Kawi, 21 Jul): open is normal work,
                                    color only speaks for done (green) and flags (amber) */}
                                {pct > 0 && <span className="bg-success opacity-40" style={{ width: `${pct}%` }} />}
                                {open > 0 && <span className="bg-fg-tertiary opacity-60" style={{ width: `${100 - pct}%` }} />}
                            </span>
                            <span className="text-right text-[12.5px] text-fg-secondary">
                                <b className="text-fg">{open} open</b> · {c.answered} answered
                                {c.flagged > 0 && <span className="text-warning-ink font-semibold"> · {c.flagged} flagged</span>}
                            </span>
                            <span className="text-right text-[12px] font-semibold text-secondary-hover">open →</span>
                        </button>
                    );
                })}
            </div>

            {/* the examples row v2 (blessed examples-row-v2.html): ONE quiet
                clickable row — a door, not a paragraph. The whole row opens
                the Browse modal; "Add your own cluster" lives INSIDE the
                modal as the last row, where you already see what exists. */}
            <button onClick={() => { setExOpen(true); setAdoptFor(null); setAdoptErr(''); }}
                className="w-full flex items-baseline gap-3 mt-[26px] px-[18px] py-3.5 border border-border-default rounded-lg bg-surface text-left hover:border-border-strong">
                <span className="font-heading text-[16px] text-fg">Examples</span>
                <span className="text-[12.5px] text-fg-tertiary">
                    {examples.length} ready-made cluster{plural(examples.length, { one: '', other: 's' })}</span>
                <span className="ml-auto text-[13px] font-semibold text-secondary-hover whitespace-nowrap">Browse →</span>
            </button>
            {exOpen && (
                <DialogShell open onClose={() => { if (!adoptBusy) setExOpen(false); }}
                    ariaLabel="Example clusters" backdropPolicy="dismiss"
                    overlayClassName="flex items-center justify-center p-4"
                    panelClassName="w-full max-w-2xl max-h-[70vh] overflow-auto border border-border-default p-6">
                    <button onClick={() => { if (!adoptBusy) setExOpen(false); }}
                        className="float-right text-[12.5px] text-fg-tertiary underline hover:text-fg-secondary">Close ✕</button>
                    <p className="font-heading text-[18px] text-fg mb-1">Example clusters</p>
                    <p className="text-[12px] text-fg-tertiary mb-2.5">
                        Adopting signs a cluster into your register — its obligations become standing work,
                        and BARK reads them in the background. Opening first to review is always possible.</p>
                    {adoptErr && <p className="text-[12.5px] text-danger mb-2">{adoptErr}</p>}
                    {/* the family grouping survives the modal (mock): headers
                        per family with counts; unknown ids fall to 'other' */}
                    {FAMILIES.map(f => {
                        const items = examples.filter(c => (FAMILY_OF[c.id] || 'other') === f.key);
                        if (!items.length) return null;
                        return (
                            <div key={f.key} className="mb-3.5">
                                <p className="font-heading text-[14px] text-fg mt-3.5 mb-1">
                                    {f.title} <span className="font-mono text-[11px] text-fg-tertiary ml-1.5">{items.length}</span>
                                </p>
                                {items.map(c => (
                                    <div key={c.id} className="flex justify-between items-baseline gap-4 py-1.5 border-b border-divider text-[12.5px] text-fg-secondary">
                                        <span className="min-w-0">
                                            {c.obligations > 0
                                                /* the name must LOOK openable (Kawi, 4 Aug) —
                                                   link color + the standing open → word */
                                                ? <button onClick={() => { setExOpen(false); onOpen(c); }}
                                                    className="font-semibold text-primary hover:underline text-left">
                                                    {c.name} <span className="text-[11px] font-semibold text-secondary-hover">open →</span></button>
                                                : <span className="font-semibold">{c.name}</span>}
                                            {CLUSTER_DESC[c.id] && (
                                                <span className="block text-[11px] text-fg-tertiary truncate">{CLUSTER_DESC[c.id]}</span>
                                            )}
                                        </span>
                                        <span className="whitespace-nowrap text-[11.5px] text-fg-tertiary">
                                            {c.obligations > 0
                                                ? (adoptFor === c.id
                                                    ? <span className="inline-flex items-center gap-2">
                                                        {isSigner
                                                            ? <button onClick={() => adoptAct(c.id)} disabled={adoptBusy}
                                                                className="h-7 px-2.5 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                                                {adoptBusy ? 'Signing…' : `Adopt — as ${signer}`}</button>
                                                            : <WaitingForSigner />}
                                                        <button onClick={() => setAdoptFor(null)} disabled={adoptBusy}
                                                            className="text-[11px] text-fg-tertiary hover:underline">Cancel</button>
                                                    </span>
                                                    : <button onClick={() => { setAdoptFor(c.id); setAdoptErr(''); }}
                                                        className="text-[11.5px] font-semibold text-secondary-hover underline">Adopt</button>)
                                                : 'not mapped yet'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                    {/* the last row of the list (examples-row-v2): adding
                        your own lives where you already see what exists */}
                    <div className="mt-3.5 pt-3 border-t border-border-default">
                        <button onClick={() => { setExOpen(false); onAddCluster(); }}
                            className="text-[12.5px] font-semibold text-link hover:underline">
                            Add your own cluster →</button>
                    </div>
                </DialogShell>
            )}
        </Wrap>
    );
}

function StatTile({ n, nd, l, sub, amber, onClick, go, bar }: {
    n: string; nd?: string; l: string; sub: string; amber?: boolean; onClick?: () => void;
    /** the mock's .tgo — the arrow pinned bottom-right, never inline in the sub */
    go?: string;
    /** the mock's covbar — a two-segment coverage bar (detailed vs the rest) */
    bar?: { a: number; b: number };
}) {
    const inner = (
        <>
            <div className={`font-heading text-[28px] leading-[1.1] tabular-nums ${amber ? 'text-warning-ink' : 'text-primary'}`}>
                {n}{nd && <span className="text-[15px] text-fg-tertiary">{nd}</span>}
            </div>
            <div className="text-[11px] font-semibold text-fg-secondary mt-0.5">{l}</div>
            {bar && (
                <div className="flex h-1.5 my-1 rounded-sm overflow-hidden border border-border-default">
                    <span className="bg-secondary" style={{ flex: Math.max(bar.a, 0) }} />
                    <span className="bg-primary-tint" style={{ flex: Math.max(bar.b, 0) }} />
                </div>
            )}
            <div className="text-[11px] text-fg-tertiary leading-snug">{sub}</div>
            {go && <span className="absolute right-[13px] bottom-[10px] text-[11px] font-semibold text-secondary-hover">{go}</span>}
        </>
    );
    const cls = `relative bg-surface border border-border-default rounded-lg px-4 pt-3.5 shadow-bark-xs flex flex-col gap-0.5 text-left ${
        onClick ? 'pb-[26px]' : 'pb-3.5'}`;
    return onClick
        ? <button onClick={onClick} className={`${cls} hover:border-border-strong hover:shadow-bark-md`}>{inner}</button>
        : <div className={cls}>{inner}</div>;
}

/* The artifact register — its own page, one level down from the clusters
   (cluster → obligation → artifact; never a sibling of clusters). Groups fold
   so 55 rows never stand as one wall. */
/* the Hierarchy view (mock blessed 6 Aug, 'build build build'): the
   governing documents' tree from the signed Based-on connections;
   BARK's waiting suggestions; documents with no place yet. Setting or
   changing a place reuses the entry page's Based-on picker — the same
   signed act. Governing documents only: models, data and deliverables
   keep their own connection kinds on the Map. */
function HierarchyView({ signer, onOpen }: { signer: string; onOpen: (id: string) => void }) {
    type HDoc = { id: string; name: string; based_on: string[] };
    type HProp = { dp_id: string; ref: string; ref_label: string;
        artifact_id: string; artifact_name: string; quote: string; why: string };
    const [docs, setDocs] = useState<HDoc[] | null>(null);
    const [props, setProps] = useState<HProp[]>([]);
    const [pickFor, setPickFor] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [err, setErr] = useState('');
    const [reload, setReload] = useState(0);
    useEffect(() => {
        get<{ docs: HDoc[]; proposals: HProp[] }>('/api/inventory/register/hierarchy')
            .then(d => { setDocs(d.docs || []); setProps(d.proposals || []); })
            .catch(e => { setDocs([]); setErr(String(e)); });
    }, [reload]);
    const decideProp = async (dpId: string, verdict: 'accepted' | 'rejected') => {
        setBusy(dpId); setErr('');
        const r = await fetch(`${API}/api/inventory/doc-proposals/${dpId}/decide`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ verdict, by: signer }),
        }).catch(() => null);
        setBusy(null);
        if (!r || !r.ok) {
            // every refusal here is a 400 with a STRING detail — but a
            // validation envelope from anywhere else is an array of
            // objects, and rendering that crashes React (round 7)
            const d = r ? (await r.json().catch(() => ({}))).detail : null;
            setErr(!r ? 'network error' : (typeof d === 'string' ? d : `${r.status}`));
            return;
        }
        pokeVerdictsBadge();
        setReload(x => x + 1);
    };
    if (docs === null) return <p className="text-[13px] text-fg-tertiary">Loading the hierarchy…</p>;
    const byId = new Map(docs.map(d => [d.id, d]));
    const parents = new Set(docs.flatMap(d => d.based_on));
    const roots = docs.filter(d => d.based_on.length === 0 && (parents.has(d.id)
        || docs.some(x => x.based_on.includes(d.id))));
    const loose = docs.filter(d => d.based_on.length === 0 && !docs.some(x => x.based_on.includes(d.id)));
    const childrenOf = (id: string) => docs.filter(d => d.based_on[0] === id);
    /* the visited set is the render's own backstop against a stored circle
       (the engine refuses new ones — legacy data must still never hang
       the page in infinite recursion; audit, 6 Aug) */
    const row = (d: HDoc, depth: number, seen: Set<string>): React.ReactNode => {
        if (seen.has(d.id)) return null;
        const next = new Set(seen); next.add(d.id);
        return (
        <React.Fragment key={d.id}>
            <div className="flex gap-2.5 items-baseline py-2 border-t border-divider first:border-t-0"
                style={{ paddingLeft: depth * 28 }}>
                {depth > 0 && <span className="font-mono text-[12px] text-border-strong">└─</span>}
                <button onClick={() => onOpen(d.id)}
                    className="font-semibold text-[14px] text-fg hover:underline text-left">{d.name}</button>
                <span className="text-[12px] text-fg-tertiary min-w-0 truncate">
                    {d.based_on.length === 0 ? 'governed by nothing above it'
                        : `governed by ${d.based_on.map(s => byId.get(s)?.name || s).join(' and ')}`}</span>
                <button onClick={() => setPickFor(p => p === d.id ? null : d.id)}
                    className="ml-auto text-[11.5px] font-semibold text-fg-tertiary hover:underline whitespace-nowrap">
                    {d.based_on.length === 0 ? 'set what governs it…' : 'change…'}</button>
            </div>
            {pickFor === d.id && (
                <ComesFromWalk artifactId={d.id} exclude={[d.id]} signer={signer}
                    onDone={() => { setPickFor(null); setReload(x => x + 1); }}
                    onCancel={() => setPickFor(null)} />
            )}
            {childrenOf(d.id).map(c => row(c, depth + 1, next))}
        </React.Fragment>
        );
    };
    return (
        <div className="mb-6">
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs px-4 py-1 mb-5">
                {roots.length === 0 && loose.length === 0 && (
                    <p className="py-3 text-[13px] text-fg-secondary">No governing documents yet — they appear here as you add them.</p>
                )}
                {roots.map(d => row(d, 0, new Set()))}
            </div>
            {props.length > 0 && (
                <>
                    <LaneLabel right="suggestions count as nothing until you decide">BARK suggests</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mb-5">
                        {props.map(pr => (
                            <div key={pr.dp_id} className="px-4 py-2.5 text-[13px]">
                                <b className="font-semibold">{pr.artifact_name}</b> — governed by <b className="font-semibold">{pr.ref_label}</b>?
                                {pr.quote && <span className="block text-[12px] text-fg-secondary mt-0.5">“{pr.quote}”{pr.why ? ` — ${pr.why}` : ''}</span>}
                                <div className="flex gap-3.5 items-center mt-1.5">
                                    <button onClick={() => decideProp(pr.dp_id, 'accepted')} disabled={busy === pr.dp_id}
                                        className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                        {busy === pr.dp_id ? 'Signing…' : `Add the connection — as ${signer}`}</button>
                                    <button onClick={() => decideProp(pr.dp_id, 'rejected')} disabled={busy === pr.dp_id}
                                        className="text-[12px] font-semibold text-fg-tertiary hover:underline">dismiss</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {loose.length > 0 && (
                <>
                    <LaneLabel right={`${loose.length} document${plural(loose.length, { one: '', other: 's' })}`}>No place yet</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs px-4 py-1">
                        {loose.map(d => row(d, 0, new Set()))}
                    </div>
                </>
            )}
            {err && <p className="text-[11px] text-warning-ink mt-2">{err}</p>}
        </div>
    );
}

function ArtifactsRegisterPage({ onBack, onOpen, onAddDocument, clusterFilter, onClusterBack, focusSupport, signer }: {
    onBack: () => void; onOpen: (id: string) => void; onAddDocument: () => void;
    clusterFilter?: ClusterRow | null; onClusterBack?: () => void;
    focusSupport?: boolean; signer: string;
}) {
    const [arts, setArts] = useState<ArtifactRow[] | null>(null);
    const [clusterRows, setClusterRows] = useState<ClusterRow[]>([]);
    const [err, setErr] = useState<string>();
    const [q, setQ] = useState('');
    const [kindF, setKindF] = useState('all');
    /* grouping: Kind is the starting point (Kawi, 15 Jul); Cluster repeats
       a document under every cluster it answers */
    const [groupBy, setGroupBy] = useState<'kind' | 'cluster' | 'hierarchy'>('kind');
    const [closed, setClosed] = useState<Record<string, boolean>>({});
    useEffect(() => {
        const qs = clusterFilter ? `?cluster_id=${encodeURIComponent(clusterFilter.id)}` : '';
        get<{ artifacts: ArtifactRow[] }>(`/api/inventory/artifacts${qs}`)
            .then(d => setArts(d.artifacts)).catch(e => setErr(String(e)));
        get<{ clusters: ClusterRow[] }>('/api/inventory/clusters')
            .then(d => setClusterRows(d.clusters)).catch(() => setClusterRows([]));
    }, [clusterFilter]);
    /* "Supports this cluster" lands here — ON the shelf (Kawi, 21 Jul).
       Hook order is sacred: this sits ABOVE the early returns. */
    useEffect(() => {
        if (focusSupport && arts !== null)
            document.getElementById('register-support-shelf')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [focusSupport, arts]);
    if (!arts) return <Loading error={err} />;
    /* only the bank's own documents live in the register (examples-separation,
       Kawi 16 Jul) — examples exist solely on each cluster's examples page */
    const bank = arts.filter(a => a.data_origin === 'real');
    const hits = bank.filter(a =>
        (!q || a.name.toLowerCase().includes(q.toLowerCase()) || a.id.toLowerCase().includes(q.toLowerCase()))
        && (kindF === 'all' || a.kind === kindF));
    const KIND_ORDER = ['Policy', 'Model', 'Data', 'Deliverable'];
    // kind descriptions — one line per group banner, same pattern as the cluster table
    const KIND_DESC: Record<string, (items: ArtifactRow[]) => string> = {
        Policy: items => {
            const proc = items.filter(a => a.policy_type === 'Procedure').length;
            return `${KIND_DESC_BASE.Policy}${proc ? ` · incl. ${proc} procedure${proc > 1 ? 's' : ''}` : ''}`;
        },
        Model: () => KIND_DESC_BASE.Model,
        Data: () => KIND_DESC_BASE.Data,
        Deliverable: () => KIND_DESC_BASE.Deliverable,
    };

    const filtering = !!q || kindF !== 'all';

    /* one render shape for both groupings; a cluster is a WORLD (Kawi,
       15 Jul): answering documents first, then its support chain */
    type Group = { key: string; title: string; desc: string; items: ArtifactRow[]; support?: ArtifactRow[] };
    const inCluster = (a: ArtifactRow, cid: string) => (a.clusters || []).includes(cid);
    const supportsCluster = (a: ArtifactRow, cid: string) => (a.support_clusters || []).includes(cid);
    const groups: Group[] = groupBy === 'kind'
        ? KIND_ORDER.filter(k => hits.some(a => a.kind === k)).map(k => {
            const items = hits.filter(a => a.kind === k);
            return { key: k, title: KIND_PLURAL[k] || k, desc: KIND_DESC[k]?.(items) || '', items };
        })
        : [
            ...clusterRows
                .filter(c => hits.some(a => inCluster(a, c.id) || supportsCluster(a, c.id)))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(c => ({ key: c.id, title: c.name, desc: CLUSTER_DESC[c.id] || '',
                    items: hits.filter(a => inCluster(a, c.id)),
                    support: hits.filter(a => supportsCluster(a, c.id)) })),
            ...(hits.some(a => !(a.clusters || []).length && !(a.support_clusters || []).length)
                ? [{ key: 'none', title: 'Connected to nothing',
                    desc: 'reaches no cluster even through the support chain — worth a look',
                    items: hits.filter(a => !(a.clusters || []).length && !(a.support_clusters || []).length) }]
                : []),
        ];

    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={clusterFilter && onClusterBack ? onClusterBack : onBack} className="text-link">← Back</button>
                {/* one navigation story (Kawi, 5 Aug): Register is a top tab,
                    so no "Clusters /" parent — the trail appears only on the
                    cluster-filtered view reached FROM a cluster page */}
                {clusterFilter && onClusterBack ? (
                    <><span className="text-border-strong">·</span>
                    <button onClick={onBack} className="text-link">Clusters</button>
                    <span className="text-border-strong">/</span>
                    <button onClick={onClusterBack} className="text-link">{clusterFilter.name}</button>
                    <span className="text-border-strong">/</span></>
                ) : <span className="text-border-strong">·</span>}
                <span className="text-fg font-semibold">Register</span>
            </div>
            <PageTitle>{clusterFilter ? `Register — ${clusterFilter.name}` : 'Register'}</PageTitle>
            <PageSub>
                {clusterFilter
                    ? `What your bank signed for ${clusterFilter.name} — answering or supporting its obligations.`
                    : 'Everything your bank holds — signed, fingerprinted, on record.'}
            </PageSub>
            {/* an empty page explains nothing it cannot show (Kawi, 5 Aug):
                search, filter, grouping and the column footnote appear only
                when there are rows to point them at. The examples warning
                left the subtitle — the copper "example" badge carries it
                where examples actually appear. */}
            {bank.length > 0 && (
            <div className="flex gap-3.5 items-center flex-wrap mb-5">
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the register…"
                    className="text-[13px] h-9 px-3 w-60 border border-border-strong rounded-md bg-surface text-fg" />
                <select value={kindF} onChange={e => setKindF(e.target.value)}
                    className="text-[13px] font-semibold h-9 px-3 border border-border-strong rounded-md bg-surface text-fg-secondary">
                    <option value="all">Kind: all</option>
                    {KIND_ORDER.map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
                <span className="ml-auto">
                    <SegButtons small value={groupBy} onChange={v => setGroupBy(v as 'kind' | 'cluster' | 'hierarchy')}
                        options={[{ key: 'kind', label: 'By kind' }, { key: 'cluster', label: 'By cluster' },
                                  { key: 'hierarchy', label: 'Hierarchy' }]} />
                </span>
            </div>
            )}
            {/* the act sits on the lane title (law 5); one language everywhere —
                Register = the collection, entry = one line of it; "artifact"
                banned from the screen (Kawi's word ruling, 20 Jul — supersedes
                the 16 Jul "Artifacts" ruling; a model is not a document, so
                no umbrella-"document" either) */}
            <LaneLabel right={bank.length > 0 ? <button onClick={onAddDocument}
                className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Add your own →</button> : undefined}>
                Entries</LaneLabel>
            {groupBy === 'hierarchy' && (
                <HierarchyView signer={signer} onOpen={onOpen} />
            )}
            {/* one door when empty (Kawi, 5 Aug): the button lives IN the
                box; the lane-corner link exists only with rows */}
            {bank.length === 0 && !filtering && (
                <div className="border border-dashed border-border-strong rounded-lg bg-surface-sunken px-6 py-8 text-center text-[13px] text-fg-secondary mb-4">
                    <b className="block text-[15px] text-fg mb-1">No entries yet</b>
                    Your first entry is born when you answer an obligation on a cluster page — or add one directly.
                    <div className="mt-4">
                        <button onClick={onAddDocument}
                            className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover">
                            Add your own →</button>
                    </div>
                </div>
            )}
            {/* one list language — the exact clusters-list pattern: foldable uppercase
                group header row with the column labels, then flat bordered rows */}
            {groupBy !== 'hierarchy' && groups.map(g => {
                const open = filtering || !closed[g.key];
                const total = g.items.length + (g.support?.length || 0);
                const rowBtn = (a: ArtifactRow, isFirst: boolean, isLast: boolean) => (
                    <button key={a.id} onClick={() => onOpen(a.id)}
                        className={`w-full grid grid-cols-[minmax(220px,1fr)_90px_220px] gap-3 items-center px-3.5 py-[11px] text-[13px] bg-surface border border-border-default text-left hover:bg-surface-sunken ${
                            isFirst ? 'rounded-t-md' : 'border-t-0'} ${isLast ? 'rounded-b-md' : ''}`}>
                        <span className="min-w-0">
                            <span className="font-mono text-[11px] text-fg-tertiary mr-3">{a.id}</span>
                            <span className="font-medium text-fg">{a.name}</span>
                            {a.policy_type === 'Procedure' && <span className="text-[11px] text-fg-tertiary ml-2">procedure</span>}
                            {groupBy === 'cluster' && <span className="text-[11px] text-fg-tertiary ml-2">{KIND_LABEL[a.kind] || a.kind}</span>}
                            {/* a support document says its job — never a bare
                                orphan dash (Kawi, 15 Jul: DLV-010) */}
                            {a.answers === 0 && a.role && (
                                <span className="block text-[11px] text-fg-tertiary">{a.role}</span>
                            )}
                        </span>
                        <span className="text-right text-fg-secondary tabular-nums">
                            {a.answers > 0 ? a.answers : <span className="text-border-strong">—</span>}</span>
                        <span className="text-right whitespace-nowrap">
                            <span className="text-[11px] font-semibold text-secondary-hover">✓ signed</span>
                            <span className="text-[11px] font-semibold text-secondary-hover ml-2">open →</span>
                        </span>
                    </button>
                );
                return (
                    <div key={g.key} className="mb-[22px]">
                        <button onClick={() => setClosed(c => ({ ...c, [g.key]: open }))}
                            className="w-full grid grid-cols-[minmax(220px,1fr)_90px_220px] gap-3 items-center px-3.5 py-1.5 text-left">
                            <span className="text-[11px] font-semibold tracking-[0.06em] uppercase text-primary hover:underline">
                                {g.title}
                                <span className="font-mono text-[11px] font-medium text-fg-tertiary normal-case ml-1">{total}</span>
                                <span className="text-[11px] font-medium text-fg-tertiary normal-case tracking-normal ml-3">{g.desc}</span>
                            </span>
                            {open
                                ? <><span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Answers</span>
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Status <span className="ml-2">▴</span></span></>
                                : <><span /><span className="text-right text-[11px] text-fg-tertiary">▾</span></>}
                        </button>
                        {open && g.items.map((a, i) => rowBtn(a, i === 0,
                            i === g.items.length - 1 && !(g.support?.length)))}
                        {/* the cluster's support chain — clearly its own shelf,
                            never mixed into the answers (Kawi, 15 Jul) */}
                        {open && (g.support?.length || 0) > 0 && (
                            <>
                                <div id="register-support-shelf"
                                    className={`w-full px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.06em] uppercase text-fg-tertiary bg-surface-sunken border border-border-default ${
                                    g.items.length === 0 ? 'rounded-t-md' : 'border-t-0'}`}>
                                    Supports this cluster
                                </div>
                                {g.support!.map((a, i) => rowBtn(a, false, i === g.support!.length - 1))}
                            </>
                        )}
                    </div>
                );
            })}
            {groups.length > 0 && (
            <p className="flex gap-4 items-baseline text-[11px] text-fg-tertiary mt-2.5">
                <span className="min-w-0">Answers = how many obligations the entry satisfies. Shared entries are the norm — one entry can
                answer several obligations. {groupBy === 'cluster' && <b className="text-fg-secondary">Grouped by cluster, an entry appears under every cluster it answers. </b>}
                Upload and withdraw live on the entry's page and the obligation pages.</span>
                {/* the rare maintenance act lives on the quiet line, not the
                    corner (Kawi, 6 Aug: "add new feels malplaced") */}
                <span className="ml-auto whitespace-nowrap"><SweepDoor /></span>
            </p>
            )}
        </Wrap>
    );
}

/* ---------- "Start from the example" — a modal: description, then decide ----------
   (Kawi, 16 Jul): the description first — what this is and what it would
   answer — then the decision, without leaving the cluster page. Examples
   carry no prose description; the honest description is the name, the
   kind and the obligations it covers. */
function StartFromExampleModal({ art, signer, targetId, also, onDone, onCancel }: {
    art: ArtifactRow; signer: string; targetId: string; also: string[];
    onDone: (newId?: string) => void; onCancel: () => void;
}) {
    const obls = art.obligations || [];
    return (
        <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8">
            {/* one box, one title (Kawi, 4 Aug: "way too messy") — the
                obligations are named on the Answers line inside the panel;
                the pick list stays folded behind "change →" */}
            <div className="bg-surface rounded-lg shadow-bark-md max-w-xl w-full my-8 px-5 py-4">
                <p className="text-[15px] font-semibold">Start from the example — {art.name}</p>
                <p className="text-[12px] text-fg-tertiary mt-0.5">
                    {KIND_LABEL[art.kind] || art.kind}{art.policy_type === 'Procedure' ? ' · procedure' : ''} <ExMark />
                </p>
                {art.description && (
                    <p className="text-[12px] text-fg-tertiary mt-1">{art.description}</p>
                )}
                <AnswerPanel obligationId={targetId} signer={signer} bare
                    oblTitles={Object.fromEntries(obls.map(o => [o.id, o.title]))}
                    initialName={art.name} initialKind={art.kind} fromExample={art.name}
                    initialAlso={also} onDone={onDone} onCancel={onCancel} />
            </div>
        </div>
    );
}

/* ---------- the institution profile — facts, confirm, sign, history ---------- */

/* ONE-NAME RULE (Kawi, 5 Aug: "the engine is the best source"): a fact's
   screen name comes from the profile contract API and nowhere else — the
   friendly words of the 18 Jul ruling now LIVE in the engine, with the
   formal term served alongside for hover. No hand-written label maps. */

/* ISO 3166-1 country codes -> country name — worldwide, not just Europe
   (Kawi, 19 Jul: "we are global"). An unknown code shows as itself, honestly. */
const COUNTRY_NAMES: Record<string, string> = {
    AF: 'Afghanistan', AX: 'Åland Islands', AL: 'Albania', DZ: 'Algeria', AS: 'American Samoa',
    AD: 'Andorra', AO: 'Angola', AI: 'Anguilla', AQ: 'Antarctica', AG: 'Antigua and Barbuda',
    AR: 'Argentina', AM: 'Armenia', AW: 'Aruba', AU: 'Australia', AT: 'Austria',
    AZ: 'Azerbaijan', BS: 'Bahamas', BH: 'Bahrain', BD: 'Bangladesh', BB: 'Barbados',
    BY: 'Belarus', BE: 'Belgium', BZ: 'Belize', BJ: 'Benin', BM: 'Bermuda', BT: 'Bhutan',
    BO: 'Bolivia', BA: 'Bosnia and Herzegovina', BW: 'Botswana', BR: 'Brazil',
    IO: 'British Indian Ocean Territory', BN: 'Brunei', BG: 'Bulgaria', BF: 'Burkina Faso',
    BI: 'Burundi', CV: 'Cabo Verde', KH: 'Cambodia', CM: 'Cameroon', CA: 'Canada',
    KY: 'Cayman Islands', CF: 'Central African Republic', TD: 'Chad', CL: 'Chile', CN: 'China',
    CO: 'Colombia', KM: 'Comoros', CG: 'Congo', CD: 'Congo (DRC)', CK: 'Cook Islands',
    CR: 'Costa Rica', CI: 'Côte d’Ivoire', HR: 'Croatia', CU: 'Cuba', CW: 'Curaçao',
    CY: 'Cyprus', CZ: 'Czechia', DK: 'Denmark', DJ: 'Djibouti', DM: 'Dominica',
    DO: 'Dominican Republic', EC: 'Ecuador', EG: 'Egypt', SV: 'El Salvador',
    GQ: 'Equatorial Guinea', ER: 'Eritrea', EE: 'Estonia', SZ: 'Eswatini', ET: 'Ethiopia',
    FK: 'Falkland Islands', FO: 'Faroe Islands', FJ: 'Fiji', FI: 'Finland', FR: 'France',
    GF: 'French Guiana', PF: 'French Polynesia', GA: 'Gabon', GM: 'Gambia', GE: 'Georgia',
    DE: 'Germany', GH: 'Ghana', GI: 'Gibraltar', GR: 'Greece', GL: 'Greenland', GD: 'Grenada',
    GP: 'Guadeloupe', GU: 'Guam', GT: 'Guatemala', GG: 'Guernsey', GN: 'Guinea',
    GW: 'Guinea-Bissau', GY: 'Guyana', HT: 'Haiti', HN: 'Honduras', HK: 'Hong Kong',
    HU: 'Hungary', IS: 'Iceland', IN: 'India', ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq',
    IE: 'Ireland', IM: 'Isle of Man', IL: 'Israel', IT: 'Italy', JM: 'Jamaica', JP: 'Japan',
    JE: 'Jersey', JO: 'Jordan', KZ: 'Kazakhstan', KE: 'Kenya', KI: 'Kiribati', KW: 'Kuwait',
    KG: 'Kyrgyzstan', LA: 'Laos', LV: 'Latvia', LB: 'Lebanon', LS: 'Lesotho', LR: 'Liberia',
    LY: 'Libya', LI: 'Liechtenstein', LT: 'Lithuania', LU: 'Luxembourg', MO: 'Macao',
    MG: 'Madagascar', MW: 'Malawi', MY: 'Malaysia', MV: 'Maldives', ML: 'Mali', MT: 'Malta',
    MH: 'Marshall Islands', MQ: 'Martinique', MR: 'Mauritania', MU: 'Mauritius', YT: 'Mayotte',
    MX: 'Mexico', FM: 'Micronesia', MD: 'Moldova', MC: 'Monaco', MN: 'Mongolia',
    ME: 'Montenegro', MS: 'Montserrat', MA: 'Morocco', MZ: 'Mozambique', MM: 'Myanmar',
    NA: 'Namibia', NR: 'Nauru', NP: 'Nepal', NL: 'Netherlands', NC: 'New Caledonia',
    NZ: 'New Zealand', NI: 'Nicaragua', NE: 'Niger', NG: 'Nigeria', NU: 'Niue',
    MK: 'North Macedonia', NO: 'Norway', OM: 'Oman', PK: 'Pakistan', PW: 'Palau',
    PS: 'Palestine', PA: 'Panama', PG: 'Papua New Guinea', PY: 'Paraguay', PE: 'Peru',
    PH: 'Philippines', PL: 'Poland', PT: 'Portugal', PR: 'Puerto Rico', QA: 'Qatar',
    RE: 'Réunion', RO: 'Romania', RU: 'Russia', RW: 'Rwanda', BL: 'Saint Barthélemy',
    KN: 'Saint Kitts and Nevis', LC: 'Saint Lucia', MF: 'Saint Martin',
    VC: 'Saint Vincent and the Grenadines', WS: 'Samoa', SM: 'San Marino',
    ST: 'São Tomé and Príncipe', SA: 'Saudi Arabia', SN: 'Senegal', RS: 'Serbia',
    SC: 'Seychelles', SL: 'Sierra Leone', SG: 'Singapore', SX: 'Sint Maarten', SK: 'Slovakia',
    SI: 'Slovenia', SB: 'Solomon Islands', SO: 'Somalia', ZA: 'South Africa',
    KR: 'South Korea', SS: 'South Sudan', ES: 'Spain', LK: 'Sri Lanka', SD: 'Sudan',
    SR: 'Suriname', SE: 'Sweden', CH: 'Switzerland', SY: 'Syria', TW: 'Taiwan',
    TJ: 'Tajikistan', TZ: 'Tanzania', TH: 'Thailand', TL: 'Timor-Leste', TG: 'Togo',
    TK: 'Tokelau', TO: 'Tonga', TT: 'Trinidad and Tobago', TN: 'Tunisia', TR: 'Türkiye',
    TM: 'Turkmenistan', TC: 'Turks and Caicos Islands', TV: 'Tuvalu', UG: 'Uganda',
    UA: 'Ukraine', AE: 'United Arab Emirates', GB: 'United Kingdom', US: 'United States',
    UY: 'Uruguay', UZ: 'Uzbekistan', VU: 'Vanuatu', VA: 'Vatican City', VE: 'Venezuela',
    VN: 'Vietnam', VG: 'British Virgin Islands', VI: 'U.S. Virgin Islands', YE: 'Yemen',
    ZM: 'Zambia', ZW: 'Zimbabwe',
};

const COUNTRY_FIELDS = new Set(['home_member_state', 'markets_active']);

/* the value a person reads: a country code becomes the country name (with
   the code kept in brackets); everything else is shown as stored. */
function prettyValue(key: string, value: string): string {
    if (!value || !COUNTRY_FIELDS.has(key)) return value;
    return value.split(/[;,]/).map(s => s.trim()).filter(Boolean)
        .map(code => {
            const name = COUNTRY_NAMES[code.toUpperCase()];
            return name ? `${name} (${code.toUpperCase()})` : code;
        }).join(' · ');
}

type QueueFact = {
    fact_id: string; key: string; label?: string; value: string; status: string;
    quote: string | null; source_url: string | null; source_title: string | null;
    door: string; reviewed_by?: string | null; reviewed_at?: string | null;
    review_note?: string | null;
};
type QueueState = {
    candidates: QueueFact[]; approved: QueueFact[]; rejected: QueueFact[];
    superseded: QueueFact[]; conflicting_keys: string[]; unresolved_keys: string[];
};

/* the identity card of step 1 — keys straight from the profile contract */
const IDENTITY_KEYS = ['legal_name', 'legal_name_short', 'lei', 'home_member_state', 'competent_authority'] as const;
type IdMatch = Partial<Record<(typeof IDENTITY_KEYS)[number], string>> & {
    summary?: string; source_url?: string; quote?: string;
};
type ContractField = {
    key: string; label: string; formal_term?: string; role: string; allowed_values: string[];
    required_for_promotion: boolean; legal_source: string; description: string;
    condition_field?: string; condition_values?: string[]; multi_value?: boolean;
    display_labels?: Record<string, string>; value_notes?: Record<string, string>;
    reads?: number;
};

/* the profile lanes (mock blessed 6 Aug: "there should be more than 9" —
   ALL contract fields on the page, grouped by what they say; the airy
   grid stays). Keys not named here still render — they fall into a last
   lane, so a new contract field can never vanish. Identity lives in the
   head, editable behind its own fold. */
const FACT_LANES: { title: string; sub?: string; keys: string[] }[] = [
    { title: 'What you are', sub: 'each answer decides what applies to you',
      keys: ['institution_type', 'snci_status', 'large_institution_status', 'srep_category',
             'o_sii_status', 'consolidation_level', 'trading_book_status', 'dora_micro_status'] },
    { title: 'What you do', sub: 'business lines and activities',
      keys: ['lines_of_business', 'lines_of_business_commercial', 'lending_types',
             'lending_counterparties', 'deposit_types', 'payment_types', 'markets_active',
             'internal_model_usage'] },
    { title: 'How big you are',
      keys: ['total_assets', 'employee_count', 'customer_count'] },
    { title: 'Reporting',
      keys: ['reporting_period', 'pillar3_publication_signal', 'business_lines_evidence'] },
];

export function ProfilePage({ onBack, signer, showCrumb = true, focus, onFocusDone }: {
    onBack: () => void; signer: string; showCrumb?: boolean;
    /* item 14 (6 Aug): arriving from a conflict door — the disputed fact,
       both values side by side, the act right here */
    focus?: ProfileFocus | null; onFocusDone?: () => void;
}) {
    /* the role decides which acts exist on screen (Kawi, 20 Jul roles):
       a member proposes; only a signer decides. Server gates enforce it —
       this only makes the screen honest. */
    const { is_signer: isSigner } = React.useContext(MeContext);
    /* the bank's own signer list (signers only see and manage it) */
    const [signersList, setSignersList] = useState<string[] | null>(null);
    const [signerErr, setSignerErr] = useState('');
    const [p, setP] = useState<ProfileData | null>(null);
    const [err, setErr] = useState<string>();
    const [editKey, setEditKey] = useState<string | null>(null);
    const [val, setVal] = useState('');
    const [busy, setBusy] = useState(false);
    const [reload, setReload] = useState(0);
    /* search in two steps (Kawi, 17 Jul: typing the exact name is not a
       good solution): 1) rough text in — BARK returns the real
       institutions it found, with sources; the user confirms which bank
       is theirs; 2) the deep search runs anchored to the confirmed
       identity, over the FULL contract. Every finding is a candidate —
       nothing counts until a named signer approves. Demo Bank AB is
       fictional: the public record finds nothing for it; the documents
       door is the path for demo data. */
    const [idQ, setIdQ] = useState('');
    const [idMatches, setIdMatches] = useState<IdMatch[] | null>(null);
    const [dBusy, setDBusy] = useState(false);
    const [deepFor, setDeepFor] = useState<string | null>(null);
    const [dErr, setDErr] = useState<string>();
    const [notes, setNotes] = useState<string[]>([]);
    /* the ONE queue (Nick's engine, taken into the inventory 17 Jul):
       every door — web search, document runs, manual — lands candidates
       here; only a named signer's review makes a fact count */
    const [queue, setQueue] = useState<QueueState | null>(null);
    const [contract, setContract] = useState<ContractField[]>([]);
    const [groupSt, setGroupSt] = useState<GroupState | null>(null);
    const [rejectFor, setRejectFor] = useState<string | null>(null);
    const [rejectNote, setRejectNote] = useState('');
    /* the profile-C layout (panel + Kawi, 17 Jul): the search retires to a
       line, history hides behind a link, answering opens on top */
    const [searchOpen, setSearchOpen] = useState<boolean | null>(null);
    const [histOpen, setHistOpen] = useState(false);
    const [idOpen, setIdOpen] = useState(false);
    const [fieldModal, setFieldModal] = useState<{ key: string; mode: 'confirm' | 'suggest' } | null>(null);
    /* errors shown WHERE the act happened (BAR-124, 18 Jul): the answer
       window keeps its own error and stays open on failure; a review error
       shows on the very card row it came from */
    const [modalErr, setModalErr] = useState<string>();
    const [reviewErr, setReviewErr] = useState<{ id: string; msg: string }>();
    /* per-field search (Kawi, 17 Jul: "i want to be able to search for each
       separate") — one bounded run for ONE field, from inside its window */
    const [fsName, setFsName] = useState('');
    const [fsBusy, setFsBusy] = useState(false);
    /* the per-field search status — a proper banner, not a floating line
       (Kawi, 18 Jul: "the message row when searching is ugly") */
    const [fsStatus, setFsStatus] = useState<
        { label: string; phase: 'searching' | 'found' | 'known' | 'off_vocab' | 'none' | 'error'; n?: number; msg?: string } | null>(null);
    const identifyRun = async () => {
        setDBusy(true); setDErr(undefined); setIdMatches(null); setNotes([]);
        try {
            const r = await fetch(`${API}/api/inventory/profile/identify`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ query: idQ.trim() }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            const matches: IdMatch[] = (await r.json()).matches || [];
            setIdMatches(matches);
            if (matches.length === 0) setDErr('No institution in the public record matches that text. Try the legal name or the organisation number.');
        } catch (e) { setDErr(String(e)); }
        setDBusy(false);
    };
    const confirmBank = async (m: IdMatch) => {
        setIdMatches(null); setDeepFor(m.legal_name || ''); setDErr(undefined);
        const runId = searchStarted(`${m.legal_name || 'the bank'} — full search`);
        try {
            // the identity card's own facts wait in the queue like everything else
            const known = IDENTITY_KEYS.filter(k => m[k]);
            if (known.length) {
                await fetch(`${API}/api/inventory/profile/candidates`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                    body: JSON.stringify({
                        items: known.map(k => ({ key: k, value: m[k], source_url: m.source_url || '',
                            source_title: 'public record', quote: m.quote || '' })),
                        door: 'web', by: signer,
                    }),
                });
                setReload(x => x + 1);
            }
            const r = await fetch(`${API}/api/inventory/profile/discover`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    institution_name: m.legal_name,
                    identity: Object.fromEntries(known.map(k => [k, m[k]])),
                }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            const d = await r.json();
            setNotes(d.notes || []);
            /* the finish is VISIBLE (Kawi, 20 Jul Klarna: "i cant see any
               thinking icon or anything that says its running") — the same
               honest banner the per-field search uses */
            const nNew = d.new ?? d.proposals?.length ?? 0;
            setFsStatus({ label: `${m.legal_name} — full search`,
                phase: nNew > 0 ? 'found' : ((d.already_known ?? 0) > 0 ? 'known' : 'none'), n: nNew });
            /* the waiting card must show the findings NOW — the group half
               still runs for minutes after this (Kawi, 21 Jul: "found 22
               for norion but i dont see the cards") */
            setReload(x => x + 1);
            if ((d.proposals?.length ?? 0) === 0 && !(d.notes?.length))
                setDErr('Nothing found with a public source — those facts stay manual entry.');
            /* ONE search door (Kawi, 17 Jul: "the search is already above") —
               the same run also proposes the group; family members land as
               candidates in the same waiting card. A failure here never
               hides the facts result — it lands as a note. */
            try {
                const g = await fetch(`${API}/api/inventory/profile/group/discover`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                    body: JSON.stringify({ institution_name: m.legal_name }),
                });
                if (!g.ok) throw new Error((await g.json()).detail || `${g.status}`);
                const stored = (await g.json()).stored?.length ?? 0;
                if (stored === 0)
                    setNotes(n => [...n, 'no group found with a public source — this institution may stand alone']);
            } catch (ge) {
                console.warn('group discovery failed', ge);   // detail for the log, words for the page
                setNotes(n => [...n, 'the group search did not finish — the group is missing from this run. Use search again above to fill it.']);
            }
        } catch (e) { setDErr(String(e)); }
        searchEnded(runId);
        setDeepFor(null); setReload(x => x + 1);
    };
    /* the manual door: a typed value is a candidate for review too —
       same rule for every door, no side path */
    const suggest = async (key: string, value: string) => {
        setBusy(true); setModalErr(undefined);
        try {
            const r = await fetch(`${API}/api/inventory/profile/candidates`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ items: [{ key, value: value.trim(), source_title: 'typed by hand' }],
                    door: 'manual', by: signer }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            setFieldModal(null); setVal(''); setReload(x => x + 1);
        } catch (e) { setModalErr(String(e).replace(/^Error:\s*/, '')); }
        setBusy(false);
    };
    const reviewEntity = async (entityId: string, verdict: 'approve' | 'reject', note = '') => {
        setDErr(undefined);
        const r = await fetch(`${API}/api/inventory/profile/group/entities/${entityId}/review`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ verdict, note, by: signer }),
        });
        if (!r.ok) { setDErr((await r.json()).detail || `${r.status}`); return; }
        setRejectFor(null); setRejectNote(''); setReload(x => x + 1);
    };
    /* one reviewer for the other waiting kinds (relations, entity facts) */
    const reviewAt = async (path: string, verdict: 'approve' | 'reject', note = '') => {
        setDErr(undefined);
        const r = await fetch(`${API}${path}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ verdict, note, by: signer }),
        });
        if (!r.ok) { setDErr((await r.json()).detail || `${r.status}`); return; }
        setRejectFor(null); setRejectNote(''); setReload(x => x + 1);
    };
    const [focusBusy, setFocusBusy] = useState(false);
    const [focusErr, setFocusErr] = useState('');
    const decideFocus = async (verdict: 'accepted' | 'rejected') => {
        if (!focus) return;
        setFocusBusy(true); setFocusErr('');
        try {
            const r = await fetch(`${API}/api/inventory/doc-proposals/${focus.dp_id}/decide`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ verdict, by: signer }),
            });
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `${r.status}`);
            pokeVerdictsBadge();
            onFocusDone?.();
            setReload(x => x + 1);
        } catch (e) { setFocusErr(String(e).replace(/^Error:\s*/, '')); }
        setFocusBusy(false);
    };
    const review = async (factId: string, verdict: 'approve' | 'reject', note = '') => {
        setReviewErr(undefined);
        const r = await fetch(`${API}/api/inventory/profile/facts/${factId}/review`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ verdict, note, by: signer }),
        });
        /* the reason shows ON THE CARD ROW, not far away (BAR-124, 18 Jul) */
        if (!r.ok) { setReviewErr({ id: factId, msg: (await r.json()).detail || `${r.status}` }); return; }
        setRejectFor(null); setRejectNote(''); setReviewErr(undefined); setReload(x => x + 1);
        pokeVerdictsBadge();   // a decided card leaves the count NOW (6 Aug)
    };
    useEffect(() => {
        get<ProfileData>('/api/inventory/profile').then(setP).catch(e => setErr(String(e)));
        get<QueueState>('/api/inventory/profile/queue').then(setQueue).catch(() => setQueue(null));
        get<GroupState>('/api/inventory/profile/group')
            .then(setGroupSt).catch(() => setGroupSt({ entities: [] }));
    }, [reload]);
    useEffect(() => {
        get<{ fields: ContractField[] }>('/api/inventory/profile/contract')
            .then(d => setContract(d.fields || [])).catch(() => setContract([]));
    }, []);
    if (!p) return <Loading error={err} />;

    const post = async (path: string, body: object) => {
        setBusy(true); setModalErr(undefined);
        try {
            const r = await fetch(`${API}${path}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(body),
            });
            /* the server's refusal carries the plain reason (value gate) — show
               it INSIDE the window and keep the window open (BAR-124, 18 Jul:
               the error used to render behind the window, so Confirm looked
               like a no-op) */
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            setEditKey(null); setVal(''); setFieldModal(null); setReload(x => x + 1);
            pokeVerdictsBadge();   // signing/confirming moves the count NOW (6 Aug)
        } catch (e) { setModalErr(String(e).replace(/^Error:\s*/, '')); }
        setBusy(false);
    };
    const answered = Object.values(p.facts).filter(Boolean).length;

    /* ---- the profile-C derivations ---- */
    const totalSwitches = Object.keys(p.facts).length;
    const apprBy = new Map((queue?.approved || []).map(a => [a.key, a]));
    const candCount = new Map<string, number>();
    (queue?.candidates || []).forEach(f => candCount.set(f.key, (candCount.get(f.key) || 0) + 1));
    const contextFields = contract.filter(f => f.role !== 'required_classification');
    const answeredContext = contextFields.filter(f => apprBy.has(f.key)).length;
    const entCands = (groupSt?.entities || []).filter(e => e.status === 'candidate');
    const linkCands = (groupSt?.links || []).filter(l => l.status === 'candidate');
    const efactCands = (groupSt?.entity_facts || []).filter(f => f.status === 'candidate');
    const changeCands = (groupSt?.changes || []).filter(c => c.status === 'candidate');
    const waitingN = (queue?.candidates.length || 0) + entCands.length
        + linkCands.length + efactCands.length + changeCands.length;
    const changed = p.changed_since_signing || [];
    const legalName = apprBy.get('legal_name')?.value || p.identity?.legal_name || 'Institution profile';
    const headWho = [
        p.facts.institution_type,
        apprBy.get('home_member_state') && `authorised in ${prettyValue('home_member_state', apprBy.get('home_member_state')!.value)}`,
        apprBy.get('competent_authority') && `supervised by ${apprBy.get('competent_authority')!.value}`,
    ].filter(Boolean).join(' · ');
    const lei = apprBy.get('lei')?.value;
    const confirmedDate = (apprBy.get('legal_name')?.reviewed_at || '').slice(0, 10);
    const dayZero = answered === 0 && waitingN === 0 && (queue?.approved.length || 0) === 0;
    const showSearch = searchOpen ?? dayZero;
    const modalDef = fieldModal ? contract.find(f => f.key === fieldModal.key) : undefined;
    const offList = (key: string, value: string): boolean => {
        const def = contract.find(c => c.key === key);
        if (!def || def.allowed_values.length === 0) return false;
        const parts = def.multi_value
            ? value.split(';').map(s => s.trim()).filter(Boolean) : [value];
        return !parts.every(v => def.allowed_values.includes(v));
    };
    /* found answers land as candidates in the one waiting card; the window
       CLOSES on search (Kawi, 18 Jul: "im not sure if i can close the window
       or not....i dont know when the cards will show up and where") — the
       page carries the progress line instead */
    const searchField = async (key: string) => {
        const label = contract.find(f => f.key === key)?.label || key;
        setFieldModal(null); setVal('');
        setFsBusy(true);
        setFsStatus({ label, phase: 'searching' });
        const runId = searchStarted(`${fsName.trim() || 'the bank'} — ${label}`);
        try {
            const r = await fetch(`${API}/api/inventory/profile/discover`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ institution_name: fsName.trim(), keys: [key] }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            const d = await r.json();
            const n = d.proposals?.length ?? 0;
            // honest four-way outcome (bug 4b, 18 Jul): a value refused as
            // off-vocabulary is NOT 'already answered' and NOT 'no source'
            const phase = n > 0 ? 'found'
                : d.already_known > 0 ? 'known'
                : d.off_vocabulary > 0 ? 'off_vocab'
                : 'none';
            setFsStatus({ label, phase, n });
        } catch (e) { setFsStatus({ label, phase: 'error', msg: String(e) }); }
        searchEnded(runId);
        setFsBusy(false); setReload(x => x + 1);
    };
    const openField = (key: string, mode: 'confirm' | 'suggest') => {
        const def = contract.find(f => f.key === key);
        const allowed = def && def.allowed_values.length > 0 ? def.allowed_values : null;
        const multi = !!def?.multi_value;
        setFsStatus(null); setModalErr(undefined);
        setFsName(apprBy.get('legal_name')?.value || '');
        const cur = mode === 'confirm' ? (p.facts[key] || '') : (apprBy.get(key)?.value || '');
        /* a stored off-list value (an old typo) never pre-fills the choice */
        let prefill = cur;
        if (allowed && multi) prefill = cur.split(';').map(s => s.trim()).filter(s => allowed.includes(s)).join('; ');
        else if (allowed && !allowed.includes(cur)) prefill = '';
        setVal(prefill); setFieldModal({ key, mode });
    };
    const histItems = [
        ...p.records.map(r => ({ id: r.id, outcome: r.outcome, what: `${r.scope || ''}${r.note ? ` — ${r.note}` : ''}`, by: r.by, at: r.at })),
        ...(queue ? [...queue.approved, ...queue.rejected, ...queue.superseded] : [])
            .filter(f => f.reviewed_by)
            .map(f => ({ id: f.fact_id, outcome: f.status, what: `profile fact ${f.label || f.key} = ${f.value}${f.review_note ? ` — ${f.review_note}` : ''}`, by: f.reviewed_by || null, at: f.reviewed_at || null })),
    ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));

    /* ONE cell for every fact (6 Aug lanes): a switch key reads p.facts and
       confirms; every other key reads the approved queue and suggests —
       exactly the two doors the old wall and the old context rows used.
       Conditional relevance, reference dates and multi-value folds move in
       unchanged. */
    const cellFor = (k: string, i: number, cols: number) => {
        const defK = contract.find(f => f.key === k);
        const isSwitch = Object.prototype.hasOwnProperty.call(p.facts, k);
        const a = apprBy.get(k);
        const rawV = isSwitch ? (p.facts[k] || '') : (a?.value || '');
        const short = (w: string) => defK?.display_labels?.[w] || w;
        const parts = rawV && defK?.multi_value
            ? rawV.split(';').map(s => s.trim()).filter(Boolean) : [];
        const asCount = parts.length > 3;
        const v = rawV && defK?.multi_value && !asCount
            ? parts.map(short).join(' · ')
            : (rawV && !isSwitch ? prettyValue(k, rawV) : rawV);
        const nCand = candCount.get(k) || 0;
        const reads = defK?.reads || 0;
        /* conditional relevance (general ruling, 17 Jul) */
        const condVal = defK?.condition_field
            ? (Object.prototype.hasOwnProperty.call(p.facts, defK.condition_field)
                ? p.facts[defK.condition_field] : apprBy.get(defK.condition_field)?.value)
            : null;
        const condParts = (condVal || '').split(';').map(s => s.trim()).filter(Boolean);
        const inapplicable = !!(defK?.condition_field && defK.condition_values?.length
            && !condParts.some(x => defK.condition_values!.includes(x)));
        /* every number carries its reference date (general ruling, 17 Jul) */
        const period = Object.prototype.hasOwnProperty.call(p.facts, 'reporting_period')
            ? p.facts['reporting_period'] : apprBy.get('reporting_period')?.value;
        const numeric = ['total_assets', 'employee_count', 'customer_count'].includes(k);
        return (
            <div key={k} className={`group px-4 pt-3 pb-2.5 ${i >= cols ? 'border-t border-divider' : ''} ${
                i % cols !== 0 ? 'border-l border-divider' : ''} ${!v && !inapplicable ? 'bg-surface-sunken' : ''}`}>
                <p className="text-[11px] font-semibold tracking-[0.05em] uppercase text-fg-tertiary"
                    title={defK?.formal_term || defK?.label}>
                    {defK?.label || k}</p>
                {inapplicable ? (
                    <p className="text-[13px] text-border-strong mt-1">
                        — applies when {contract.find(c => c.key === defK!.condition_field)?.label || defK!.condition_field} is {defK!.condition_values!.join(' or ')}</p>
                ) : asCount ? (
                    <details className="mt-0.5">
                        <summary title="Click to see which ones"
                            className="list-none cursor-pointer [&::-webkit-details-marker]:hidden">
                            <span className="text-[17px] font-semibold text-fg leading-snug">
                                {(defK?.allowed_values?.length || 0) > 0
                                    ? `${parts.length} of ${defK!.allowed_values.length}` : parts.length}</span>
                            <span className="text-[11px] text-fg-tertiary"> — which ones ▾</span>
                        </summary>
                        <div className="grid grid-cols-2 gap-x-6 mt-2 pt-2 border-t border-divider text-[12.5px] text-fg-secondary leading-relaxed">
                            {parts.map(w => <div key={w}>{short(w)}</div>)}
                            {(defK?.allowed_values || []).filter(x => !parts.includes(x)).map(x => (
                                <div key={x} className="text-border-strong">{short(x)} — not picked</div>))}
                        </div>
                    </details>
                ) : v ? (
                    <p className={`${String(v).length > 24 ? 'text-[14px]' : 'text-[17px]'} font-semibold text-fg mt-0.5 leading-snug`}>
                        {v}
                        {numeric && (
                            <span className="font-normal text-[11px] text-fg-tertiary">
                                {' '}· {period ? `as at ${period}` : 'no reference date'}</span>
                        )}</p>
                ) : <p className="text-[13px] text-border-strong mt-1">— not answered{isSwitch ? ' · decides nothing' : ''}</p>}
                {!inapplicable && (
                    <p className="text-[11px] text-fg-tertiary mt-1">
                        {changed.includes(k) && <span className="font-semibold text-warning-ink">changed after signing · </span>}
                        {nCand > 0 && <>{nCand} suggestion{plural(nCand, { one: '', other: 's' })} waiting · </>}
                        {reads > 0 && <>read by {reads} obligation{plural(reads, { one: '', other: 's' })} · </>}
                        <button onClick={() => openField(k, isSwitch ? 'confirm' : 'suggest')}
                            className={'text-link font-semibold'}>
                            {v ? 'change →' : 'answer →'}</button>
                    </p>
                )}
            </div>
        );
    };

    return (
        <Wrap>
            {/* the crumb belongs to the INLINE door only — the standalone
                /institution page says it in its own head (Kawi, 20 Jul) */}
            {showCrumb && (
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <span className="text-fg font-semibold">Institution profile</span>
            </div>
            )}

            {/* item 14 (6 Aug): arriving from a conflict door — the disputed
                fact with BOTH values and the act, right at the top. Using
                the document's value goes through the queue (candidate +
                approve, supersedes); keeping the profile's buries the
                suggestion durably. */}
            {focus && (
                <div className="border border-border-strong rounded-lg bg-surface shadow-bark-md px-4 py-3 mb-6">
                    <p className="text-[13px] text-fg mb-1">
                        <b className="font-semibold">{focus.artifact_name || 'Your document'}</b> says{' '}
                        <b className="font-semibold">{focus.label}</b>: <b className="font-semibold">{focus.value}</b>
                        {' — '}the profile holds <b className="font-semibold">{apprBy.get(focus.key)?.value || '—'}</b>.
                    </p>
                    {focus.quote && <p className="text-[12px] text-fg-secondary mb-2.5">“{focus.quote}”</p>}
                    <div className="flex gap-3.5 items-center flex-wrap">
                        {isSigner ? (
                            <button onClick={() => decideFocus('accepted')} disabled={focusBusy}
                                className="h-8 px-3.5 rounded-md bg-primary text-white text-[12.5px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                {focusBusy ? 'Signing…' : `Use the document's value — as ${signer}`}</button>
                        ) : <WaitingForSigner />}
                        <button onClick={() => decideFocus('rejected')} disabled={focusBusy}
                            className="text-[12px] font-semibold text-fg-tertiary hover:underline">
                            Keep the profile's value</button>
                        <button onClick={() => onFocusDone?.()}
                            className="text-[11px] font-semibold text-fg-tertiary hover:underline ml-auto">Cancel</button>
                        {focusErr && <span className="text-[11px] text-warning-ink">{focusErr}</span>}
                    </div>
                </div>
            )}

            {/* THE HEAD — the bank as a bank, not a form (profile-C, Kawi 17 Jul) */}
            <div className="flex gap-7 items-start flex-wrap">
                <div className="min-w-0">
                    <PageTitle>{legalName}</PageTitle>
                    {headWho && <p className="text-[14px] text-fg-secondary -mt-1">{headWho}</p>}
                    <p className="text-[12px] text-fg-tertiary mt-1.5">
                        {lei && <><span className="font-mono">LEI {lei}</span> · </>}
                        {confirmedDate && <>public record confirmed {confirmedDate} · </>}
                        {/* an existing bank's name prefills the box (Kawi,
                            21 Jul) — only when empty, typed text is never
                            overwritten */}
                        <button onClick={() => {
                            if (!idQ.trim()) setIdQ(apprBy.get('legal_name')?.value || '');
                            setSearchOpen(true);
                        }} className="text-link font-semibold">
                            {confirmedDate ? 'search again →' : 'search the public record →'}</button>
                        {' · '}
                        <button onClick={() => setIdOpen(o => !o)} className="text-link font-semibold">
                            {idOpen ? 'close identity ✕' : 'edit identity →'}</button>
                    </p>
                </div>
                <div className="ml-auto text-right shrink-0">
                    {!p.signed ? (
                        <>
                            <p className="text-[13px] font-semibold text-warning-ink">Not signed — nothing counts yet</p>
                            <p className="text-[12px] text-fg-tertiary mt-0.5">
                                the signature makes the facts count · <button onClick={() => setHistOpen(true)}
                                    className="text-link font-semibold">history →</button></p>
                            {answered > 0 && isSigner && (
                                <button onClick={() => post('/api/inventory/profile/sign', { by: signer })} disabled={busy}
                                    className="mt-2.5 h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                    Sign — as {signer}</button>
                            )}
                        </>
                    ) : changed.length > 0 ? (
                        <>
                            <p className="text-[13px] font-semibold text-warning-ink">Signed for an earlier version</p>
                            <p className="text-[12px] text-fg-tertiary mt-0.5">
                                {changed.length} fact{changed.length > 1 ? 's' : ''} changed since signing on {p.signed_at} · <button
                                    onClick={() => setHistOpen(true)} className="text-link font-semibold">history →</button></p>
                            {isSigner && (
                            <button onClick={() => post('/api/inventory/profile/sign', { by: signer })} disabled={busy}
                                className="mt-2.5 h-9 px-4 rounded-md border border-border-strong bg-surface text-fg text-[13px] font-semibold hover:bg-bg-warm disabled:opacity-60">
                                Re-sign — as {signer}</button>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="text-[13px] font-semibold text-fg">Signed ✓</p>
                            <p className="text-[12px] text-fg-tertiary mt-0.5">
                                by {p.signed_by} on {p.signed_at} · <button onClick={() => setHistOpen(true)}
                                    className="text-link font-semibold">history →</button></p>
                        </>
                    )}
                </div>
            </div>

            {/* IDENTITY behind its own fold (6 Aug lanes): the head SHOWS
                who the bank is; the five identity fields are edited here —
                same door as before (suggest → review), nothing new. */}
            {idOpen && (
                <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs py-1 mt-4">
                    {IDENTITY_KEYS.map(k => {
                        const f = contract.find(c => c.key === k);
                        if (!f) return null;
                        const a = apprBy.get(k);
                        const n = candCount.get(k) || 0;
                        return (
                            <div key={k} className="flex gap-3 items-baseline px-4 py-2 text-[13px]">
                                <span className="text-[12.5px] text-fg-secondary w-40 shrink-0"
                                    title={f.formal_term || f.label}>{f.label}</span>
                                {a ? (
                                    <span className="font-semibold text-fg min-w-0 truncate">
                                        {k === 'lei' ? <span className="font-mono text-[11.5px]">{a.value}</span>
                                            : prettyValue(k, a.value)}</span>
                                ) : (
                                    <span className="text-[12px] text-border-strong">— not answered{n > 0 && <> · {n} waiting</>}</span>
                                )}
                                <button onClick={() => openField(k, 'suggest')}
                                    className="ml-auto text-[11px] font-semibold text-link whitespace-nowrap">
                                    {a ? 'change →' : 'answer →'}</button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* the notice line: what waits, what stands */}
            <div className="flex gap-4 items-center flex-wrap text-[12.5px] border-y border-divider py-2 mt-4 mb-6">
                {waitingN > 0
                    ? <span className="text-fg-secondary"><b className="text-fg">{waitingN} waiting for review</b> — counts
                        as nothing until you approve it · <a href="#waiting" className="text-link font-semibold">go →</a></span>
                    : <span className="text-fg-tertiary">Nothing waiting for review</span>}
                {/* ONE profile, one count (Kawi, 20 Jul: no 9-frame) */}
                <span className="ml-auto text-fg-tertiary">{answered + answeredContext} of {totalSwitches + contextFields.length} facts answered</span>
            </div>

            {/* the search — a tool behind a line, full card only when opened (or day zero) */}
            {/* the deep search announces itself even after the search card
                closed (Kawi, 20 Jul: the Klarna silence) — page-level, always */}
            {deepFor && !showSearch && (
                <div className="flex items-center gap-2.5 mb-4 px-4 py-2.5 rounded-lg border border-border-default bg-surface-sunken text-fg-secondary text-[12.5px]">
                    <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse shrink-0" />
                    <span>Searching the public record for <b>{deepFor}</b> — a few minutes.
                        Findings land in the waiting card below, each for your approval.</span>
                </div>
            )}
            {!showSearch && notes.map((n, i) => <p key={i} className="text-[11px] text-warning-ink mb-2">{n}</p>)}
            {showSearch && (
                <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs px-3.5 py-3 mb-6">
                    <div className="flex gap-2 items-center flex-wrap">
                        <input value={idQ} onChange={e => setIdQ(e.target.value)}
                            placeholder="Bank name or organisation number…"
                            className="text-[13px] h-9 px-3 w-72 border border-border-strong rounded-md bg-surface text-fg" />
                        <button disabled={dBusy || !!deepFor || !idQ.trim()} onClick={identifyRun}
                            className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                            {dBusy ? 'Looking…' : 'Find the bank →'}</button>
                        <span className="text-[11px] text-fg-tertiary">BARK finds the bank, you confirm it, then the deep search runs · nothing counts until you approve it</span>
                        <button onClick={() => setSearchOpen(false)}
                            className="ml-auto text-[11px] text-fg-tertiary hover:underline">close ✕</button>
                    </div>
                    {idMatches && idMatches.length > 0 && (
                        <div className="mt-3 divide-y divide-divider border-t border-divider">
                            <div className="flex justify-end pt-2">
                                {/* a suggestion must be dismissable (Kawi, 17 Jul:
                                    "i cant cancel the suggestion") — closing stores nothing */}
                                <button onClick={() => setIdMatches(null)}
                                    className="text-[11px] text-fg-tertiary hover:underline">
                                    Not my bank — close ✕</button>
                            </div>
                            {idMatches.map((m, i) => (
                                <div key={i} className="flex justify-between items-center gap-4 py-2.5 text-[13px]">
                                    <span className="min-w-0">
                                        <span className="font-medium text-fg">{m.legal_name}</span>
                                        {m.summary && <span className="text-fg-secondary"> · {m.summary}</span>}
                                        <span className="block text-[11px] text-fg-tertiary truncate">
                                            {[m.home_member_state && prettyValue('home_member_state', m.home_member_state),
                                              m.competent_authority, m.lei && `LEI ${m.lei}`]
                                                .filter(Boolean).join(' · ')}
                                            {m.source_url && <> · <a href={m.source_url} target="_blank" rel="noreferrer"
                                                className="text-link hover:underline">source →</a></>}
                                        </span>
                                    </span>
                                    <button onClick={() => confirmBank(m)}
                                        className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover whitespace-nowrap">
                                        This is my bank →</button>
                                </div>
                            ))}
                        </div>
                    )}
                    {deepFor && <p className="text-[11px] text-fg-tertiary mt-2">
                        Searching the public record for {deepFor} — this can take a couple of minutes.
                        Findings appear in the waiting card when the search finishes; each needs your approval.</p>}
                    {notes.map((n, i) => <p key={i} className="text-[11px] text-warning-ink mt-2">{n}</p>)}
                    {dErr && <p className="text-[11px] text-warning-ink mt-2">{dErr}</p>}
                </div>
            )}

            {/* THE FACT LANES — every contract field on the page, grouped by
                what it says (mock blessed 6 Aug — Kawi: "there should be more
                than 9!"). Supersedes the nine-only wall; identity lives in
                the head. Unnamed contract keys fall into a last lane — a new
                field can never vanish. */}
            {(() => {
                const placed = new Set<string>([...FACT_LANES.flatMap(l => l.keys), ...IDENTITY_KEYS]);
                const leftovers = contract.map(f => f.key).filter(k => !placed.has(k));
                const lanes = leftovers.length
                    ? [...FACT_LANES, { title: 'More', keys: leftovers }]
                    : FACT_LANES;
                return lanes.map((lane, li) => {
                    const nAns = lane.keys.filter(k =>
                        Object.prototype.hasOwnProperty.call(p.facts, k) ? !!p.facts[k] : apprBy.has(k)).length;
                    return (
                        <div key={lane.title} className={li > 0 ? 'mt-6' : ''}>
                            <LaneLabel right={`${nAns} of ${lane.keys.length} answered`}>
                                {lane.title}
                                {lane.sub && <span className="normal-case tracking-normal font-medium"> — {lane.sub}</span>}
                            </LaneLabel>
                            <div className="grid grid-cols-3 border border-border-default rounded-lg bg-surface shadow-bark-xs overflow-hidden">
                                {lane.keys.map((k, i) => cellFor(k, i, 3))}
                            </div>
                        </div>
                    );
                });
            })()}
            <p className="text-[11px] text-fg-tertiary mt-2">
                Who confirmed what, when, and from which source — all of it lives in{' '}
                <button onClick={() => setHistOpen(true)} className="text-link font-semibold">history →</button>
            </p>
            {fsStatus && (
                <div className={`flex items-center gap-2.5 mt-3 px-4 py-2.5 rounded-lg border text-[12.5px] ${
                    fsStatus.phase === 'searching' ? 'border-border-default bg-surface-sunken text-fg-secondary'
                        : fsStatus.phase === 'found' ? 'border-success/40 bg-success/5 text-fg'
                        : fsStatus.phase === 'error' ? 'border-danger/40 bg-danger/5 text-warning-ink'
                        : 'border-border-default bg-surface-sunken text-fg-secondary'}`}>
                    {fsStatus.phase === 'searching' ? (
                        <>
                            <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse shrink-0" />
                            <span>Searching the public record for <b>{fsStatus.label}</b> — usually a few seconds.</span>
                        </>
                    ) : fsStatus.phase === 'found' ? (
                        <>
                            <span className="text-success font-bold shrink-0">✓</span>
                            <span>Found <b>{fsStatus.n}</b> for <b>{fsStatus.label}</b> — in the waiting card below, for your approval.</span>
                            <button onClick={() => setFsStatus(null)}
                                className="ml-auto text-[11px] text-fg-tertiary hover:underline shrink-0">dismiss ✕</button>
                        </>
                    ) : fsStatus.phase === 'known' ? (
                        <>
                            <span>Found <b>{fsStatus.label}</b>, but it matches what is already answered — no new card.</span>
                            <button onClick={() => setFsStatus(null)}
                                className="ml-auto text-[11px] text-fg-tertiary hover:underline shrink-0">dismiss ✕</button>
                        </>
                    ) : fsStatus.phase === 'off_vocab' ? (
                        <>
                            <span>Found a value for <b>{fsStatus.label}</b>, but it is not one of the allowed answers — nothing added.</span>
                            <button onClick={() => setFsStatus(null)}
                                className="ml-auto text-[11px] text-fg-tertiary hover:underline shrink-0">dismiss ✕</button>
                        </>
                    ) : fsStatus.phase === 'none' ? (
                        <>
                            <span>No public source for <b>{fsStatus.label}</b> — it stays manual entry.</span>
                            <button onClick={() => setFsStatus(null)}
                                className="ml-auto text-[11px] text-fg-tertiary hover:underline shrink-0">dismiss ✕</button>
                        </>
                    ) : (
                        <>
                            <span>Search failed for <b>{fsStatus.label}</b>: {fsStatus.msg}</span>
                            <button onClick={() => setFsStatus(null)}
                                className="ml-auto text-[11px] text-fg-tertiary hover:underline shrink-0">dismiss ✕</button>
                        </>
                    )}
                </div>
            )}

            {/* THE WAITING WORK — the card exists only when something waits
                (Kawi, 17 Jul: the top line already says when it is empty) */}
            {waitingN > 0 && (
                <div id="waiting" className="border border-border-default rounded-lg bg-surface shadow-bark-xs mt-6">
                    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-divider">
                        <span className="text-[11px] font-semibold tracking-[0.07em] uppercase text-fg-tertiary">Waiting for review</span>
                        <span className="font-mono text-[11px] text-fg-tertiary">{waitingN}</span>
                        <span className="ml-auto text-[11px] text-fg-tertiary">counts as nothing until approved</span>
                    </div>
                    <div className="divide-y divide-divider">
                        {(queue?.candidates || []).map(f => {
                            const now = Object.prototype.hasOwnProperty.call(p.facts, f.key)
                                ? p.facts[f.key] : apprBy.get(f.key)?.value;
                            const def = contract.find(c => c.key === f.key);
                            return (
                                <React.Fragment key={f.fact_id}>
                                    <div className="px-4 py-3">
                                        <div className="flex gap-2 items-baseline flex-wrap text-[13px]">
                                            <span className="font-semibold text-fg">{f.label || f.key}</span>
                                            <span className="text-fg-tertiary">→</span>
                                            <span className="font-semibold text-fg">{f.value}</span>
                                            <span className="text-[11.5px] text-fg-secondary">now: {now || '— not answered'}
                                                {def && def.allowed_values.length > 0 && (
                                                    <> · one of <span className="font-mono">{def.allowed_values.join('|')}</span></>)}</span>
                                        </div>
                                        <p className="text-[11.5px] text-fg-tertiary mt-1">
                                            {f.quote && <>“{f.quote}” · </>}
                                            {f.source_url
                                                ? <a href={f.source_url} target="_blank" rel="noreferrer"
                                                    className="text-link hover:underline font-semibold">source →</a>
                                                : <>via {f.door === 'manual' ? 'hand' : f.door}</>}
                                            {queue?.conflicting_keys.includes(f.key) && (
                                                <span className="font-semibold text-warning-ink"> · conflicting</span>)}
                                            {offList(f.key, f.value) && (
                                                <span className="font-semibold text-warning-ink"> · not one of the allowed answers</span>)}
                                            {f.door === 'manual' && !f.source_url && (
                                                <span className="font-semibold text-warning-ink"> · no source given</span>)}
                                        </p>
                                        {isSigner ? (<div className="flex gap-3 items-center mt-2">
                                            <button onClick={() => review(f.fact_id, 'approve')}
                                                className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover">
                                                Approve — as {signer}</button>
                                            <button onClick={() => setRejectFor(rejectFor === f.fact_id ? null : f.fact_id)}
                                                className="text-[11px] text-fg-tertiary hover:underline">reject</button>
                                        </div>) : <WaitingForSigner />}
                                        {reviewErr?.id === f.fact_id && (
                                            <p className="mt-1.5 px-2.5 py-1.5 rounded-md border border-danger/40 bg-danger/5 text-[11px] text-warning-ink">
                                                {reviewErr.msg}</p>
                                        )}
                                    </div>
                                    {rejectFor === f.fact_id && (
                                        <div className="px-4 py-2.5 bg-bg-warm flex gap-2 items-center">
                                            <input value={rejectNote} onChange={e => setRejectNote(e.target.value)}
                                                placeholder="reason — required" autoFocus
                                                className={`text-[11px] border rounded-md px-2.5 h-7 w-64 bg-surface text-fg ${
                                                    rejectNote.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                            <button disabled={!rejectNote.trim()}
                                                onClick={() => review(f.fact_id, 'reject', rejectNote.trim())}
                                                className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                                                Reject — as {signer}</button>
                                            <button onClick={() => { setRejectFor(null); setRejectNote(''); }}
                                                className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {entCands.map(e => (
                            <React.Fragment key={e.entity_id}>
                                <div className="px-4 py-3">
                                    <div className="flex gap-2 items-baseline flex-wrap text-[13px]">
                                        <span className="font-semibold text-fg">Group: {e.name}</span>
                                        <span className="text-fg-tertiary">→</span>
                                        <span className="font-semibold text-fg">{ENTITY_KIND_WORDS[e.kind] || e.kind}{e.parent_name ? ` of ${e.parent_name}` : ''}</span>
                                    </div>
                                    <p className="text-[11.5px] text-fg-tertiary mt-1">
                                        {e.quote && <>“{e.quote}” · </>}
                                        {e.source_url
                                            ? <a href={e.source_url} target="_blank" rel="noreferrer"
                                                className="text-link hover:underline font-semibold">source →</a>
                                            : <>via {e.door === 'manual' ? 'hand' : e.door}</>}
                                    </p>
                                    {isSigner ? (<div className="flex gap-3 items-center mt-2">
                                        <button onClick={() => reviewEntity(e.entity_id, 'approve')}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover">
                                            Approve — as {signer}</button>
                                        <button onClick={() => setRejectFor(rejectFor === e.entity_id ? null : e.entity_id)}
                                            className="text-[11px] text-fg-tertiary hover:underline">reject</button>
                                    </div>) : <WaitingForSigner />}
                                </div>
                                {rejectFor === e.entity_id && (
                                    <div className="px-4 py-2.5 bg-bg-warm flex gap-2 items-center">
                                        <input value={rejectNote} onChange={e2 => setRejectNote(e2.target.value)}
                                            placeholder="reason — required" autoFocus
                                            className={`text-[11px] border rounded-md px-2.5 h-7 w-64 bg-surface text-fg ${
                                                rejectNote.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                        <button disabled={!rejectNote.trim()}
                                            onClick={() => reviewEntity(e.entity_id, 'reject', rejectNote.trim())}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                                            Reject — as {signer}</button>
                                        <button onClick={() => { setRejectFor(null); setRejectNote(''); }}
                                            className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                        {linkCands.map(l => (
                            <React.Fragment key={l.link_id}>
                                <div className="px-4 py-3">
                                    <div className="flex gap-2 items-baseline flex-wrap text-[13px]">
                                        <span className="font-semibold text-fg">Relation: {l.from_name}</span>
                                        <span className="text-fg-tertiary">—{l.rel_type}→</span>
                                        <span className="font-semibold text-fg">{l.to_name}</span>
                                        {l.amount && <span className="text-[11.5px] text-fg-secondary">{l.amount}</span>}
                                    </div>
                                    <p className="text-[11.5px] text-fg-tertiary mt-1">
                                        {l.description && <>{l.description} · </>}
                                        {l.quote && <>“{l.quote}” · </>}
                                        {l.source_url
                                            ? <a href={l.source_url} target="_blank" rel="noreferrer"
                                                className="text-link hover:underline font-semibold">source →</a>
                                            : <>via {l.door === 'manual' ? 'hand' : l.door}</>}
                                    </p>
                                    {isSigner ? (<div className="flex gap-3 items-center mt-2">
                                        <button onClick={() => reviewAt(`/api/inventory/profile/group/links/${l.link_id}/review`, 'approve')}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover">
                                            Approve — as {signer}</button>
                                        <button onClick={() => setRejectFor(rejectFor === l.link_id ? null : l.link_id)}
                                            className="text-[11px] text-fg-tertiary hover:underline">reject</button>
                                    </div>) : <WaitingForSigner />}
                                </div>
                                {rejectFor === l.link_id && (
                                    <div className="px-4 py-2.5 bg-bg-warm flex gap-2 items-center">
                                        <input value={rejectNote} onChange={e2 => setRejectNote(e2.target.value)}
                                            placeholder="reason — required" autoFocus
                                            className={`text-[11px] border rounded-md px-2.5 h-7 w-64 bg-surface text-fg ${
                                                rejectNote.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                        <button disabled={!rejectNote.trim()}
                                            onClick={() => reviewAt(`/api/inventory/profile/group/links/${l.link_id}/review`, 'reject', rejectNote.trim())}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                                            Reject — as {signer}</button>
                                        <button onClick={() => { setRejectFor(null); setRejectNote(''); }}
                                            className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                        {efactCands.map(f => (
                            <React.Fragment key={f.efact_id}>
                                <div className="px-4 py-3">
                                    <div className="flex gap-2 items-baseline flex-wrap text-[13px]">
                                        <span className="font-semibold text-fg">{f.entity_name}</span>
                                        <span className="text-fg-tertiary">· {f.key.replace(/_/g, ' ')} →</span>
                                        <span className="font-semibold text-fg">{f.value}</span>
                                    </div>
                                    <p className="text-[11.5px] text-fg-tertiary mt-1">
                                        {f.quote && <>“{f.quote}” · </>}
                                        {f.source_url
                                            ? <a href={f.source_url} target="_blank" rel="noreferrer"
                                                className="text-link hover:underline font-semibold">source →</a>
                                            : <>via {f.door === 'manual' ? 'hand' : f.door}</>}
                                    </p>
                                    {isSigner ? (<div className="flex gap-3 items-center mt-2">
                                        <button onClick={() => reviewAt(`/api/inventory/profile/group/facts/${f.efact_id}/review`, 'approve')}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover">
                                            Approve — as {signer}</button>
                                        <button onClick={() => setRejectFor(rejectFor === f.efact_id ? null : f.efact_id)}
                                            className="text-[11px] text-fg-tertiary hover:underline">reject</button>
                                    </div>) : <WaitingForSigner />}
                                </div>
                                {rejectFor === f.efact_id && (
                                    <div className="px-4 py-2.5 bg-bg-warm flex gap-2 items-center">
                                        <input value={rejectNote} onChange={e2 => setRejectNote(e2.target.value)}
                                            placeholder="reason — required" autoFocus
                                            className={`text-[11px] border rounded-md px-2.5 h-7 w-64 bg-surface text-fg ${
                                                rejectNote.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                        <button disabled={!rejectNote.trim()}
                                            onClick={() => reviewAt(`/api/inventory/profile/group/facts/${f.efact_id}/review`, 'reject', rejectNote.trim())}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                                            Reject — as {signer}</button>
                                        <button onClick={() => { setRejectFor(null); setRejectNote(''); }}
                                            className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                        {/* structure changes (Kawi, 20 Jul): rename / kind /
                            parent / shares — approving lands the change and
                            flags every obligation bound to the entity */}
                        {changeCands.map(c => (
                            <React.Fragment key={c.change_id}>
                                <div className="px-4 py-3">
                                    <div className="flex gap-2 items-baseline flex-wrap text-[13px]">
                                        <span className="font-semibold text-fg">{c.entity_name}</span>
                                        <span className="text-fg-tertiary">· change {c.field.replace(/_/g, ' ')} →</span>
                                        <span className="font-semibold text-fg">{c.value_shown || c.value}</span>
                                    </div>
                                    <p className="text-[11.5px] text-fg-tertiary mt-1">
                                        {c.quote && <>“{c.quote}” · </>}
                                        {c.source_url
                                            ? <a href={c.source_url} target="_blank" rel="noreferrer"
                                                className="text-link hover:underline font-semibold">source →</a>
                                            : <>via {c.door === 'manual' ? 'hand' : c.door}</>}
                                        {' '}· approving flags every obligation bound to this entity
                                    </p>
                                    {isSigner ? (<div className="flex gap-3 items-center mt-2">
                                        <button onClick={() => reviewAt(`/api/inventory/profile/group/changes/${c.change_id}/review`, 'approve')}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover">
                                            Approve — as {signer}</button>
                                        <button onClick={() => setRejectFor(rejectFor === c.change_id ? null : c.change_id)}
                                            className="text-[11px] text-fg-tertiary hover:underline">reject</button>
                                    </div>) : <WaitingForSigner />}
                                </div>
                                {rejectFor === c.change_id && (
                                    <div className="px-4 py-2.5 bg-bg-warm flex gap-2 items-center">
                                        <input value={rejectNote} onChange={e2 => setRejectNote(e2.target.value)}
                                            placeholder="reason — required" autoFocus
                                            className={`text-[11px] border rounded-md px-2.5 h-7 w-64 bg-surface text-fg ${
                                                rejectNote.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                        <button disabled={!rejectNote.trim()}
                                            onClick={() => reviewAt(`/api/inventory/profile/group/changes/${c.change_id}/review`, 'reject', rejectNote.trim())}
                                            className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                                            Reject — as {signer}</button>
                                        <button onClick={() => { setRejectFor(null); setRejectNote(''); }}
                                            className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    {dErr && !showSearch && <p className="text-[11px] text-warning-ink px-4 pb-2.5">{dErr}</p>}
                </div>
            )}

            {/* THE GROUP — full width; tree + chart together, expand to chart only */}
            <div className="mt-6" id="profile-group-section">
                <GroupSection signer={signer} ents={groupSt?.entities || []}
                    links={groupSt?.links || []} efacts={groupSt?.entity_facts || []} bankName={legalName}
                    selfIds={[apprBy.get('legal_name')?.value, apprBy.get('lei')?.value]
                        .filter((v): v is string => !!v)}
                    onChanged={() => setReload(x => x + 1)} />
            </div>

            {err && <div className="mt-2 text-[11px] text-warning-ink">{err}</div>}

            {/* SIGNERS OF THIS BANK (roles, 20 Jul) — signers only; every
                list change is a record like everything else */}
            {isSigner && p?.id && (
                <>
                    <LaneLabel right="who may approve, reject and sign — for this bank">Signers of this bank</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs mb-8">
                        {signersList === null && (
                            <button onClick={() => {
                                setSignerErr('');
                                get<{ signers: string[] }>(`/api/inventory/institutions/${p.id}/signers`)
                                    .then(d => setSignersList(d.signers)).catch(e2 => setSignerErr(String(e2)));
                            }} className="px-4 py-3 text-[12px] text-link font-semibold">show the list →</button>
                        )}
                        {signersList !== null && (
                            <>
                                {signersList.length === 0 && (
                                    <p className="px-4 py-3 text-[12.5px] text-fg-secondary">
                                        No named signers for this bank yet — the global BARK list still signs.</p>
                                )}
                                {signersList.map(e2 => (
                                    <div key={e2} className="flex items-center gap-3 px-4 py-2.5 text-[13px] border-b border-divider last:border-b-0">
                                        <span className="font-medium text-fg">{e2}</span>
                                    </div>
                                ))}
                                {/* READ-ONLY (Kawi, 20 Jul: "thats not how we
                                    decide") — who signs is governed from BARK
                                    admin, never self-served on the profile */}
                                <p className="px-4 py-2.5 border-t border-divider text-[11.5px] text-fg-tertiary">
                                    Changing who signs is managed in BARK admin — not here.</p>
                            </>
                        )}
                        {signerErr && <p className="px-4 pb-2.5 text-[11px] text-warning-ink">{signerErr}</p>}
                    </div>
                </>
            )}

            {/* THE ANSWER WINDOW — every answer opens on top, with Cancel
                (cancel-everywhere; profile-C). Confirm counts immediately as a
                signed record; a typed context value files for review. */}
            {fieldModal && (() => {
                const def = modalDef;
                const allowed = def && def.allowed_values.length > 0 ? def.allowed_values : null;
                const multi = !!def?.multi_value;
                // ";"-separated: the ruled Annex I heads carry commas inside the words
                const parts = val.split(';').map(s => s.trim()).filter(Boolean);
                const togglePick = (c: string) => setVal(
                    (parts.includes(c) ? parts.filter(x => x !== c) : [...parts, c]).join('; '));
                const label = def?.label || fieldModal.key;
                return (
                    <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8">
                        <div className={`bg-surface rounded-lg shadow-bark-md border border-border-default w-full mt-16 ${
                            allowed && multi && allowed.length > 6 ? 'max-w-lg' : 'max-w-md'}`}>
                            <div className="px-5 py-4 border-b border-divider">
                                <p className="text-[15px] font-semibold text-fg">{label}</p>
                                {def?.description && <p className="text-[12px] text-fg-secondary mt-1">{def.description}</p>}
                                {def?.legal_source && (
                                    <p className="text-[11px] text-fg-tertiary mt-1">Legal anchor: {def.legal_source}</p>
                                )}
                                <p className="text-[11px] text-fg-tertiary mt-1">
                                    {fieldModal.mode === 'confirm'
                                        ? 'Confirming counts immediately, as a signed record.'
                                        : 'A typed value waits in the review queue — it counts as nothing until approved.'}
                                </p>
                            </div>
                            <div className="px-5 py-4">
                                {allowed && multi ? (
                                    /* one calm column (the blessed picture, 17 Jul):
                                       box first, hanging indent, quiet tint on picked,
                                       a picked-count line — no pills */
                                    <>
                                        <div className="-mx-2 max-h-[430px] overflow-y-auto">
                                            {allowed.map(c => {
                                                /* ruled labels (17 Jul): the short word leads,
                                                   the full legal name stays underneath — the
                                                   stored value is ALWAYS the legal word */
                                                const short = def?.display_labels?.[c] || c;
                                                const note = def?.value_notes?.[c];
                                                return (
                                                    <label key={c}
                                                        className={`grid grid-cols-[16px_1fr] gap-2.5 items-start px-3 py-[7px] rounded-md cursor-pointer text-[12.5px] leading-snug text-fg ${
                                                            parts.includes(c) ? 'bg-primary-tint' : 'hover:bg-surface-sunken'}`}>
                                                        <input type="checkbox" className="mt-0.5"
                                                            checked={parts.includes(c)} onChange={() => togglePick(c)} />
                                                        <span>{short}
                                                            {short !== c && (
                                                                <span className="block text-[10.5px] text-fg-tertiary leading-snug">{c}</span>
                                                            )}
                                                            {note && (
                                                                <span className="block text-[10.5px] text-fg-tertiary leading-snug">{note}</span>
                                                            )}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[11px] text-fg-tertiary mt-2">
                                            {parts.length} of {allowed.length} picked</p>
                                    </>
                                ) : allowed ? (
                                    <select value={val} onChange={e => setVal(e.target.value)} autoFocus
                                        className="text-[13px] h-9 px-2.5 w-full border border-border-strong rounded-md bg-surface text-fg">
                                        <option value="" disabled>choose…</option>
                                        {allowed.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                ) : (
                                    <input value={val} onChange={e => setVal(e.target.value)} autoFocus
                                        className="text-[13px] h-9 px-3 w-full border border-border-strong rounded-md bg-surface text-fg" />
                                )}
                            </div>
                            <div className="px-5 pb-3.5 border-t border-divider pt-3">
                                <p className="text-[11px] text-fg-tertiary mb-1.5">
                                    Or let the search propose this one field — found answers wait for your approval:</p>
                                <div className="flex gap-2 items-center">
                                    <input value={fsName} onChange={e => setFsName(e.target.value)}
                                        placeholder="Bank name…"
                                        className="text-[12px] h-8 px-2.5 flex-1 border border-border-strong rounded-md bg-surface text-fg" />
                                    <button disabled={fsBusy || !fsName.trim()}
                                        onClick={() => searchField(fieldModal.key)}
                                        className="text-[11px] font-semibold text-link whitespace-nowrap disabled:opacity-50">
                                        {fsBusy ? 'Searching…' : 'Search this field →'}</button>
                                </div>
                            </div>
                            {modalErr && (
                                <p className="mx-5 mb-1 px-3 py-2 rounded-md border border-danger/40 bg-danger/5 text-[12px] text-warning-ink">
                                    {modalErr}</p>
                            )}
                            <div className="px-5 py-3.5 border-t border-divider flex gap-2 justify-end">
                                <button onClick={() => { setFieldModal(null); setVal(''); setModalErr(undefined); }}
                                    className="h-8 px-3.5 rounded-md border border-border-strong bg-surface text-fg text-[13px] font-semibold hover:bg-bg-warm">
                                    Cancel</button>
                                <button disabled={busy || !val.trim()}
                                    onClick={() => fieldModal.mode === 'confirm'
                                        ? post('/api/inventory/profile/facts', { key: fieldModal.key, value: val, by: signer })
                                        : suggest(fieldModal.key, val)}
                                    className="h-8 px-3.5 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                    {fieldModal.mode === 'confirm' ? `Confirm — as ${signer}` : 'Add for review'}</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* HISTORY — behind a link, never on the page (Kawi, 17 Jul) */}
            {histOpen && (
                <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8">
                    <div className="bg-surface rounded-lg shadow-bark-md border border-border-default w-full max-w-2xl mt-16">
                        <div className="flex items-center px-5 py-4 border-b border-divider">
                            <p className="text-[15px] font-semibold text-fg">History</p>
                            <span className="ml-3 text-[11px] text-fg-tertiary">who confirmed what, when, from which source</span>
                            <button onClick={() => setHistOpen(false)}
                                className="ml-auto text-[11px] text-fg-tertiary hover:underline">close ✕</button>
                        </div>
                        <div className="divide-y divide-divider max-h-[60vh] overflow-y-auto">
                            {histItems.length === 0 && (
                                <p className="text-[12px] text-fg-tertiary px-5 py-3">Nothing on record yet.</p>)}
                            {histItems.map(h => (
                                <div key={`${h.id}-${h.at || ''}`} className="flex justify-between items-baseline gap-4 px-5 py-2.5 text-[13px]">
                                    <span className="text-fg-secondary min-w-0">
                                        <b className="text-fg">{h.outcome}</b> · {h.what}</span>
                                    <span className="text-[11px] text-fg-tertiary whitespace-nowrap">{h.by} · {(h.at || '').slice(0, 10)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Wrap>
    );
}

/* ---------- the group structure — entities and ownership, as a graph ---------- */

type GroupEntityRow = {
    entity_id: string; name: string; kind: string; status: string;
    lei?: string | null; jurisdiction?: string | null; regulator?: string | null;
    legal_form?: string | null; share_pct?: number | null; voting_pct?: number | null;
    parent_name?: string | null; parent_id?: string | null;
    source_url?: string | null; quote?: string | null; door?: string;
    bound_obligations?: number;
};
type GroupLinkRow = {
    link_id: string; rel_type: string; description?: string | null;
    amount?: string | null; status: string; source_url?: string | null;
    quote?: string | null; door?: string;
    from_id: string; from_name: string; to_id: string; to_name: string;
};
type EntityFactRow = {
    efact_id: string; key: string; value: string; status: string;
    source_url?: string | null; quote?: string | null; door?: string;
    entity_id: string; entity_name: string;
};
type EntityChangeRow = {
    change_id: string; field: string; value: string; value_shown?: string;
    status: string; source_url?: string | null; quote?: string | null; door?: string;
    entity_id: string; entity_name: string;
};
type GroupState = {
    entities: GroupEntityRow[]; links?: GroupLinkRow[]; entity_facts?: EntityFactRow[];
    changes?: EntityChangeRow[];
};

/* display words — machine values never render raw (word law) */
/* "parent", not "parent company" (Kawi, 17 Jul): the position is the same
   whether the owner is a company, a foundation or an association */
const ENTITY_KIND_WORDS: Record<string, string> = {
    // 'group' and 'regulated_entity' are retired vocabulary (21/17 Jul) —
    // the words stay here only so LEGACY stored rows render readably
    group: 'group', parent: 'parent', regulated_entity: 'regulated entity',
    subsidiary: 'subsidiary', branch: 'branch',
};
/* kind is the POSITION in the ownership only (Kawi, 17 Jul: a full map of
   the group, not the tenant's view). Supervised = the regulator field;
   the tenant's own box is MATCHED from the profile, never chosen.
   'group' retired 21 Jul: a group is a subtree, never a box. */
const ENTITY_KINDS_UI = ['parent', 'subsidiary', 'branch'];
/* kind = border colour on the drawn chart; tokens only */
const ENTITY_KIND_STROKE: Record<string, string> = {
    subsidiary: 'var(--link)', branch: 'var(--accent-copper)',
    parent: 'var(--border-strong)', group: 'var(--border-strong)',
};

/* Kawi, 17 Jul: "we need a place to handle group structures. a field is not
   the solution... should be a graph structure." Entities are nodes, ownership
   is an edge; candidates render grey and count as nothing until a named
   signer approves (the same trust rules as facts). */
function GroupSection({ signer, ents, links, efacts, bankName, selfIds, onChanged }: {
    signer: string; ents: GroupEntityRow[]; links: GroupLinkRow[];
    efacts?: EntityFactRow[]; bankName: string;
    selfIds: string[]; onChanged: () => void;
}) {
    /* the role decides the window's primary act (Kawi, 20 Jul roles) */
    const { is_signer: isSigner } = React.useContext(MeContext);
    /* which box is the tenant — matched from the profile's approved
       identity (legal name, LEI), never a picked word */
    const isSelf = (e: GroupEntityRow) =>
        selfIds.includes(e.name) || (!!e.lei && selfIds.includes(e.lei));
    /* the group is the PICTURE on the profile (profile-C) — review happens
       in the waiting card; this card shows, searches, adds and ACTIVATES.
       Default = tree AND chart together; expand = chart alone, full width
       (Kawi, 17 Jul). */
    const [expand, setExpand] = useState(false);
    const [zoom, setZoom] = useState<number | null>(null);   // null = fit the card
    /* the bank's own chart (Kawi, 21 Jul: "the internal org chart...save a
       place for that so that you can upload") — a register entry with the
       role 'internal org chart'; the strip below the group shows it */
    const OWN_CHART_ROLE = 'internal org chart';
    const [ownCharts, setOwnCharts] = useState<{ id: string; name: string }[] | null>(null);
    const [ocOpen, setOcOpen] = useState(false);
    const [ocName, setOcName] = useState('Internal organisation chart');
    const [ocFile, setOcFile] = useState<File | null>(null);
    const [ocBusy, setOcBusy] = useState(false);
    useEffect(() => {
        get<{ artifacts: (ArtifactRow & { role?: string | null })[] }>('/api/inventory/artifacts')
            .then(d => setOwnCharts(d.artifacts
                .filter(a => a.data_origin === 'real' && a.role === OWN_CHART_ROLE)
                .map(a => ({ id: a.id, name: a.name }))))
            .catch(() => setOwnCharts([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /* the prudential-scope lens (Kawi, 21 Jul — "prudential scope" ruled,
       CRR's own phrase): one chart, two views. The scope is the approved
       consolidation_scope entity fact; excluded/insurance dim out. */
    const [lens, setLens] = useState<'legal' | 'prudential'>('legal');
    /* tick-box scope (Kawi, 22 Jul: "tick boxes when prudential scope is
       selected...build!") — ticks stage locally; ONE signed apply files and
       approves every change through the same road as the change window */
    const [staged, setStaged] = useState<Record<string, 'prudential' | 'excluded'>>({});
    const [applyBusy, setApplyBusy] = useState(false);
    const [applyErr, setApplyErr] = useState<string>();
    const stagedN = Object.keys(staged).length;
    const applyScopes = async () => {
        setApplyBusy(true); setApplyErr(undefined);
        try {
            const r = await fetch(`${API}/api/inventory/profile/group/scope/apply`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    changes: Object.entries(staged).map(([entity_id, scope]) => ({ entity_id, scope })),
                    by: signer,
                }),
            });
            if (!r.ok) throw new Error(`${r.status}`);
            const d = await r.json();
            if ((d.failed || []).length > 0)
                setApplyErr(`${d.failed.length} tick(s) did not land — ${d.failed[0].why}`);
            setStaged({}); onChanged();
        } catch (e) { setApplyErr(`Signing failed (${String(e)}). Nothing was recorded.`); }
        setApplyBusy(false);
    };
    const scopeOf = new Map((efacts || [])
        .filter(f => f.key === 'consolidation_scope' && f.status === 'approved')
        .map(f => [f.entity_id, f.value]));
    const scopeWaiting = new Set((efacts || [])
        .filter(f => f.key === 'consolidation_scope' && f.status === 'candidate')
        .map(f => f.entity_id));
    const outOfScope = (e: GroupEntityRow) =>
        ['excluded', 'insurance'].includes(scopeOf.get(e.entity_id) || '');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    const [adding, setAdding] = useState(false);
    const [eName, setEName] = useState('');
    const [eKind, setEKind] = useState('subsidiary');
    const [eParent, setEParent] = useState('');
    const [eJur, setEJur] = useState('');
    const [eShare, setEShare] = useState('');
    const [eForm, setEForm] = useState('');
    /* intra-group relations (Kawi, 17 Jul: "its not only percentages") */
    const [relAdding, setRelAdding] = useState(false);
    const [rFrom, setRFrom] = useState('');
    const [rTo, setRTo] = useState('');
    const [rType, setRType] = useState('exposure');
    const [rAmount, setRAmount] = useState('');
    const [rDesc, setRDesc] = useState('');
    /* structure changes (Kawi, 20 Jul, v2 blessed): the window shows ALL the
       entity's facts with their current values — overtype what changed, only
       the edited ones file (each as its own candidate card). No mode picking. */
    const [chFor, setChFor] = useState<GroupEntityRow | null>(null);
    const [chDraft, setChDraft] = useState<Record<string, string>>({});
    const [chSource, setChSource] = useState('');
    const [chBusy, setChBusy] = useState(false);
    /* the entity's CURRENT values, in the draft's own string form — the
       baseline that decides which rows are edits */
    const chBase = (e: GroupEntityRow): Record<string, string> => ({
        name: e.name,
        kind: e.kind,
        parent: (e.parent_id || (e.parent_name && nameToId.get(e.parent_name)) || ''),
        share_pct: e.share_pct != null ? String(e.share_pct) : '',
        voting_pct: e.voting_pct != null ? String(e.voting_pct) : '',
        // the BAB PUBL case (20 Jul): garbled scan words become correctable
        legal_form: e.legal_form || '',
        jurisdiction: e.jurisdiction || '',
        regulator: e.regulator || '',
        // the scope (ruled 21 Jul) rides the same window but files as an
        // ENTITY FACT, not a structure change — split at the file acts
        consolidation_scope: scopeOf.get(e.entity_id) || '',
    });
    const chDirty = (e: GroupEntityRow): [string, string][] =>
        Object.entries(chDraft).filter(([k, v]) => {
            const base = chBase(e)[k] ?? '';
            if (v.trim() === base) return false;
            if (k === 'parent' && !v.trim()) return false;   // clearing the parent is not a change act
            if (k === 'consolidation_scope' && !v.trim()) return false;   // scope is set, never cleared
            return v.trim() !== '' || base !== '';           // '' vs unset is no edit
        });
    const approvedEnts = ents.filter(e => e.status === 'approved');
    const postJ = async (path: string, body: object): Promise<boolean> => {
        setErr(undefined);
        const r = await fetch(`${API}${path}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body),
        });
        if (!r.ok) { setErr((await r.json()).detail || `${r.status}`); return false; }
        onChanged(); return true;
    };
    const nameToId = new Map(ents.map(e => [e.name, e.entity_id]));
    const parentOf = (e: GroupEntityRow): string | null =>
        e.parent_id || (e.parent_name && nameToId.get(e.parent_name)) || null;
    const children = new Map<string, GroupEntityRow[]>();
    ents.forEach(e => {
        const p = parentOf(e) || '__root__';
        children.set(p, [...(children.get(p) || []), e]);
    });
    /* auto-mark the subtree (Kawi, 22 Jul: "when i select klarna
       everything under includes...you should auto mark") — a tick walks
       down; every row stays individually re-tickable afterwards */
    const descendantsOf = (id: string): GroupEntityRow[] => {
        const out: GroupEntityRow[] = [];
        const stack = [...(children.get(id) || [])];
        const walked = new Set<string>([id]);
        while (stack.length) {
            const n = stack.pop()!;
            if (walked.has(n.entity_id)) continue;
            walked.add(n.entity_id);
            out.push(n);
            stack.push(...(children.get(n.entity_id) || []));
        }
        return out;
    };
    const entityRow = (e: GroupEntityRow, depth: number, seen: Set<string>): React.ReactNode => {
        if (seen.has(e.entity_id)) return null;
        const nextSeen = new Set(seen); nextSeen.add(e.entity_id);
        const cand = e.status === 'candidate';
        return (
            <React.Fragment key={e.entity_id}>
                <div className={`group flex justify-between items-center gap-4 px-3.5 py-2 text-[13px] ${
                        lens === 'prudential' && outOfScope(e) && !staged[e.entity_id] ? 'opacity-40' : ''}`}
                    style={{ paddingLeft: `${14 + depth * 22}px` }}>
                    <span className="min-w-0">
                        {lens === 'prudential' && !cand && (
                            <input type="checkbox" className="accent-primary mr-2 align-middle"
                                checked={staged[e.entity_id]
                                    ? staged[e.entity_id] === 'prudential'
                                    : scopeOf.get(e.entity_id) === 'prudential'}
                                onChange={ev => {
                                    const want = (ev.target.checked ? 'prudential' : 'excluded') as 'prudential' | 'excluded';
                                    setStaged(st => {
                                        const next = { ...st };
                                        const put = (row: GroupEntityRow) => {
                                            if (row.status !== 'approved') return;
                                            const cur = scopeOf.get(row.entity_id) || '';
                                            if (cur === want) delete next[row.entity_id];
                                            else next[row.entity_id] = want;
                                        };
                                        put(e);
                                        descendantsOf(e.entity_id).forEach(put);
                                        return next;
                                    });
                                }} />
                        )}
                        <span className={cand ? 'text-fg-tertiary' : 'font-semibold text-fg'}>
                            {depth > 0 && <span className="text-border-strong">└ </span>}{e.name}
                        </span>
                        <span className="text-[11px] text-fg-tertiary"> · {ENTITY_KIND_WORDS[e.kind] || e.kind}
                            {e.share_pct != null ? ` · ${e.share_pct}%` : ''}
                            {e.legal_form ? ` · ${e.legal_form}` : ''}
                            {e.jurisdiction ? ` · ${e.jurisdiction}` : ''}{e.regulator ? ` · ${e.regulator}` : ''}
                            {/* the scope word (ruled 21 Jul) — quiet, from the
                                approved entity fact; candidates say waiting */}
                            {scopeOf.get(e.entity_id) && <> · {scopeOf.get(e.entity_id)}</>}
                            {!scopeOf.get(e.entity_id) && scopeWaiting.has(e.entity_id) && <> · scope waiting</>}
                            {isSelf(e) && <> — this institution</>}
                            {cand && <> — waiting for review, see the waiting card</>}</span>
                    </span>
                    {/* the edit act (mock blessed 20 Jul): approved boxes only —
                        a candidate is reviewed in the card, not changed */}
                    {!cand && (
                        <button onClick={() => { setChFor(e); setChDraft(chBase(e)); setChSource(''); }}
                            className="text-[11px] font-semibold text-link whitespace-nowrap shrink-0">
                            change →</button>
                    )}
                </div>
                {(children.get(e.entity_id) || []).map(c => entityRow(c, depth + 1, nextSeen))}
            </React.Fragment>
        );
    };

    /* the drawn chart — top-down, DESIGNED (Kawi, 17 Jul: "less system look
       and more design look"). The system look came from the layout: children
       were spread evenly per level, not under their parents, so lines
       crossed. This is a tidy tree — every parent centered over its own
       subtree, no crossings — with soft curved connectors, shadowed boxes
       and two-line labels. */
    const chart = (wide: boolean) => {
        /* orthogonal RAILS — the legal-structure chart idiom (Kawi, 17 Jul:
           lines overlapped and the space sat unused). A small family (≤3)
           sits in a row under its parent; a larger family stacks in two
           columns beside a vertical rail. Every connector stays inside its
           own family's envelope, so lines cannot cross boxes by
           construction. */
        const BW = 168, BH = 44, HGAP = 18, VGAP = 34, RAIL = 28;
        /* hidden, not greyed (Kawi, 22 Jul): in the prudential lens an
           unticked subtree leaves the chart completely. A node that is out
           itself but has in-scope descendants must STAY (the tree needs
           it) — it keeps the dashed ghost look instead. Staged ticks count
           immediately. */
        const effScope = (e: GroupEntityRow) => (staged[e.entity_id] ?? scopeOf.get(e.entity_id)) || '';
        const chartOut = (e: GroupEntityRow) => ['excluded', 'insurance'].includes(effScope(e));
        const subtreeAllOut = (e: GroupEntityRow, walked: Set<string>): boolean => {
            if (walked.has(e.entity_id)) return true;
            walked.add(e.entity_id);
            if (!chartOut(e)) return false;
            return (children.get(e.entity_id) || []).every(k => subtreeAllOut(k, walked));
        };
        const chartHidden = (e: GroupEntityRow) =>
            lens === 'prudential' && subtreeAllOut(e, new Set<string>());
        type Blk = { e: GroupEntityRow; w: number; h: number; kids: Blk[]; stacked: boolean };
        const build = (e: GroupEntityRow, seen: Set<string>): Blk => {
            if (seen.has(e.entity_id)) return { e, w: BW, h: BH, kids: [], stacked: false };
            seen.add(e.entity_id);
            const kids = (children.get(e.entity_id) || []).filter(k => !chartHidden(k)).map(k => build(k, seen));
            if (kids.length === 0) return { e, w: BW, h: BH, kids, stacked: false };
            /* expanded = the wide style (Kawi, 17 Jul): every family one row,
               the full pane width carries it; the split view stacks big
               families beside a rail to stay compact */
            if (kids.length <= 3 || wide) {
                const rowW = kids.reduce((s, k) => s + k.w, 0) + (kids.length - 1) * HGAP;
                return { e, w: Math.max(BW, rowW), h: BH + VGAP + Math.max(...kids.map(k => k.h)),
                         kids, stacked: false };
            }
            const colW = Math.max(...kids.map(k => k.w));
            let h = BH + 14;
            for (let i = 0; i < kids.length; i += 2) {
                const pair = kids.slice(i, i + 2);
                h += VGAP / 2 + Math.max(...pair.map(k => k.h));
            }
            return { e, w: Math.max(BW, colW * 2 + RAIL * 2), h, kids, stacked: true };
        };
        const pos = new Map<string, { x: number; y: number }>();   // box centres
        const rails: string[] = [];
        const place = (b: Blk, x: number, y: number) => {
            const cx = x + b.w / 2;
            pos.set(b.e.entity_id, { x: cx, y: y + BH / 2 });
            if (b.kids.length === 0) return;
            if (!b.stacked) {
                const rowW = b.kids.reduce((s, k) => s + k.w, 0) + (b.kids.length - 1) * HGAP;
                let kx = x + (b.w - rowW) / 2;
                const topY = y + BH + VGAP;
                const dist = y + BH + VGAP / 2;
                const centres: number[] = [];
                b.kids.forEach(k => { place(k, kx, topY); centres.push(kx + k.w / 2); kx += k.w + HGAP; });
                let d = `M ${cx} ${y + BH} V ${dist}`;
                if (centres.length > 1) d += ` M ${Math.min(...centres)} ${dist} H ${Math.max(...centres)}`;
                centres.forEach(c => { d += ` M ${c} ${dist} V ${topY}`; });
                rails.push(d);
                return;
            }
            const colW = Math.max(...b.kids.map(k => k.w));
            const railX = cx;
            let cy = y + BH + 14;
            let stubs = '';
            let lastMid = y + BH;
            for (let i = 0; i < b.kids.length; i += 2) {
                const pair = b.kids.slice(i, i + 2);
                cy += VGAP / 2;
                const rowH = Math.max(...pair.map(k => k.h));
                pair.forEach((k, j) => {
                    const kx = j === 0 ? railX - RAIL - colW + (colW - k.w) / 2
                                       : railX + RAIL + (colW - k.w) / 2;
                    place(k, kx, cy);
                    const mid = cy + BH / 2;
                    /* the stub meets the BOX edge, not the block edge — a
                       family block is wider than its own box (the
                       disconnected line, Kawi's screenshot 17 Jul) */
                    const boxCx = kx + k.w / 2;
                    stubs += ` M ${railX} ${mid} H ${j === 0 ? boxCx + BW / 2 : boxCx - BW / 2}`;
                    lastMid = Math.max(lastMid, mid);
                });
                cy += rowH;
            }
            rails.push(`M ${railX} ${y + BH} V ${lastMid}${stubs}`);
        };
        const seen = new Set<string>();
        const rootBlocks = (children.get('__root__') || []).filter(r => !chartHidden(r)).map(r => build(r, seen));
        let rx = 12;
        rootBlocks.forEach(b => { place(b, rx, 14); rx += b.w + HGAP * 2; });
        const W = Math.max(320, rx - HGAP * 2 + 12);
        const H = Math.max(1, ...rootBlocks.map(b => b.h)) + 28;
        return (
            <div className="relative min-w-0">
                {/* the pane fills the card's column (Kawi, 21 Jul: "we are
                    only using 1/3 of the box height" — the 440px cap sat in
                    a card that grows with the tree) and scrolls inside */}
                <div className="overflow-auto px-3.5 py-3 h-full">
                    {/* fit follows the pane: the drawing fills the width it is
                        given (small groups stay sane and centred). The EXPANDED
                        chart is the exception (Kawi, 17 Jul: fitting the wide
                        row to the pane shrank the boxes): it keeps its natural
                        size and the pane scrolls sideways — boxes stay readable
                        at any group width. */}
                    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="group chart"
                        style={zoom === null
                            ? (wide
                                ? { width: W, height: H, display: 'block' }
                                : { width: '100%', maxWidth: W * 1.2,
                                    height: 'auto', display: 'block', margin: '0 auto' })
                            : { width: W * zoom, height: H * zoom, display: 'block' }}>
                        <defs>
                            <filter id="entity-shadow" x="-20%" y="-20%" width="140%" height="160%">
                                <feDropShadow dx="0" dy="1" stdDeviation="1.2" floodColor="#0c1929" floodOpacity="0.10" />
                            </filter>
                        </defs>
                        {rails.map((d, i) => (
                            <path key={`r-${i}`} fill="none" stroke="var(--border-strong)"
                                strokeWidth={1} d={d} />
                        ))}
                        {ents.map(e => {
                            const a = pos.get(e.entity_id); if (!a) return null;
                            const cand = e.status === 'candidate';
                            const kindLine = `${ENTITY_KIND_WORDS[e.kind] || e.kind}${
                                e.jurisdiction ? ` · ${e.jurisdiction}` : ''}`;
                            /* the DISTINGUISHING part of a long name is its tail
                               ("…Fond 4"), so cut the middle, never the end —
                               and drop the (publ) noise (Kawi, 17 Jul: five
                               boxes all read "Svensk Hypotekspension …") */
                            const disp = e.name.replace(/\s*\(publ\)\s*$/i, '');
                            const label = disp.length > 26
                                ? `${disp.slice(0, 14)}…${disp.slice(-11)}` : disp;
                            /* the lens dims what sits outside the prudential
                               scope — same rule as the tree rows */
                            const dimmed = lens === 'prudential' && outOfScope(e);
                            return (
                                <g key={e.entity_id} opacity={dimmed ? 0.35 : undefined}>
                                    {/* the tooltip carries the FULL name — the box may cut
                                        it in an unlucky place (Kawi, 17 Jul) */}
                                    <title>{`${e.name} — ${ENTITY_KIND_WORDS[e.kind] || e.kind}${
                                        e.share_pct != null ? ` · owned ${e.share_pct}%` : ''}${
                                        e.legal_form ? ` · ${e.legal_form}` : ''}${
                                        e.jurisdiction ? ` · ${e.jurisdiction}` : ''}${
                                        e.regulator ? ` · supervised by ${e.regulator}` : ''}${
                                        isSelf(e) ? ' · this institution' : ''}`}</title>
                                    <rect x={a.x - BW / 2} y={a.y - BH / 2} width={BW} height={BH} rx={8}
                                        fill="var(--surface)"
                                        stroke={isSelf(e) ? 'var(--primary)'
                                            : ENTITY_KIND_STROKE[e.kind] || 'var(--border-strong)'}
                                        strokeWidth={isSelf(e) ? 1.6 : 1}
                                        strokeDasharray={cand || dimmed ? '4 3' : undefined}
                                        filter={cand || dimmed ? undefined : 'url(#entity-shadow)'} />
                                    {/* supervised = a property any box can have, shown as
                                        the small dot (the regulator is in the tooltip) */}
                                    {e.regulator && <circle cx={a.x + BW / 2 - 9} cy={a.y - BH / 2 + 9}
                                        r={3} fill="var(--primary)" />}
                                    {/* inline size/weight so the DOWNLOADED svg
                                        keeps its typography (classes don't travel) */}
                                    <text x={a.x} y={a.y - 3} textAnchor="middle"
                                        style={{ fontSize: 11, fontWeight: 600 }} fill={cand ? 'var(--fg-tertiary)' : 'var(--fg)'}>
                                        {label}
                                    </text>
                                    <text x={a.x} y={a.y + 12} textAnchor="middle"
                                        style={{ fontSize: 9.5 }} fill="var(--fg-tertiary)">
                                        {kindLine.length > 30 ? `${kindLine.slice(0, 29)}…` : kindLine}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs">
                <div className="flex gap-2 items-center flex-wrap px-4 py-2.5 border-b border-divider">
                    <span className="text-[11px] font-semibold tracking-[0.07em] uppercase text-fg-tertiary">Group</span>
                    {ents.length > 0 && (
                        <SegButtons small value={lens} onChange={v => { setLens(v as 'legal' | 'prudential'); if (v === 'legal') setStaged({}); }}
                            options={[{ key: 'legal', label: 'Legal group' },
                                      { key: 'prudential', label: 'Prudential scope' }]} />
                    )}
                    {lens === 'prudential' && stagedN === 0 && (() => {
                        const out = approvedEnts.filter(outOfScope).length;
                        const unset = approvedEnts.filter(e => !scopeOf.get(e.entity_id)).length;
                        return (
                            <span className="text-[11px] text-fg-tertiary">
                                {out === 0 && unset === 0
                                    ? 'same as the legal group — nothing excluded'
                                    : <>{out > 0 && <>{out} outside the scope</>}
                                       {out > 0 && unset > 0 && ' · '}
                                       {unset > 0 && <>{unset} without a scope ruling yet</>}</>}
                            </span>
                        );
                    })()}
                    {lens === 'prudential' && stagedN > 0 && (
                        /* the tick apply — one signature, every staged change */
                        <span className="inline-flex items-center gap-2 text-[11px]">
                            <span className="font-semibold text-fg-secondary">
                                {stagedN} scope change{stagedN > 1 ? 's' : ''} ticked</span>
                            {isSigner
                                ? <button onClick={applyScopes} disabled={applyBusy}
                                    className="h-6 px-2.5 rounded-md bg-primary text-white font-semibold hover:bg-primary-hover disabled:opacity-40">
                                    {applyBusy ? 'Signing…' : `Apply — as ${signer}`}</button>
                                : <WaitingForSigner />}
                            <button onClick={() => setStaged({})}
                                className="text-fg-tertiary hover:underline">Cancel</button>
                        </span>
                    )}
                    {applyErr && <span className="text-[11px] text-warning-ink">{applyErr}</span>}
                    <span className="ml-auto flex gap-3 items-center">
                        {ents.length > 0 && (
                            /* the zoom lives in the header — never on top of a box
                               (Kawi, 17 Jul: the group hid behind the buttons) */
                            <span className="flex gap-1">
                                {(['−', 'fit', '+'] as const).map(b => (
                                    <button key={b}
                                        onClick={() => setZoom(z => b === 'fit' ? null
                                            : Math.max(0.5, Math.min(3, (z ?? 1) + (b === '+' ? 0.25 : -0.25))))}
                                        className="text-[11px] font-semibold text-fg-tertiary border border-border-default rounded px-1.5 py-0.5 bg-surface hover:bg-surface-sunken">
                                        {b}</button>
                                ))}
                            </span>
                        )}
                        {ents.length > 0 && (
                            <button onClick={() => setExpand(x => !x)}
                                className="text-[11px] font-semibold text-link hover:underline">
                                {expand ? 'Show the tree' : 'Expand the chart ⤢'}</button>
                        )}
                        {ents.length > 0 && (() => {
                            /* export (Kawi, 21 Jul: "attach the org chart in
                               documents") — the drawn svg, tokens resolved to
                               their computed values at click time. PNG first
                               (Kawi: "yes png" — it pastes everywhere), the
                               svg stays beside it for crisp print. */
                            const svgText = () => {
                                const svg = document.querySelector('svg[aria-label="group chart"]');
                                if (!svg) return null;
                                const css = getComputedStyle(document.documentElement);
                                const body = getComputedStyle(document.body);
                                let out = svg.outerHTML.replace(/var\((--[a-z-]+)\)/g,
                                    (_, v) => css.getPropertyValue(v).trim() || '#000');
                                out = out.replace('<svg ', `<svg style="font-family:${
                                    body.fontFamily.replace(/"/g, "'")}" `);
                                const vb = (svg.getAttribute('viewBox') || '0 0 980 400').split(' ');
                                return { out, w: Number(vb[2]) || 980, h: Number(vb[3]) || 400 };
                            };
                            const save = (blob: Blob, ext: string) => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url; a.download = `${bankName} — group chart.${ext}`;
                                a.click(); URL.revokeObjectURL(url);
                            };
                            return (
                                <span className="flex gap-1.5 items-baseline">
                                    <button onClick={() => {
                                        const s = svgText(); if (!s) return;
                                        const img = new Image();
                                        img.onload = () => {
                                            const c = document.createElement('canvas');
                                            c.width = s.w * 2; c.height = s.h * 2;   // 2× for sharp paste
                                            const ctx = c.getContext('2d');
                                            if (!ctx) return;
                                            ctx.fillStyle = getComputedStyle(document.documentElement)
                                                .getPropertyValue('--surface').trim() || '#ffffff';
                                            ctx.fillRect(0, 0, c.width, c.height);
                                            ctx.drawImage(img, 0, 0, c.width, c.height);
                                            c.toBlob(b => { if (b) save(b, 'png'); }, 'image/png');
                                        };
                                        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(s.out);
                                    }} className="text-[11px] font-semibold text-link hover:underline">
                                        Download PNG ↓</button>
                                    <button onClick={() => {
                                        const s = svgText(); if (!s) return;
                                        save(new Blob([s.out], { type: 'image/svg+xml' }), 'svg');
                                    }} className="text-[11px] font-semibold text-fg-tertiary hover:underline">
                                        svg</button>
                                </span>
                            );
                        })()}
                        {/* no group-search button here (Kawi, 17 Jul: "the search
                            is already above") — the ONE public-record search on
                            the profile also proposes the group */}
                        <button onClick={() => setAdding(a => !a)}
                            className="text-[11px] font-semibold text-link hover:underline">Add entity →</button>
                        {approvedEnts.length >= 2 && (
                            <button onClick={() => { setRFrom(approvedEnts[0].entity_id); setRTo(approvedEnts[1].entity_id); setRelAdding(true); }}
                                className="text-[11px] font-semibold text-link hover:underline">Add relation →</button>
                        )}
                    </span>
                </div>
                {chFor && (() => {
                    /* v2 (Kawi, 20 Jul: "you select what you want to edit....
                       not clear"): all facts sit there with their current
                       values — overtype what changed; edited rows highlight
                       and say what they were; only the edits file, each as
                       its own candidate card. */
                    const base = chBase(chFor);
                    const dirty = chDirty(chFor);
                    const dirtyKeys = new Set(dirty.map(([k]) => k));
                    const wordOf = (k: string, v: string) =>
                        k === 'kind' ? (ENTITY_KIND_WORDS[v] || v)
                        : k === 'parent' ? (ents.find(x => x.entity_id === v)?.name || v || '—')
                        : (v || '—');
                    const rowCls = (k: string) =>
                        `flex-1 text-[13px] h-8 px-2.5 border rounded-md text-fg ${
                            dirtyKeys.has(k) ? 'border-primary bg-primary-tint font-semibold' : 'border-border-strong bg-surface'}`;
                    const set = (k: string) => (ev: { target: { value: string } }) =>
                        setChDraft(d => ({ ...d, [k]: ev.target.value }));
                    const n = dirty.length;
                    return (
                    <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8">
                        <div className="bg-surface rounded-lg shadow-bark-md border border-border-default w-full max-w-md mt-16">
                            <div className="px-5 py-4 border-b border-divider">
                                <p className="text-[15px] font-semibold text-fg">Change {chFor.name}</p>
                                <p className="text-[11px] text-fg-tertiary mt-0.5">
                                    Edit what changed — it files for review; nothing changes until a named signer approves.</p>
                            </div>
                            <div className="px-5 py-4 flex flex-col gap-2">
                                {([['name', 'Name'], ['kind', 'Position'], ['parent', 'Parent'],
                                   ['share_pct', 'Ownership %'], ['voting_pct', 'Voting %'],
                                   ['legal_form', 'Legal form'], ['jurisdiction', 'Country'],
                                   ['regulator', 'Regulator'],
                                   ['consolidation_scope', 'Prudential scope']] as const).map(([k, label]) => (
                                    <React.Fragment key={k}>
                                        <div className="flex items-center gap-2.5">
                                            <span className="w-24 shrink-0 text-[11px] font-semibold tracking-[0.05em] uppercase text-fg-tertiary">{label}</span>
                                            {k === 'consolidation_scope' ? (
                                                <select aria-label={label} value={chDraft.consolidation_scope || ''}
                                                    onChange={set('consolidation_scope')} className={rowCls('consolidation_scope')}>
                                                    <option value="">— not set</option>
                                                    {['prudential', 'insurance', 'excluded'].map(x =>
                                                        <option key={x} value={x}>{x}</option>)}
                                                </select>
                                            ) : k === 'kind' ? (
                                                <select aria-label={label} value={chDraft.kind || ''} onChange={set('kind')} className={rowCls('kind')}>
                                                    {ENTITY_KINDS_UI.map(x =>
                                                        <option key={x} value={x}>{ENTITY_KIND_WORDS[x] || x}</option>)}
                                                </select>
                                            ) : k === 'parent' ? (
                                                <select aria-label={label} value={chDraft.parent || ''} onChange={set('parent')} className={rowCls('parent')}>
                                                    <option value="">— none yet</option>
                                                    {approvedEnts.filter(x => x.entity_id !== chFor.entity_id)
                                                        .map(x => <option key={x.entity_id} value={x.entity_id}>{x.name}</option>)}
                                                </select>
                                            ) : (
                                                <input aria-label={label} value={chDraft[k] || ''} onChange={set(k)}
                                                    placeholder={k === 'name' ? 'Legal name'
                                                        : k === 'legal_form' ? 'AB, ASA, GmbH…'
                                                        : k === 'jurisdiction' ? 'two-letter country (SE, EE…)'
                                                        : k === 'regulator' ? 'the supervisor, as the source writes it'
                                                        : '0–100'}
                                                    className={rowCls(k)} />
                                            )}
                                        </div>
                                        {dirtyKeys.has(k) && (
                                            <p className="ml-[106px] text-[11px] text-accent-copper -mt-0.5">
                                                was: {wordOf(k, base[k])}</p>
                                        )}
                                    </React.Fragment>
                                ))}
                                <div className="flex items-center gap-2.5">
                                    <span className="w-24 shrink-0 text-[11px] font-semibold tracking-[0.05em] uppercase text-fg-tertiary">Source</span>
                                    <input aria-label="Source" value={chSource} onChange={e => setChSource(e.target.value)}
                                        placeholder="link or reference — optional"
                                        className="flex-1 text-[13px] h-8 px-2.5 border border-border-strong rounded-md bg-surface text-fg" />
                                </div>
                                <p className="text-[11.5px] text-fg-secondary bg-surface-sunken border border-border-default rounded-md px-3 py-2 mt-1">
                                    {n > 0 ? <>Files <b>{n}</b> change{plural(n, { one: '', other: 's' })} for review. </>
                                           : <>Nothing edited yet. </>}
                                    {(chFor.bound_obligations || 0) > 0
                                        ? <>Approving flags <b>{chFor.bound_obligations}</b> obligation{plural(chFor.bound_obligations || 0, { one: '', other: 's' })} bound to {chFor.name} — each needs review under the new structure.</>
                                        : <>No obligation is bound to {chFor.name} yet — approving changes the picture, and flags nothing today.</>}
                                </p>
                            </div>
                            <div className="px-5 py-3.5 border-t border-divider flex gap-2 items-center justify-end">
                                {/* member: filing IS the primary act; signer: quiet
                                    secondary next to the direct change (roles, 20 Jul) */}
                                <button disabled={n === 0 || chBusy}
                                    onClick={async () => {
                                        setChBusy(true);
                                        /* scope files as an ENTITY FACT; the rest as
                                           structure changes — same review card */
                                        const scopeEdit = dirty.find(([k]) => k === 'consolidation_scope');
                                        const structEdits = dirty.filter(([k]) => k !== 'consolidation_scope');
                                        let ok = true;
                                        if (structEdits.length)
                                            ok = await postJ(`/api/inventory/profile/group/entities/${chFor.entity_id}/changes`, {
                                                items: structEdits.map(([field, value]) => ({
                                                    field, value: value.trim(), source_url: chSource.trim() })),
                                                door: 'manual', by: signer });
                                        if (ok && scopeEdit)
                                            ok = await postJ(`/api/inventory/profile/group/entities/${chFor.entity_id}/facts`, {
                                                items: [{ key: 'consolidation_scope', value: scopeEdit[1].trim(),
                                                    source_url: chSource.trim() }],
                                                door: 'manual', by: signer });
                                        if (ok) setChFor(null);
                                        setChBusy(false);
                                    }}
                                    className={isSigner
                                        ? 'mr-auto text-[11px] text-fg-tertiary hover:underline disabled:opacity-50'
                                        : 'h-8 px-3.5 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60 order-last'}>
                                    {isSigner ? 'file for review instead'
                                        : chBusy ? 'Filing…'
                                        : n > 0 ? `File ${n} change${plural(n, { one: '', other: 's' })} for review — as ${signer}` : 'Nothing to file'}</button>
                                <button onClick={() => setChFor(null)}
                                    className="h-8 px-3.5 rounded-md border border-border-strong bg-surface text-fg text-[13px] font-semibold hover:bg-bg-warm">
                                    Cancel</button>
                                {/* the signer's DIRECT act (Kawi, 20 Jul: no 4-eyes
                                    ceremony for the decider) — applies at once; same
                                    gates, same records, same flags */}
                                {isSigner && <button disabled={n === 0 || chBusy}
                                    onClick={async () => {
                                        setChBusy(true);
                                        const scopeEdit = dirty.find(([k]) => k === 'consolidation_scope');
                                        const structEdits = dirty.filter(([k]) => k !== 'consolidation_scope');
                                        let ok = true;
                                        if (structEdits.length)
                                            ok = await postJ(`/api/inventory/profile/group/entities/${chFor.entity_id}/changes/apply`, {
                                                items: structEdits.map(([field, value]) => ({
                                                    field, value: value.trim(), source_url: chSource.trim() })),
                                                door: 'manual', by: signer });
                                        if (ok && scopeEdit) {
                                            /* the signer's one act = candidate + own
                                               approval, both on record (no facts/apply
                                               route exists — this IS the apply) */
                                            const r = await fetch(`${API}/api/inventory/profile/group/entities/${chFor.entity_id}/facts`, {
                                                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                                body: JSON.stringify({ items: [{ key: 'consolidation_scope',
                                                    value: scopeEdit[1].trim(), source_url: chSource.trim() }],
                                                    door: 'manual', by: signer }),
                                            });
                                            if (!r.ok) { setErr((await r.json()).detail || `${r.status}`); ok = false; }
                                            else {
                                                for (const s of (await r.json()).stored || []) {
                                                    ok = ok && await postJ(`/api/inventory/profile/group/facts/${s.efact_id}/review`,
                                                        { verdict: 'approve', by: signer });
                                                }
                                            }
                                        }
                                        if (ok) setChFor(null);
                                        setChBusy(false);
                                    }}
                                    className="h-8 px-3.5 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                    {chBusy ? 'Changing…' : n > 0 ? `Change — as ${signer}` : 'Nothing to change'}</button>}
                            </div>
                        </div>
                    </div>
                    );
                })()}
                {relAdding && (
                    <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8">
                        <div className="bg-surface rounded-lg shadow-bark-md border border-border-default w-full max-w-md mt-16">
                            <div className="px-5 py-4 border-b border-divider">
                                <p className="text-[15px] font-semibold text-fg">Add an intra-group relation</p>
                                <p className="text-[11px] text-fg-tertiary mt-0.5">
                                    Exposure, guarantee or service between two approved entities.
                                    It waits as a candidate until you approve it.</p>
                            </div>
                            <div className="px-5 py-4 flex flex-col gap-2.5">
                                <select value={rFrom} onChange={e => setRFrom(e.target.value)}
                                    className="text-[13px] h-9 px-2.5 border border-border-strong rounded-md bg-surface text-fg">
                                    {approvedEnts.map(x => <option key={x.entity_id} value={x.entity_id}>from: {x.name}</option>)}
                                </select>
                                <select value={rType} onChange={e => setRType(e.target.value)}
                                    className="text-[13px] h-9 px-2.5 border border-border-strong rounded-md bg-surface text-fg">
                                    {['exposure', 'guarantee', 'service'].map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <select value={rTo} onChange={e => setRTo(e.target.value)}
                                    className="text-[13px] h-9 px-2.5 border border-border-strong rounded-md bg-surface text-fg">
                                    {approvedEnts.map(x => <option key={x.entity_id} value={x.entity_id}>to: {x.name}</option>)}
                                </select>
                                <input value={rAmount} onChange={e => setRAmount(e.target.value)}
                                    placeholder="Amount (e.g. 500 MSEK) — optional"
                                    className="text-[13px] h-9 px-3 border border-border-strong rounded-md bg-surface text-fg" />
                                <input value={rDesc} onChange={e => setRDesc(e.target.value)}
                                    placeholder="What it is — one line"
                                    className="text-[13px] h-9 px-3 border border-border-strong rounded-md bg-surface text-fg" />
                            </div>
                            <div className="px-5 py-3.5 border-t border-divider flex gap-2 justify-end">
                                <button onClick={() => { setRelAdding(false); setRAmount(''); setRDesc(''); }}
                                    className="h-8 px-3.5 rounded-md border border-border-strong bg-surface text-fg text-[13px] font-semibold hover:bg-bg-warm">
                                    Cancel</button>
                                <button disabled={!rFrom || !rTo || rFrom === rTo}
                                    onClick={async () => {
                                        if (await postJ('/api/inventory/profile/group/links', {
                                            items: [{ from_id: rFrom, to_id: rTo, rel_type: rType,
                                                amount: rAmount.trim(), description: rDesc.trim() }],
                                            door: 'manual', by: signer })) {
                                            setRelAdding(false); setRAmount(''); setRDesc('');
                                        }
                                    }}
                                    className="h-8 px-3.5 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                    Add for review</button>
                            </div>
                        </div>
                    </div>
                )}
                {adding && (
                    /* the act opens ON TOP, not squeezed between header and tree
                       (Kawi, 17 Jul) — the page's own modal idiom, with Cancel */
                    <div className="fixed inset-0 z-50 bg-primary/40 flex items-start justify-center overflow-y-auto p-8">
                        <div className="bg-surface rounded-lg shadow-bark-md border border-border-default w-full max-w-md mt-16">
                            <div className="px-5 py-4 border-b border-divider">
                                <p className="text-[15px] font-semibold text-fg">Add an entity</p>
                                <p className="text-[11px] text-fg-tertiary mt-0.5">
                                    It waits as a candidate until you approve it — nothing counts before that.</p>
                            </div>
                            <div className="px-5 py-4 flex flex-col gap-2.5">
                                <input value={eName} onChange={e => setEName(e.target.value)} placeholder="Legal name…" autoFocus
                                    className="text-[13px] h-9 px-3 border border-border-strong rounded-md bg-surface text-fg" />
                                <select value={eKind} onChange={e => setEKind(e.target.value)}
                                    className="text-[13px] h-9 px-2.5 border border-border-strong rounded-md bg-surface text-fg">
                                    {ENTITY_KINDS_UI.map(k => <option key={k} value={k}>{ENTITY_KIND_WORDS[k]}</option>)}
                                </select>
                                <select value={eParent} onChange={e => setEParent(e.target.value)}
                                    className="text-[13px] h-9 px-2.5 border border-border-strong rounded-md bg-surface text-fg">
                                    <option value="">no parent here</option>
                                    {ents.map(x => <option key={x.entity_id} value={x.name}>owned by: {x.name}</option>)}
                                </select>
                                <div className="flex gap-2.5">
                                    <input value={eJur} onChange={e => setEJur(e.target.value)} placeholder="Country (SE…)"
                                        className="text-[13px] h-9 px-3 w-32 border border-border-strong rounded-md bg-surface text-fg" />
                                    <input value={eShare} onChange={e => setEShare(e.target.value)}
                                        placeholder="Owned %" inputMode="decimal"
                                        className="text-[13px] h-9 px-3 w-28 border border-border-strong rounded-md bg-surface text-fg" />
                                    <input value={eForm} onChange={e => setEForm(e.target.value)}
                                        placeholder="Legal form (AB…)"
                                        className="text-[13px] h-9 px-3 flex-1 border border-border-strong rounded-md bg-surface text-fg" />
                                </div>
                            </div>
                            <div className="px-5 py-3.5 border-t border-divider flex gap-2 justify-end">
                                <button onClick={() => { setAdding(false); setEName(''); setEParent(''); setEJur(''); setEShare(''); setEForm(''); }}
                                    className="h-8 px-3.5 rounded-md border border-border-strong bg-surface text-fg text-[13px] font-semibold hover:bg-bg-warm">
                                    Cancel</button>
                                <button disabled={!eName.trim()}
                                    onClick={async () => {
                                        if (await postJ('/api/inventory/profile/group/entities', {
                                            items: [{ name: eName.trim(), kind: eKind, parent_name: eParent,
                                                jurisdiction: eJur.trim(), legal_form: eForm.trim(),
                                                share_pct: eShare.trim() ? Number(eShare) : null }],
                                            door: 'manual', by: signer })) {
                                            setAdding(false); setEName(''); setEParent(''); setEJur(''); setEShare(''); setEForm('');
                                        }
                                    }}
                                    className="h-8 px-3.5 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                    Add for review</button>
                            </div>
                        </div>
                    </div>
                )}
                {ents.length === 0
                    ? <p className="text-[12px] text-fg-tertiary px-4 py-3">
                        No entities yet. The public-record search above proposes the group, or add one by hand —
                        every entity waits for your approval before it counts.</p>
                    : (
                        <>
                            <div className={expand ? '' : 'grid grid-cols-[minmax(280px,2fr)_3fr]'}>
                                {!expand && (
                                    <div className="divide-y divide-divider border-r border-divider">
                                        {(children.get('__root__') || []).map(e => entityRow(e, 0, new Set()))}
                                    </div>
                                )}
                                {chart(expand)}
                            </div>
                            {/* THE BANK'S OWN CHART (21 Jul) — uploaded, next to
                                the legal picture; a signed register entry */}
                            <div className="border-t border-divider px-4 py-2.5 flex gap-3 items-baseline flex-wrap text-[12.5px]">
                                <span className="text-[11px] font-semibold tracking-[0.07em] uppercase text-fg-tertiary">The bank's own chart</span>
                                {(ownCharts || []).length === 0
                                    ? <span className="text-fg-tertiary">your internal organisation chart — uploaded as a file, kept in the register</span>
                                    : (ownCharts || []).map(c => (
                                        <span key={c.id} className="font-semibold text-fg">{c.name}
                                            <span className="font-normal text-[11px] text-fg-tertiary"> · in the register · {c.id}</span></span>
                                    ))}
                                {isSigner && !ocOpen && (
                                    <button onClick={() => setOcOpen(true)}
                                        className="ml-auto text-[11px] font-semibold text-link hover:underline">Upload →</button>
                                )}
                                {ocOpen && (
                                    <span className="ml-auto flex gap-2 items-center flex-wrap">
                                        <input value={ocName} onChange={e => setOcName(e.target.value)}
                                            className="text-[12px] h-8 px-2.5 w-56 border border-border-strong rounded-md bg-surface text-fg" />
                                        <input type="file" onChange={e => setOcFile(e.target.files?.[0] || null)}
                                            className="text-[12px]" />
                                        <button disabled={ocBusy || !ocFile || !ocName.trim()}
                                            onClick={async () => {
                                                setOcBusy(true); setErr(undefined);
                                                const fd = new FormData();
                                                fd.append('file', ocFile!); fd.append('name', ocName.trim());
                                                fd.append('kind', 'Deliverable'); fd.append('role', OWN_CHART_ROLE);
                                                fd.append('by', signer);
                                                const r = await fetch(`${API}/api/inventory/artifacts/own-document`,
                                                    { method: 'POST', body: fd, headers: authHeaders() });
                                                if (!r.ok) setErr((await r.json()).detail || `${r.status}`);
                                                else {
                                                    const d = await r.json();
                                                    setOwnCharts(cs => [...(cs || []), { id: d.id, name: d.name }]);
                                                    setOcOpen(false); setOcFile(null);
                                                }
                                                setOcBusy(false);
                                            }}
                                            className="h-8 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                            {ocBusy ? 'Signing…' : `Sign as ${signer} →`}</button>
                                        <button onClick={() => { setOcOpen(false); setOcFile(null); }}
                                            className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
                                    </span>
                                )}
                            </div>
                            {links.filter(l => l.status === 'approved').length > 0 && (
                                <div className="border-t border-divider">
                                    <p className="text-[11px] font-semibold tracking-[0.07em] uppercase text-fg-tertiary px-4 pt-2.5">Relations</p>
                                    {links.filter(l => l.status === 'approved').map(l => (
                                        <p key={l.link_id} className="text-[12.5px] text-fg-secondary px-4 py-1.5">
                                            <span className="font-semibold text-fg">{l.from_name}</span>
                                            <span className="text-fg-tertiary"> —{l.rel_type}→ </span>
                                            <span className="font-semibold text-fg">{l.to_name}</span>
                                            {l.amount && <span className="text-fg-tertiary"> · {l.amount}</span>}
                                            {l.description && <span className="text-fg-tertiary"> · {l.description}</span>}
                                        </p>
                                    ))}
                                    <div className="pb-1.5" />
                                </div>
                            )}
                            <p className="text-[11px] text-fg-tertiary px-4 py-2.5 border-t border-divider flex gap-x-3 gap-y-1 items-center flex-wrap">
                                <span><span className="inline-block w-2 h-2 rounded-full bg-primary mr-1 align-middle" />this institution (matched from the profile)</span>
                                <span><span className="inline-block w-2 h-2 rounded-full bg-link mr-1 align-middle" />subsidiary</span>
                                <span><span className="inline-block w-2 h-2 rounded-full bg-accent-copper mr-1 align-middle" />branch</span>
                                <span><span className="inline-block w-2 h-2 rounded-full bg-border-strong mr-1 align-middle" />parent</span>
                                <span>small dot = supervised</span>
                                <span className="text-fg-tertiary">· dashed = waiting, counts as nothing · full name on hover</span>
                            </p>
                        </>
                    )}
                {err && <p className="text-[11px] text-warning-ink px-4 pb-2.5">{err}</p>}
            </div>
        </>
    );
}

/* ---------- group binding on an obligation (Kawi, 17 Jul) ---------- */

function AppliesToLine({ obligationId, bindings, signer, onChanged }: {
    obligationId: string; bindings: { entity_id: string; name: string; level: string }[];
    signer: string; onChanged: () => void;
}) {
    /* the manual bind act died 22 Jul (Kawi: "isnt the profile what we
       should use for this?") — the level comes from the profile/wiring
       road when built; what remains here is DISPLAY of stored bindings
       plus unbind as the signed correction. Role-gated since 21 Jul. */
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [unbindFor, setUnbindFor] = useState<string | null>(null);
    const [reason, setReason] = useState('');
    const [err, setErr] = useState<string>();
    const act = async (path: string, body: object): Promise<boolean> => {
        setErr(undefined);
        const r = await fetch(`${API}${path}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body),
        });
        if (!r.ok) { setErr((await r.json()).detail || `${r.status}`); return false; }
        onChanged(); return true;
    };
    if (bindings.length === 0) return null;
    return (
        <div className="text-[12px] text-fg-tertiary mt-1.5">
            Applies to:{' '}
            {bindings.map((b, i) => (
                    <span key={b.entity_id}>
                        {i > 0 && ' · '}
                        <span className="font-semibold text-fg-secondary">{b.name}</span> ({b.level}){' '}
                        <button onClick={() => { setUnbindFor(unbindFor === b.entity_id ? null : b.entity_id); setReason(''); }}
                            className="text-fg-tertiary hover:underline">✕</button>
                    </span>
                ))}
            {unbindFor && (
                <span className="inline-flex gap-2 items-center ml-2">
                    <input value={reason} onChange={e => setReason(e.target.value)}
                        placeholder="reason — required" autoFocus
                        className={`text-[11px] border rounded-md px-2.5 h-7 w-56 bg-surface text-fg ${
                            reason.trim() ? 'border-border-strong' : 'border-danger'}`} />
                    {isSigner ? (
                        <button disabled={!reason.trim()}
                        onClick={async () => {
                            if (await act(`/api/inventory/obligations/${obligationId}/applies-to/remove`,
                                { entity_id: unbindFor, reason: reason.trim(), by: signer })) {
                                setUnbindFor(null); setReason('');
                            }
                        }}
                        className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-60">
                        Unbind — as {signer}</button>
                    ) : <WaitingForSigner />}
                    <button onClick={() => { setUnbindFor(null); setReason(''); }}
                        className="text-[11px] text-fg-tertiary hover:underline">cancel</button>
                </span>
            )}
            {err && <span className="block text-warning-ink mt-1">{err}</span>}
        </div>
    );
}

/* ---------- add your own obligation — honest placeholder page (6 decisions open) ---------- */

function AddObligationPage({ cluster, onBack }: { cluster: ClusterRow; onBack: () => void }) {
    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <button onClick={onBack} className="text-link">{cluster.name}</button>
                <span className="text-border-strong">/</span>
                <span className="text-fg font-semibold">Add your own obligation</span>
            </div>
            <PageTitle>Add your own obligation</PageTitle>
            <PageSub>
                Duties your bank sets for itself — including business-driven internal limits —
                join the cluster with a permanent "added by your bank" mark and can feed the Limits watch.
            </PageSub>
            <NextCard kicker="Not built yet" title="The rules are still open decisions"
                detail="who may add · second pair of eyes · how BARK watches a bank-added row · edit and retire flow · placement authority · how it counts in the gap numbers — the six open decisions of the requirements draft (11 Jul)" />
        </Wrap>
    );
}

/* ---------- add your own cluster — honest placeholder page (rules undecided) ---------- */

function AddClusterPage({ onBack }: { onBack: () => void }) {
    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <button onClick={onBack} className="text-link">Clusters</button>
                <span className="text-border-strong">/</span>
                <span className="text-fg font-semibold">Add your own cluster</span>
            </div>
            <PageTitle>Add your own cluster</PageTitle>
            <PageSub>
                The register never claims to be complete — your bank can add clusters for its own
                frameworks, the same way it can add its own obligations.
            </PageSub>
            <NextCard kicker="Not built yet" title="The rules are still open decisions"
                detail="who may add · whether a second person confirms · how BARK watches a bank-added cluster — same open questions as add-your-own-obligation" />
        </Wrap>
    );
}

/* ---------- page 2: one cluster ---------- */

function ClusterPage({ cluster, onBack, onOpen, onRecords, signer, onArtifacts, onAddObligation, onAddDocument, onOpenArtifact }: {
    cluster: ClusterRow; onBack: () => void; onOpen: (id: string) => void; onRecords: () => void;
    signer: string; onArtifacts: (focusSupport?: boolean) => void; onAddObligation: () => void;
    onAddDocument: () => void; onOpenArtifact: (id: string) => void;
}) {
    const [rows, setRows] = useState<OblRow[] | null>(null);
    const [err, setErr] = useState<string>();
    /* the take act (Kawi, 22 Jul: "i might want to accept cluster but not
       the obligations") — one signature makes the cluster yours; duties
       stay suggested. Local `taken` bridges until the next fetch. */
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [taken, setTaken] = useState(false);
    const [takeBusy, setTakeBusy] = useState(false);
    const [takeErr, setTakeErr] = useState<string>();
    const yoursNow = cluster.signed || taken;
    const take = async () => {
        setTakeBusy(true); setTakeErr(undefined);
        try {
            const r = await fetch(`${API}/api/inventory/clusters/${cluster.id}/accept`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ by: signer }),
            });
            if (!r.ok) throw new Error(`${r.status}`);
            setTaken(true);
        } catch (e) { setTakeErr(`Signing failed (${String(e)}). Nothing was recorded.`); }
        setTakeBusy(false);
    };
    /* reality-first (Kawi, 16 Jul): the face is the PIECES — the real things
       the bank holds; the duty rows are the audit layer, one click down */
    /* no side memory (Kawi, 22 Jul: "why do we start from the last visited
       page") — a cluster ALWAYS opens on Obligations */
    /* REGISTER-FIRST (Kawi, 4 Aug: "i want register to be the center of
       everything"): a cluster ALWAYS opens on its Register side; the
       obligations are the checklist behind it */
    const [view, setView] = useState<'pieces' | 'duties'>('pieces');
    const switchSide = (v: 'pieces' | 'duties') => setView(v);
    const [startFor, setStartFor] = useState<string | null>(null);
    const [ghostsChoice, setGhostsChoice] = useState<boolean | null>(() => {
        try {
            const s = localStorage.getItem(`bark_examples_open:${cluster.id}`);
            return s === null ? null : s === '1';
        } catch { return null; }
    });
    const [whichFor, setWhichFor] = useState<string | null>(null);
    const [q, setQ] = useState('');
    const [type, setType] = useState('all');
    /* Detailed first (Kawi, 22 Jul: "I want detailed to be the first view") */
    const [dview, setDview] = useState<'simple' | 'detailed'>('detailed');
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const [reload, setReload] = useState(0);
    const [arts, setArts] = useState<ArtifactRow[] | null>(null);

    useEffect(() => {
        get<{ obligations: OblRow[] }>(`/api/inventory/clusters/${cluster.id}/obligations`)
            .then(d => setRows(d.obligations)).catch(e => setErr(String(e)));
        get<{ artifacts: ArtifactRow[] }>(`/api/inventory/artifacts?cluster_id=${encodeURIComponent(cluster.id)}`)
            .then(d => setArts(d.artifacts)).catch(() => setArts([]));
    }, [cluster.id, reload]);
    if (!rows) return <Loading error={err} />;

    /* the audit layer (duties view) keeps its filters */
    const filtered = rows.filter(r =>
        (!q || r.title.toLowerCase().includes(q.toLowerCase())
            || r.id.toLowerCase().includes(q.toLowerCase())) &&
        (type === 'all' || r.obligation_type === type));
    const filtering = !!q || type !== 'all';
    const groups = TYPE_ORDER.filter(t => filtered.some(r => r.obligation_type === t));
    /* two-layer edition (29 Jul): ANY row carrying row_class switches the
       obligations view to the layered list; clusters without layer data
       (IRRBB, LE edition 1) keep the flat page exactly as before */
    const layered = rows.some(r => !!r.row_class);

    /* the face: pieces + ghosts, all computed from the two fetches.
       A PIECE answers this cluster directly. A GHOST is an example that
       would still cover an uncovered duty — it dies the moment the bank's
       own pieces cover everything it stood for (Kawi, 16 Jul). */
    const live = rows.filter(r => r.adoption !== 'dismissed');
    const answered = live.filter(r => r.confirmed > 0).length;
    const flaggedIds = new Set(rows
        .filter(r => r.change_status && !['green', 'none'].includes(r.change_status))
        .map(r => r.id));
    const uncovered = new Set(live.filter(r => r.confirmed === 0).map(r => r.id));
    const world = arts || [];
    const answering = world.filter(a => !a.supports_only && (a.obligations || []).length > 0);
    const bankPieces = answering.filter(a => a.data_origin === 'real');
    const ghosts = answering.filter(a => a.data_origin !== 'real'
        && (a.obligations || []).some(o => uncovered.has(o.id)));
    const supportCount = world.length - answering.length;
    const pieceState = (a: ArtifactRow): 'review' | 'signed' | 'ghost' =>
        a.data_origin !== 'real' ? 'ghost'
            : (a.obligations || []).some(o => flaggedIds.has(o.id)) ? 'review' : 'signed';
    const rank = (a: ArtifactRow) => pieceState(a) === 'review' ? 0 : pieceState(a) === 'signed' ? 1 : 2;
    const sorted = [...bankPieces, ...ghosts]
        .sort((x, y) => rank(x) - rank(y) || (x.name || '').localeCompare(y.name || ''));
    const flaggedPiece = bankPieces.find(a => pieceState(a) === 'review');
    const firstGhost = sorted.find(a => pieceState(a) === 'ghost');
    const ghostCover = new Set(ghosts.flatMap(a => (a.obligations || [])
        .map(o => o.id)).filter(oid => uncovered.has(oid))).size;
    const grouped = sorted.length > 12;
    const KIND_GDESC: Record<string, string> = {
        Policy: 'what the board sets and owns',
        Model: 'the calculation machinery the obligations demand',
        Data: 'the datasets the machinery consumes',
        Deliverable: 'what gets produced and sent',
    };

    /* one piece row — bank pieces show their state; ghosts carry the one
       birth act, opening the ONE panel prefilled with everything the
       example would answer (uncovered duty first).
       Row grammar = the obligations table's (Kawi, 4 Aug: "i like the
       obligations page more" → YES): mono id left, bold name, Covers and
       Status columns right — the dot alone carries the colour */
    const pieceRow = (a: ArtifactRow, isFirst: boolean, isLast: boolean) => {
        const st = pieceState(a);
        const obls = a.obligations || [];
        const nFlagged = obls.filter(o => flaggedIds.has(o.id)).length;
        return (
            <React.Fragment key={a.id}>
                <div className={`w-full grid grid-cols-[112px_minmax(220px,1fr)_90px_220px] gap-3 items-center px-3.5 py-3 text-[13px] bg-surface border border-border-default ${
                    isFirst ? 'rounded-t-md' : 'border-t-0'} ${isLast ? 'rounded-b-md' : ''}`}>
                    <span className="font-mono text-[11px] text-fg-tertiary whitespace-nowrap">{a.id}</span>
                    <span className="min-w-0">
                        <span className="flex items-center gap-2">
                            <button onClick={() => onOpenArtifact(a.id)}
                                className={`font-semibold hover:underline text-left truncate ${st === 'ghost' ? 'text-fg-tertiary' : 'text-fg'}`}>{a.name}</button>
                            {st === 'ghost' && <ExMark />}
                        </span>
                        {/* the example says WHY it still stands (Kawi, 6 Aug)
                            and names the exact ground behind "which? →" —
                            unanswered obligations first, the already-answered
                            overlap greyed underneath */}
                        {st === 'ghost' && (() => {
                            const open = obls.filter(o => uncovered.has(o.id));
                            const done = obls.filter(o => !uncovered.has(o.id));
                            return (
                                <span className="block text-[11px] text-fg-tertiary mt-0.5">
                                    would cover {obls.length} — {open.length} still without an answer{' '}
                                    <button onClick={() => setWhichFor(w => w === a.id ? null : a.id)}
                                        className="font-semibold text-link hover:underline">
                                        {whichFor === a.id ? 'hide' : 'which? →'}</button>
                                    {whichFor === a.id && (
                                        <span className="block mt-1">
                                            {open.map(o => (
                                                <span key={o.id} className="block text-[11.5px] text-fg-secondary">· {o.title}</span>
                                            ))}
                                            {done.map(o => (
                                                <span key={o.id} className="block text-[11.5px] text-fg-tertiary line-through decoration-border-strong">· {o.title} — already answered</span>
                                            ))}
                                        </span>
                                    )}
                                </span>
                            );
                        })()}
                    </span>
                    <span className="text-right text-fg-secondary tabular-nums">
                        {st !== 'ghost' && obls.length > 0 ? obls.length : <span className="text-border-strong">—</span>}</span>
                    <span className="text-right whitespace-nowrap">
                        {st === 'signed' && <span className="inline-flex items-center gap-[7px] text-[13px] text-fg-secondary"><Dot tone="g" /> signed</span>}
                        {st === 'review' && <span className="inline-flex items-center gap-[7px] text-[13px] text-fg-secondary"><Dot tone="a" /> needs review{nFlagged > 1 ? ` · ${nFlagged}` : ''}</span>}
                        {st === 'ghost' && (
                            <button onClick={() => setStartFor(startFor === a.id ? null : a.id)}
                                className="text-[11px] font-semibold text-link hover:underline">Start from the example →</button>
                        )}
                    </span>
                </div>
            </React.Fragment>
        );
    };

    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <button onClick={onBack} className="text-link">Clusters</button>
                <span className="text-border-strong">/</span>
                <span className="text-fg font-semibold">{cluster.name}</span>
            </div>
            <PageTitle>{cluster.name}</PageTitle>
            {/* no what-is-this box here (Kawi, 22 Jul: "what the fuck is that
                shit") — the cluster's own description says it better */}
            {CLUSTER_DESC[cluster.id] && (
                <p className="text-[13px] text-fg-tertiary mb-0">
                    {CLUSTER_DESC[cluster.id].charAt(0).toUpperCase() + CLUSTER_DESC[cluster.id].slice(1)}.
                </p>
            )}

            {/* tiles — the level pattern (counts · counts · Need attention ·
                Register · Add-your-own), same order as the landing */}
            {/* law 9 (approved picture): true numbers only; the add act sits
                on the obligations lane title (law 5) */}
            {/* reality-first tiles (approved picture): pieces first, duties as
                the audit layer, flags, sources — true numbers only (law 9) */}
            <div className="grid grid-cols-2 min-[900px]:grid-cols-4 gap-3 mb-6 mt-6">
                <StatTile n={arts === null ? '…' : String(bankPieces.length)}
                    nd={arts === null ? undefined : `/${bankPieces.length + ghosts.length}`}
                    l="In the register"
                    sub={bankPieces.length === 0 ? 'empty until you sign' : 'signed by named people'}
                    go="open →" onClick={onArtifacts} />
                <StatTile n={String(answered)} nd={`/${live.length}`} l="Obligations answered"
                    sub="the audit layer underneath"
                    bar={{ a: answered, b: live.length - answered }} />
                <StatTile n={String(cluster.flagged)} l="Need attention"
                    sub="flags bubble up to their entry" amber={cluster.flagged > 0} />
                <StatTile n={String(rows.reduce((n, r) => n + r.citations, 0))} l="Sources pinned"
                    sub={yoursNow ? `yours${cluster.signed_at ? ` since ${cluster.signed_at}` : ''}` : 'not signed yet'} />
            </div>

            {/* the take act — ADDITIVE above the chain: making the cluster
                yours and deciding its duties are two different signatures
                (Kawi, 22 Jul). Members see the waiting words, not a button. */}
            {!yoursNow && (
                <NextCard kicker="Make it yours"
                    title="Take this cluster"
                    detail={`signing puts ${cluster.name} under Your clusters — dismiss the obligations that do not apply, answer the rest with documents${
                        isSigner ? '' : ' · waiting for a signer to decide.'}`}
                    action={isSigner
                        ? { label: takeBusy ? 'Signing…' : `Sign — as ${signer}`, onClick: take }
                        : undefined} />
            )}
            {taken && (
                <p className="text-[12.5px] font-semibold text-secondary-hover mb-3">
                    ✓ {cluster.name} is yours now — it sits under Your clusters on the landing.</p>
            )}
            {takeErr && <p className="text-[12.5px] text-danger mb-3">{takeErr}</p>}

            {/* the card, reality-first: a flag names its PIECE and opens it;
                then the ceremony; then build/complete the set (picture order) */}
            {cluster.flagged > 0 || flaggedPiece
                ? <NextCard warn kicker="Needs attention"
                    title={flaggedPiece ? `${flaggedPiece.name} needs review` : 'The law moved — review needed'}
                    detail="the law moved under one of its obligations — review and clear, on record"
                    action={flaggedPiece
                        ? { label: 'Open →', onClick: () => onOpenArtifact(flaggedPiece.id) }
                        : (() => {
                            const f = rows.find(r => flaggedIds.has(r.id));
                            return f ? { label: 'Open →', onClick: () => onOpen(f.id) } : undefined;
                        })()} />
                : answered === 0 && ghosts.length > 0
                ? <NextCard kicker="Next for you" title="Build the set"
                    detail={`${ghosts.length} example${ghosts.length > 1 ? 's' : ''} cover${ghosts.length > 1 ? '' : 's'} ${
                        ghostCover === live.length ? `all ${live.length}` : `${ghostCover} of ${live.length}`} obligations — one signature can cover several`}
                    action={firstGhost ? { label: 'Start →', onClick: () => setStartFor(firstGhost.id) } : undefined} />
                : answered < live.length
                ? <NextCard kicker="Next for you" title="Complete the set"
                    detail={`${bankPieces.length} entr${plural(bankPieces.length, { one: 'y', other: 'ies' })} in the register · ${answered} of ${live.length} obligations answered`}
                    action={firstGhost
                        ? { label: 'Continue →', onClick: () => setStartFor(firstGhost.id) }
                        : { label: 'The audit layer →', onClick: () => setView('duties') }} />
                : <NextCard kicker="All in order" title="Nothing needs you in this cluster ✓"
                    detail={`${bankPieces.length} entr${plural(bankPieces.length, { one: 'y', other: 'ies' })} · ${live.length} obligations answered${
                        yoursNow ? ` · yours${cluster.signed_at ? ` since ${cluster.signed_at}` : ''}` : ''}`} />}

            {/* the one switch (Kawi, 20 Jul: "should be a toggle no?") — the
                obligations stop hiding behind a bottom link */}
            <div className="mb-3">
                {/* register-first (Kawi, 4 Aug): Register is the LEFT segment —
                    the landing side never starts on the right */}
                <SegButtons value={view} onChange={v => switchSide(v as 'pieces' | 'duties')}
                    options={[
                        { key: 'pieces', label: `Register${arts ? ` · ${sorted.length}` : ''}` },
                        { key: 'duties', label: `Obligations${rows ? ` · ${live.length}` : ''}` },
                    ]} />
            </div>
            {view === 'pieces' && (
                <>
                    <LaneLabel right={<button onClick={onAddDocument}
                        className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Add your own →</button>}>
                        Entries</LaneLabel>
                    {arts === null && <p className="text-[13px] text-fg-tertiary">Loading the register…</p>}
                    {arts !== null && sorted.length === 0 && (
                        <div className="border border-dashed border-border-strong rounded-lg bg-surface-sunken px-6 py-8 text-center text-[13px] text-fg-secondary">
                            <b className="block text-[15px] text-fg mb-1">Nothing in the register yet</b>
                            No examples exist for this cluster either — add your own, or work from the audit layer below.
                        </div>
                    )}
                    {/* header rows carry the column labels — the obligations
                        table's pattern (Kawi, 4 Aug) */}
                    {grouped
                        ? ['Policy', 'Model', 'Data', 'Deliverable'].filter(k => bankPieces.some(a => a.kind === k)).map(k => {
                            const items = bankPieces.filter(a => a.kind === k);
                            return (
                                <div key={k} className="mb-[18px]">
                                    <div className="grid grid-cols-[112px_minmax(220px,1fr)_90px_220px] gap-3 items-center px-3.5 py-1.5">
                                        <span className="col-span-2 text-[11px] font-semibold tracking-[0.06em] uppercase text-primary">
                                            {KIND_PLURAL[k] || k}
                                            <span className="font-mono text-[11px] font-medium text-fg-tertiary normal-case ml-1">{items.length}</span>
                                            <span className="text-[11px] font-medium text-fg-tertiary normal-case tracking-normal ml-3">{KIND_GDESC[k]}</span>
                                        </span>
                                        <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Answers</span>
                                        <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Status</span>
                                    </div>
                                    <div>{items.map((a, i) => pieceRow(a, i === 0, i === items.length - 1))}</div>
                                </div>
                            );
                        })
                        : bankPieces.length > 0 && (
                            <div className="mb-[18px]">
                                <div className="grid grid-cols-[112px_minmax(220px,1fr)_90px_220px] gap-3 items-center px-3.5 py-1.5">
                                    <span className="col-span-2" />
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Answers</span>
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Status</span>
                                </div>
                                {bankPieces.map((a, i) => pieceRow(a, i === 0, i === bankPieces.length - 1))}
                            </div>
                        )}
                    {/* the examples live on their OWN shelf, hideable (Kawi,
                        6 Aug: "shouldnt the examples somehow be separated and
                        with possibility to hide") — never mixed with signed
                        entries. Only examples still covering unanswered
                        ground appear (the standing rule). */}
                    {ghosts.length > 0 && (() => {
                        /* default: open for an empty register (a new bank
                           needs the starting points), folded once real
                           entries exist; a click is remembered per cluster */
                        const shown = ghostsChoice ?? bankPieces.length === 0;
                        const toggle = () => {
                            setGhostsChoice(!shown);
                            try { localStorage.setItem(`bark_examples_open:${cluster.id}`, !shown ? '1' : '0'); } catch { /* view pref only */ }
                        };
                        return (
                            <div className="mb-[18px]">
                                <LaneLabel right={<button onClick={toggle}
                                    className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">
                                    {shown ? 'hide →' : `show · ${ghosts.length}`}</button>}>
                                    Examples — starting points, they count as nothing</LaneLabel>
                                {shown && ghosts.map((a, i) => pieceRow(a, i === 0, i === ghosts.length - 1))}
                            </div>
                        );
                    })()}
                    {supportCount > 0 && (
                        <p className="text-[13px] mt-1">
                            <button onClick={() => onArtifacts(true)} className="font-semibold text-link hover:underline">
                                Supports this cluster — {supportCount} →</button>
                        </p>
                    )}
                </>
            )}
            {view === 'duties' && layered && (
                <>
                    <LaneLabel right={<button onClick={onAddObligation}
                        className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Add your own →</button>}>
                        Obligations</LaneLabel>
                    <LayeredObligations rows={rows} onOpen={onOpen} />
                </>
            )}
            {view === 'duties' && !layered && (
                <>
            <div className="flex gap-3.5 items-center flex-wrap mb-2.5">
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search obligations…"
                    className="text-[13px] h-9 px-3 w-60 border border-border-strong rounded-md bg-surface text-fg" />
                <select value={type} onChange={e => setType(e.target.value)}
                    className="text-[13px] font-semibold h-9 px-3 border border-border-strong rounded-md bg-surface text-fg-secondary">
                    <option value="all">Type: all</option>
                    {TYPE_ORDER.map(t => <option key={t}>{t}</option>)}
                </select>
                <span className="ml-auto">
                    <SegButtons small value={dview} onChange={v => setDview(v as 'simple' | 'detailed')}
                        options={[{ key: 'simple', label: 'Simple' }, { key: 'detailed', label: 'Detailed' }]} />
                </span>
            </div>

            <LaneLabel right={<button onClick={onAddObligation}
                className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Add your own →</button>}>
                Obligations</LaneLabel>
            {dview === 'simple' ? (
                /* Simple = the same open flat pattern as Detailed, fewer columns;
                   groups start OPEN and each list says what it holds (Kawi, 12 Jul) */
                <div className="mt-1">
                    {groups.map(t => {
                        const items = filtered.filter(r => r.obligation_type === t);
                        const isOpen = filtering || open[t] !== false;
                        return (
                            <div key={t} className="mb-[22px]">
                                <button onClick={() => setOpen(o => ({ ...o, [t]: !isOpen }))}
                                    className="w-full grid grid-cols-[minmax(220px,1fr)_130px] gap-3 items-center px-3.5 py-1.5 text-left">
                                    <span className="text-[11px] font-semibold tracking-[0.06em] uppercase text-primary hover:underline">
                                        {t}
                                        <span className="font-mono text-[11px] font-medium text-fg-tertiary normal-case ml-1">{items.length}</span>
                                        <span className="text-[11px] font-medium text-fg-tertiary normal-case tracking-normal ml-3">{TYPE_DESC[t]}</span>
                                    </span>
                                    {isOpen
                                        ? <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Status <span className="ml-2">▴</span></span>
                                        : <span className="text-right text-[11px] text-fg-tertiary">▾</span>}
                                </button>
                                {/* Simple stays CLEAN: title only, description on hover
                                    (visible description lines live in Detailed — Kawi, 12 Jul) */}
                                {isOpen && items.map((r, i) => (
                                    <button key={r.id} onClick={() => onOpen(r.id)}
                                        className={`w-full grid grid-cols-[minmax(220px,1fr)_130px] gap-3 items-center px-3.5 py-3 text-[13px] bg-surface border border-border-default text-left hover:bg-surface-sunken ${
                                            i > 0 ? 'border-t-0' : 'rounded-t-md'} ${i === items.length - 1 ? 'rounded-b-md' : ''}`}>
                                        {/* one list language (Kawi, 4 Aug) — same row bones as the
                                            register side: py-3, semibold names */}
                                        <span className="font-semibold text-fg truncate" title={r.plain_language || undefined}>{r.title}</span>
                                        {(r.wiring_waiting || 0) > 0 && (
                                            <span className="ml-2 text-[11px] font-semibold text-accent-copper whitespace-nowrap">
                                                {r.wiring_waiting} wiring waiting</span>)}
                                        <span className="text-right"><RowState r={r} /></span>
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* one list language — the clusters-list pattern: group header row
                   carries the column labels, then flat bordered rows */
                <div>
                    {TYPE_ORDER.filter(t => filtered.some(r => r.obligation_type === t)).map(t => {
                        const items = filtered.filter(r => r.obligation_type === t);
                        return (
                            <div key={t} className="mb-[22px]">
                                <div className="grid grid-cols-[112px_minmax(220px,1fr)_90px_110px_130px_90px] gap-3 items-center px-3.5 py-1.5">
                                    <span className="col-span-2 text-[11px] font-semibold tracking-[0.06em] uppercase text-primary">
                                        {t}
                                        <span className="font-mono text-[11px] font-medium text-fg-tertiary normal-case ml-1">{items.length}</span>
                                        <span className="text-[11px] font-medium text-fg-tertiary normal-case tracking-normal ml-3">{TYPE_DESC[t]}</span>
                                    </span>
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Citations</span>
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Next due</span>
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Status</span>
                                    <span className="text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Owner</span>
                                </div>
                                {items.map((r, i) => (
                                    <button key={r.id} onClick={() => onOpen(r.id)}
                                        className={`w-full grid grid-cols-[112px_minmax(220px,1fr)_90px_110px_130px_90px] gap-3 items-center px-3.5 py-3 text-[13px] bg-surface border border-border-default text-left hover:bg-surface-sunken ${
                                            i > 0 ? 'border-t-0' : 'rounded-t-md'} ${i === items.length - 1 ? 'rounded-b-md' : ''}`}>
                                        {/* the description lives on the obligation's own page, not in the
                                            table (Kawi, 12 Jul) — hover shows it here; row bones match
                                            the register side (Kawi, 4 Aug): py-3, semibold names */}
                                        <span className="font-mono text-[11px] text-fg-tertiary whitespace-nowrap">{r.id}</span>
                                        <span className="min-w-0">
                                            <span className="block font-semibold text-fg truncate" title={r.plain_language || undefined}>{r.title}</span>
                                            {/* WHO closes it, on the row (Kawi, 6 Aug: "we are
                                                missing what artifact is closing which obligation") */}
                                            {(r.answered_by || []).length > 0 && (
                                                <span className="block text-[11px] text-fg-tertiary truncate">
                                                    answered by {(r.answered_by || []).map(a => a.name).join(' · ')}</span>
                                            )}
                                        </span>
                                        {(r.wiring_waiting || 0) > 0 && (
                                            <span className="ml-2 text-[11px] font-semibold text-accent-copper whitespace-nowrap">
                                                {r.wiring_waiting} wiring waiting</span>)}
                                        <span className="text-right text-fg-secondary tabular-nums">{r.citations}</span>
                                        <span className="text-right">
                                            {r.next_due ? (<>
                                                <span className="block text-fg font-medium tabular-nums">{fmtDay(r.next_due)}</span>
                                                <span className="block text-[11px] text-fg-tertiary">
                                                    {r.due_kind === 'set' ? `set by ${r.override_by}` : r.cycle}{r.due_kind === 'period_end' ? ' · period end' : ''}</span>
                                                {r.lands_weekend && (
                                                    <span className="block text-[11px] font-semibold text-warning-ink">
                                                        weekend — shift rule not confirmed</span>
                                                )}
                                                {r.override_passed && (
                                                    <span className="block text-[11px] font-semibold text-warning-ink">
                                                        your set date passed — computed calendar resumed</span>
                                                )}
                                            </>) : r.cycle === 'event' ? (
                                                <span className="text-[11px] text-fg-tertiary">event-driven</span>
                                            ) : r.cycle === 'none' ? (
                                                <span className="text-[11px] text-fg-tertiary">no cycle</span>
                                            ) : r.cycle_waiting ? (
                                                <span className="text-[11px] font-semibold text-accent-copper">suggested — in Actions</span>
                                            ) : <span className="text-border-strong">—</span>}
                                        </span>
                                        <span className="text-right"><RowState r={r} table /></span>
                                        <span className="text-right text-[12px] text-fg-secondary truncate">
                                            {/* the duty's own named owner wins; the answering
                                                document's owner is the fallback (PR #608 review —
                                                duty_owner was typed but never rendered) */}
                                            {/* the two are DIFFERENT accountabilities — an
                                                auditor must not read a document's owner as the
                                                duty's owner (round 7) */}
                                            {r.duty_owner
                                                || (() => {
                                                    const via = (r.answered_by || []).find(a => a.owner);
                                                    return via
                                                        ? <span title={`owner of ${via.name}, not a named owner of this duty`}>
                                                            {via.owner} <span className="text-fg-tertiary">· via {via.name}</span></span>
                                                        : <span className="text-border-strong">—</span>;
                                                })()}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
            {dview === 'detailed' && (
                <p className="text-[11px] text-fg-tertiary mt-2.5">Next due = the signed report cycle's period end (a date says "due" only when a signed offset exists). Owner = the duty's named owner; without one, the owner of the entry that answers the row.</p>
            )}
                </>
            )}

            <div className="mt-[34px] pt-4 border-t border-border-default flex gap-[26px] items-center flex-wrap text-[13px]">
                <IdChip kind="cluster">{cluster.id.replace('CLUSTER-', 'CLUSTER · ')}</IdChip>
                <button onClick={onRecords} className="font-semibold text-link hover:underline">How this catalogue was verified →</button>
                {/* everything audit or the regulator asks for, one document
                    (Kawi, 6 Aug) — opens in a tab, print to PDF from there */}
                <button onClick={async () => {
                    const r = await fetch(`${API}/api/inventory/clusters/${cluster.id}/export`,
                        { headers: authHeaders() }).catch(() => null);
                    if (!r || !r.ok) return;
                    const blob = await r.blob();
                    window.open(URL.createObjectURL(blob), '_blank');
                }} className="font-semibold text-link hover:underline">Export — for audit or regulator →</button>
                <span className="text-fg-tertiary" title={cluster.source_instruments || 'Not in the app yet'}>
                    Legal sources{cluster.source_instruments ? ` · ${cluster.source_instruments.split(',').length}` : ''} →
                </span>
                <span className="ml-auto"><ReviewAsk kind="cluster" refId={cluster.id} signer={signer} /></span>
            </div>

            {startFor && (() => {
                const a = sorted.find(x => x.id === startFor);
                if (!a) return null;
                const obls = a.obligations || [];
                const target = obls.find(o => uncovered.has(o.id)) || obls[0];
                return (
                    <StartFromExampleModal art={a} signer={signer}
                        targetId={target.id}
                        also={obls.filter(o => o.id !== target.id).map(o => o.id)}
                        onDone={id => { setStartFor(null); if (id) onOpenArtifact(id); else setReload(x => x + 1); }}
                        onCancel={() => setStartFor(null)} />
                );
            })()}
        </Wrap>
    );
}

/* status words are neutral ink — the dot alone carries the colour (mock rule) */
function RowState({ r, table }: { r: OblRow; table?: boolean }) {
    /* the ceremony died (Kawi, 22 Jul) — a duty is in scope unless the
       bank dismissed it; rows show their real answered/open state */
    if (r.adoption === 'dismissed')
        return <span className="inline-flex items-center gap-[7px] text-[13px] text-fg-secondary whitespace-nowrap"><Dot tone="mute" /> dismissed</span>;
    /* fail-closed (16 Jul): an open flag beats every green — the row says
       review needed until a named person clears it */
    if (r.change_status && !['green', 'none'].includes(r.change_status))
        return <span className="inline-flex items-center gap-[7px] text-[13px] text-fg-secondary whitespace-nowrap"><Dot tone="a" /> review needed</span>;
    /* trust words only (Kawi, 6 Aug: "answered by my policy but still
       orange?"): an EXAMPLE's claim counts as nothing, so it never tints
       a status; and "compliant"/"assessed" said more than a signed answer
       proves. A row is answered, open, flagged or dismissed — nothing else. */
    if (r.confirmed > 0)
        return <span className="inline-flex items-center gap-[7px] text-[13px] text-fg-secondary whitespace-nowrap"><Dot tone="g" /> answered</span>;
    if (table)
        return <span className="inline-flex items-center gap-[7px] text-[13px] text-fg-secondary whitespace-nowrap"><Dot tone="mute" /> open</span>;
    return <span className="text-[11px] font-semibold text-link whitespace-nowrap">Answer →</span>;
}

/* ---------- the two-layer obligations list (29 Jul, duty-page-mock) ---------- */
/* Layer 1 = the parent-less rows, the short list. Click one to unfold what
   sits UNDER it — definitions (word), conditions (fine print), options,
   authority rows — with the mock's small type labels. dead + not-applicable
   rows collapse into the bottom "Switched off for you by your profile"
   block. Flat clusters (no row_class anywhere) never reach this component.
   Options render WITHOUT the "Use this option" act for now: electing an
   option must add its follow-up duty, and that option→follow-up linkage is
   not yet machine-readable — no half-built acts. */
const UNDER_LABEL: Record<string, string> = {
    definition: 'word', condition: 'fine print', option: 'option',
    authority: 'authority', duty: 'duty',
};
const UNDER_ORDER = ['definition', 'condition', 'option', 'authority', 'duty'];
function LayeredObligations({ rows, onOpen }: { rows: OblRow[]; onOpen: (id: string) => void }) {
    const [openId, setOpenId] = useState<string | null>(null);
    const [showOff, setShowOff] = useState(false);
    const { off, byId, byParent, parents, resolvedById } = useMemo(() => {
        const isOff = (r: OblRow) => r.row_class === 'dead' || r.row_class === 'not-applicable';
        const off = rows.filter(isOff);
        const byId = new Map(rows.map(r => [r.id, r]));
        const byParent = new Map<string, OblRow[]>();
        /* one row can appear under EVERY parent it belongs to. The old code
           read the single slot, so a condition qualifying twenty duties showed
           under one of them and was invisible under the other nineteen. */
        const parentsOf = (r: OblRow) => r.parent_ids !== undefined && r.parent_ids !== null
            ? r.parent_ids : (r.parent_id ? [r.parent_id] : []);
        /* FAIL-SAFE, and it is the whole point (PR #611 review): only parents
           that are ON THIS PAGE count. This page is one cluster, and a
           multi-parent row routinely names parents in other clusters — so a
           row whose parents are all elsewhere would have been drawn nowhere:
           not top-level, and under no visible parent. An obligation that
           exists in the register and appears on no page is not a display bug.
           Unresolvable parents are dropped, and a row left with none rises to
           the top of the list. mapGraph/model.ts already states this rule for
           the map; the list follows it now too.
           A row naming itself is not its own parent — that would drop it out
           of the top level and nest it under itself. */
        const rawResolved = (r: OblRow) => {
            const seen = new Set<string>();
            return parentsOf(r).filter(p => {
                if (p === r.id || !byId.has(p) || seen.has(p)) return false;
                if (isOff(byId.get(p)!)) return false;
                seen.add(p);
                return true;
            });
        };
        const rawResolvedById = new Map(rows.map(r => [r.id, rawResolved(r)]));
        const rootCache = new Map<string, boolean>();
        const reachesTop = (id: string, path = new Set<string>()): boolean => {
            const cached = rootCache.get(id);
            if (cached !== undefined) return cached;
            if (path.has(id)) return false;
            const ps = rawResolvedById.get(id) || [];
            if (ps.length === 0) {
                rootCache.set(id, true);
                return true;
            }
            path.add(id);
            const ok = ps.some(p => reachesTop(p, path));
            path.delete(id);
            if (ok) rootCache.set(id, true);
            return ok;
        };
        const resolvedById = new Map(rows.map(r => [
            r.id, (rawResolvedById.get(r.id) || []).filter(p => reachesTop(p)),
        ]));
        const resolved = (r: OblRow) => resolvedById.get(r.id) || [];
        rows.forEach(r => {
            if (isOff(r)) return;
            resolved(r).forEach(p => {
                const arr = byParent.get(p);
                if (arr) arr.push(r);
                else byParent.set(p, [r]);
            });
        });
        const parents = rows.filter(r => resolved(r).length === 0 && !isOff(r));
        return { off, byId, byParent, parents, resolvedById };
    }, [rows]);
    const resolved = (r: OblRow) => resolvedById.get(r.id) || [];
    /* conditional rows: the §L1-19a…d pattern, or a child of an option —
       they exist only IF the option is elected */
    const allParentsAreOptions = (r: OblRow) =>
        resolved(r).length > 0
        && resolved(r).every(p => byId.get(p)?.row_class === 'option');
    const isConditional = (r: OblRow, underParentId?: string) =>
        /L1-\d+[a-d]$/.test(r.source_label || '')
        /* EVERY parent, not SOME (PR #611 review): a row under an option
           AND a plain duty is required via the duty, so calling it
           "conditional" would tell a bank a mandatory row is optional. */
        || (underParentId
            ? byId.get(underParentId)?.row_class === 'option' && allParentsAreOptions(r)
            : allParentsAreOptions(r));
    const renderUnder = (parentId: string, path: Set<string>): React.ReactNode[] => {
        const under = (byParent.get(parentId) || []).filter(u =>
            !resolved(u).some(p => p !== parentId && path.has(p)));
        return UNDER_ORDER.filter(k => under.some(u => u.row_class === k)).flatMap(k =>
            under.filter(u => u.row_class === k).map(u => {
                const childPath = new Set<string>();
                path.forEach(p => childPath.add(p));
                childPath.add(u.id);
                const nested = path.has(u.id) ? [] : renderUnder(u.id, childPath);
                return (
                    <React.Fragment key={u.id}>
                        <button onClick={() => onOpen(u.id)}
                            className="w-full flex gap-3 items-baseline py-2 border-b border-divider text-left text-[13px] text-fg-secondary hover:bg-surface">
                            <span className={`text-[11px] font-medium uppercase min-w-[110px] flex-none ${
                                u.row_class === 'option' ? 'text-secondary-hover' : 'text-fg-tertiary'}`}>
                                {UNDER_LABEL[u.row_class || ''] || u.row_class}</span>
                            <span className="min-w-0">{u.title}
                                {isConditional(u, parentId) && (
                                    <span className="text-[11px] font-medium text-fg-tertiary"> · conditional — on option elected</span>
                                )}</span>
                        </button>
                        {nested.length > 0 && (
                            <div className="ml-5 border-l border-divider pl-4">
                                {nested}
                            </div>
                        )}
                    </React.Fragment>
                );
            }));
    };
    return (
        <div className="mt-1">
            <p className="text-[13px] text-fg-tertiary mb-2.5">
                The short list. Click an obligation to see the definitions, fine print
                and options that belong to it.</p>
            {/* one list language (Kawi, 4 Aug: "supposed to look so different?") —
                the layered list wears the SAME row bones as the register side:
                bordered box, 13px semibold names, 11px subtitles, status right */}
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
            {parents.map(r => {
                const under = byParent.get(r.id) || [];
                const isOpen = openId === r.id;
                return (
                    <div key={r.id} className={isOpen ? 'bg-surface-sunken' : ''}>
                        <button onClick={() => setOpenId(isOpen ? null : r.id)}
                            className="w-full flex justify-between items-center gap-4 px-3.5 py-3 text-left hover:bg-surface-sunken">
                            <span className="min-w-0">
                                <span className="block text-[13px] font-semibold text-fg">{r.title}</span>
                                {r.row_class && r.row_class !== 'duty' && (
                                    <span className="block text-[11px] font-medium uppercase text-fg-tertiary mt-0.5">
                                        {UNDER_LABEL[r.row_class] || r.row_class}</span>
                                )}
                                {isConditional(r) && (
                                    <span className="block text-[11px] font-medium uppercase text-fg-tertiary mt-0.5">
                                        conditional — on option elected</span>
                                )}
                                <span className="block text-[11px] text-fg-tertiary mt-0.5">
                                    {r.source_label || r.id}
                                    {under.length > 0 && ` · ${under.length} directly underneath`}
                                </span>
                            </span>
                            <span className="whitespace-nowrap"><RowState r={r} table /></span>
                        </button>
                        {isOpen && (
                            <div className="ml-9 mr-3.5 mb-2 border-t border-divider">
                                {under.length === 0 && (
                                    <p className="py-2 text-[12.5px] text-fg-tertiary">
                                        Nothing underneath — the obligation stands on its own.</p>
                                )}
                                {renderUnder(r.id, new Set([r.id]))}
                                <p className="py-2">
                                    <button onClick={() => onOpen(r.id)}
                                        className="text-[12px] font-semibold text-link hover:underline">
                                        Open the obligation page →</button>
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
            </div>
            {off.length > 0 && (
                <div className="mt-6 pt-3 border-t border-border-default text-[12.5px] text-fg-tertiary">
                    <b className="text-fg-secondary font-semibold">
                        Switched off for you by your profile ({off.length}):</b>{' '}
                    {off.slice(0, 3).map(r => r.title).join(' · ')}{off.length > 3 ? ' …' : ''}{' '}
                    <button onClick={() => setShowOff(s => !s)}
                        className="underline hover:text-fg-secondary">{showOff ? 'hide' : 'show'}</button>
                    {showOff && (
                        <div className="mt-2 border border-border-default rounded-lg bg-surface divide-y divide-divider">
                            {off.map(r => (
                                <button key={r.id} onClick={() => onOpen(r.id)}
                                    className="w-full flex gap-3 items-baseline px-3.5 py-2 text-left text-[12.5px] text-fg-secondary hover:bg-surface-sunken">
                                    <span className="font-mono text-[11px] text-fg-tertiary flex-none">{r.source_label || r.id}</span>
                                    <span className="min-w-0">{r.title}</span>
                                    <span className="ml-auto text-[11px] text-fg-tertiary whitespace-nowrap">
                                        {r.row_class === 'dead' ? 'no longer law' : 'off by your profile'}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* One document can answer many obligations — the pick lists below feed the
   own-answer walk (born links) and the "Also answers…" act (added links).
   The rows come from the live register, grouped per cluster. */
type PickGroup = { cluster: string; items: { id: string; title: string }[] };
function useObligationGroups(): PickGroup[] | null {
    const [groups, setGroups] = useState<PickGroup[] | null>(null);
    useEffect(() => {
        get<{ clusters: ClusterRow[] }>('/api/inventory/clusters').then(async d => {
            const detailed = d.clusters.filter(c => c.obligations > 0);
            const all = await Promise.all(detailed.map(c =>
                get<{ obligations: OblRow[] }>(`/api/inventory/clusters/${c.id}/obligations`)
                    // dismissed obligations take no answers (gap 6) — keep
                    // them out of every pick list
                    .then(r => ({ cluster: c.name, items: r.obligations
                        .filter(o => o.adoption !== 'dismissed')
                        .map(o => ({ id: o.id, title: o.title })) }))));
            setGroups(all);
        }).catch(() => setGroups([]));
    }, []);
    return groups;
}

function AlsoPickList({ locked, lockedLabel, selected, onToggle, tall }: {
    locked: string[]; lockedLabel: string; selected: string[]; onToggle: (id: string) => void;
    tall?: boolean;
}) {
    const groups = useObligationGroups();
    const [q, setQ] = useState('');
    const [openG, setOpenG] = useState<Record<string, boolean>>({});
    if (!groups) return <p className="text-[11px] text-fg-tertiary mt-2">Loading the obligations…</p>;
    /* scale rule (Kawi, 15 Jul: the flat list is unmanageable): search on
       top, clusters folded by default, your picked rows always visible */
    const term = q.trim().toLowerCase();
    const hit = (o: { id: string; title: string }) =>
        !term || o.title.toLowerCase().includes(term) || o.id.toLowerCase().includes(term);
    const chosen = groups.flatMap(g => g.items.filter(o => locked.includes(o.id) || selected.includes(o.id)));
    const row = (o: { id: string; title: string }) => {
        const isLocked = locked.includes(o.id);
        return (
            <label key={o.id} className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] ${
                isLocked ? 'text-fg-tertiary' : 'text-fg-secondary hover:bg-surface-sunken cursor-pointer'}`}>
                <input type="checkbox" checked={isLocked || selected.includes(o.id)}
                    disabled={isLocked} onChange={() => onToggle(o.id)} />
                <span className="truncate">{o.title}</span>
                {isLocked && <span className="text-[11px] text-fg-tertiary ml-auto whitespace-nowrap">{lockedLabel}</span>}
            </label>
        );
    };
    return (
        <div className={`border border-border-default rounded-md overflow-y-auto mt-2 ${tall ? 'max-h-[480px] bg-surface shadow-bark-xs' : 'max-h-72'}`}>
            <div className="p-2 bg-surface sticky top-0 border-b border-border-default">
                <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Search obligations…"
                    className="text-[13px] h-8 px-3 w-full border border-border-strong rounded-md bg-surface text-fg" />
            </div>
            {!term && chosen.length > 0 && (
                <div>
                    <div className="px-3 py-1.5 text-[11px] font-semibold tracking-[0.06em] uppercase text-fg-tertiary bg-surface-sunken">
                        Picked <span className="font-mono normal-case ml-1">{chosen.length}</span>
                    </div>
                    {chosen.map(row)}
                </div>
            )}
            {groups.map(g => {
                const inBody = g.items.filter(o => hit(o)
                    && (term ? true : !(locked.includes(o.id) || selected.includes(o.id))));
                if (term && inBody.length === 0) return null;
                const open = term ? true : !!openG[g.cluster];
                const pickedHere = g.items.filter(o => locked.includes(o.id) || selected.includes(o.id)).length;
                return (
                    <div key={g.cluster}>
                        <button onClick={() => setOpenG(s => ({ ...s, [g.cluster]: !open }))}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] font-semibold tracking-[0.06em] uppercase text-fg-tertiary bg-surface-sunken hover:text-fg-secondary">
                            {g.cluster}
                            <span className="font-mono normal-case">{g.items.length}</span>
                            {pickedHere > 0 && <span className="font-mono normal-case text-secondary-hover">{pickedHere} picked</span>}
                            <span className="ml-auto">{open ? '▴' : '▾'}</span>
                        </button>
                        {open && inBody.map(row)}
                    </div>
                );
            })}
        </div>
    );
}

/* The bank's OWN answer — name + kind + file + signature in one act
   (Kawi, 15 Jul: examples are usable suggestions, never the boundary).
   "Also answers": the same document can be linked to more obligations in
   the SAME signed act — one document, many links, born together. */
/* THE ONE ANSWER PANEL (redesign v3, Kawi "go ahead" 16 Jul): every door —
   the card, the lane act, "use as starting point" on an example — opens
   this single flat surface. Your document is always born as YOUR document;
   examples never convert (they stay suggestions forever). */
function AnswerPanel({ obligationId, signer, initialName, initialKind, initialAlso, onDone, onCancel, bare, oblTitles }: {
    /* no obligationId = a document-first act (kept general); today every
       caller passes one — the register door is AddOwnPage */
    obligationId?: string; signer: string;
    initialName?: string; initialKind?: string; fromExample?: string;
    initialAlso?: string[];
    /* fix 4 (Kawi, 16 Jul): the newborn document's id rides back so the
       caller can land on its page */
    onDone: (newId?: string) => void; onCancel: () => void;
    /* one-box modal (Kawi, 4 Aug: "way too messy"): embedded in the
       example modal the panel drops its own frame and title, lists the
       obligations by name, and keeps the pick list folded */
    bare?: boolean; oblTitles?: Record<string, string>;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    /* drop-first (list item 1, 6 Aug): the plain answer act starts at the
       FILE — BARK reads it before the signature and prefills the name,
       kind and further obligations it answers. The example flow keeps
       its prefilled name and the plain file input. */
    const dropFirst = !initialName;
    const [phase, setPhase] = useState<'drop' | 'reading' | 'form'>(dropFirst ? 'drop' : 'form');
    const [drag, setDrag] = useState(false);
    const [proposed, setProposed] = useState<{ obligation_id: string; title: string; quote: string }[]>([]);
    const [whyFor, setWhyFor] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [name, setName] = useState(initialName || '');
    const [kind, setKind] = useState(initialKind || 'Policy');
    const [file, setFile] = useState<File | null>(null);
    const [also, setAlso] = useState<string[]>(initialAlso || []);
    const [pickOpen, setPickOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    const takeFile = async (f: File) => {
        setFile(f); setPhase('reading'); setErr(undefined); setNote('');
        try {
            const fd = new FormData();
            fd.append('file', f);
            const r = await fetch(`${API}/api/inventory/artifacts/preview`,
                { method: 'POST', body: fd, headers: authHeaders() });
            if (!r.ok) throw new Error(`${r.status}`);
            const d = await r.json();
            if (d.readable) {
                if (!name.trim()) setName(d.name || '');
                setKind(d.kind || 'Policy');
                const extra = (d.obligations || [])
                    .filter((o: any) => o.obligation_id !== obligationId);
                setProposed(extra);
                setAlso(s => [...s, ...extra.map((o: any) => o.obligation_id)
                    .filter((id: string) => !s.includes(id))]);
            } else {
                setNote('BARK could not read this file — name it and pick yourself.');
            }
        } catch {
            setNote('BARK could not read this file right now — name it and pick yourself.');
        }
        setPhase('form');
    };
    const sign = async () => {
        if (!file || !name.trim()) { setErr('name and file are both required'); return; }
        setBusy(true); setErr(undefined);
        const fd = new FormData();
        fd.append('file', file); fd.append('name', name.trim()); fd.append('kind', kind);
        fd.append('by', signer);
        also.forEach(id => fd.append(obligationId ? 'also' : 'obligations', id));
        const url = obligationId
            ? `${API}/api/inventory/obligations/${obligationId}/own-answer`
            : `${API}/api/inventory/artifacts/own-document`;
        const r = await fetch(url, { method: 'POST', body: fd, headers: authHeaders() });
        setBusy(false);
        if (!r.ok) { setErr((await r.json()).detail || `${r.status}`); return; }
        const out = await r.json().catch(() => ({}));
        onDone(out.id);
    };
    const picked = obligationId ? [obligationId, ...also] : also;
    const knownTitles = oblTitles ? picked.map(id => oblTitles[id]).filter(Boolean) : [];
    const unknown = picked.length - knownTitles.length;
    const frame = bare ? 'mt-3' : 'border border-border-strong rounded-lg bg-surface shadow-bark-md px-5 py-4 mt-2.5';
    if (phase === 'drop') return (
        <div className={frame}>
            {!bare && <p className="text-[15px] font-semibold mb-3">Your answer to this obligation</p>}
            <label onDragOver={e => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={e => { e.preventDefault(); setDrag(false);
                    const f = e.dataTransfer.files?.[0]; if (f) takeFile(f); }}
                className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg px-6 py-10 cursor-pointer text-center ${
                    drag ? 'border-primary bg-primary-tint' : 'border-border-strong bg-surface-sunken hover:bg-bg-warm'}`}>
                <span className="text-[14px] font-semibold text-fg">Drop the file here — or click to choose</span>
                <span className="text-[12px] text-fg-tertiary">BARK reads it and prepares the act. Nothing counts until you sign.</span>
                <input type="file" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) takeFile(f); }} />
            </label>
            <div className="flex mt-3">
                <button onClick={onCancel} className="text-[11px] font-semibold text-fg-tertiary hover:underline ml-auto">Cancel</button>
            </div>
        </div>
    );
    if (phase === 'reading') return (
        <div className={frame}>
            {!bare && <p className="text-[15px] font-semibold mb-3">Your answer to this obligation</p>}
            <p className="text-[13px] text-fg-secondary py-6 text-center">
                BARK is reading <b className="text-fg">{file?.name}</b> — preparing the act…</p>
            <div className="flex">
                <button onClick={onCancel} className="text-[11px] font-semibold text-fg-tertiary hover:underline ml-auto">Cancel</button>
            </div>
        </div>
    );
    return (
        <div className={frame}>
            {/* tightened (29 Jul): no explainer sentence — the fields say it */}
            {!bare && <p className="text-[15px] font-semibold mb-3">Your answer to this obligation</p>}
            {note && <p className="text-[12px] text-warning-ink mb-2">{note}</p>}
            <div className="flex gap-3 items-center flex-wrap mb-3">
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Name…"
                    className="text-[13px] h-9 px-3 w-80 border border-border-strong rounded-md bg-surface text-fg" />
                <select value={kind} onChange={e => setKind(e.target.value)}
                    className="text-[13px] font-semibold h-9 px-3 border border-border-strong rounded-md bg-surface text-fg-secondary">
                    {(['Policy','Model','Data','Deliverable'] as const).map(k => (
                        <option key={k} value={k}>{KIND_LABEL[k] || k}</option>
                    ))}
                </select>
                {dropFirst
                    ? <span className="text-[12px] text-fg-tertiary"><span className="font-mono">{file?.name}</span></span>
                    : <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="text-[13px]" />}
            </div>
            {/* BARK's further picks (drop-first): untickable rows with the
                document's own sentence behind "why" — never auto-signed */}
            {proposed.length > 0 && (
                <div className="border border-border-default rounded-lg bg-surface divide-y divide-divider mb-2">
                    {proposed.map(o => (
                        <div key={o.obligation_id} className="px-3 py-2 text-[13px]">
                            <label className="flex items-center gap-2.5 cursor-pointer">
                                <input type="checkbox" checked={also.includes(o.obligation_id)}
                                    onChange={() => setAlso(s => s.includes(o.obligation_id)
                                        ? s.filter(x => x !== o.obligation_id) : [...s, o.obligation_id])} />
                                <span className="min-w-0 font-medium text-fg">also answers {o.title}</span>
                                <button onClick={e => { e.preventDefault();
                                    setWhyFor(w => w === o.obligation_id ? null : o.obligation_id); }}
                                    className="text-[11px] font-semibold text-link hover:underline ml-auto whitespace-nowrap">
                                    {whyFor === o.obligation_id ? 'hide' : 'why →'}</button>
                            </label>
                            {whyFor === o.obligation_id && o.quote && (
                                <p className="text-[12px] text-fg-secondary mt-1.5 mb-0 pl-6">“{o.quote}”</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {oblTitles ? (
                <p className="text-[13px] text-fg-secondary">
                    Answers <b className="text-fg">{picked.length} obligation{plural(picked.length, { one: '', other: 's' })}</b>:{' '}
                    {knownTitles.join(' · ')}{unknown > 0 ? ` (+${unknown} more)` : ''}{' '}
                    <button onClick={() => setPickOpen(o => !o)} className="text-[13px] font-semibold text-link hover:underline">
                        {pickOpen ? 'hide' : 'change →'}</button>
                </p>
            ) : (
            <p className="text-[13px] text-fg-secondary">
                {obligationId ? 'Also answers: ' : 'Answers: '}
                <b className="text-fg">{also.length
                    ? `${also.length}${obligationId ? ' more' : ''} obligation${also.length > 1 ? 's' : ''}`
                    : obligationId ? 'nothing else yet' : 'nothing yet — it stands in the register on its own'}</b>{' '}
                <button onClick={() => setPickOpen(o => !o)} className="text-[13px] font-semibold text-link hover:underline">
                    {pickOpen ? 'hide the list' : obligationId ? 'pick more obligations →' : 'pick obligations →'}</button>
            </p>
            )}
            {pickOpen && <AlsoPickList locked={obligationId ? [obligationId] : []}
                lockedLabel={obligationId ? 'this page' : ''} selected={also}
                onToggle={id => setAlso(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])} />}
            {err && <p className="text-[11px] text-warning-ink mt-2">{err}</p>}
            {/* shape A (5 Aug): the act is instant; the read runs after */}
            <p className="text-[11px] text-fg-tertiary mt-2">
                After you sign, BARK reads the document — what it finds lands in Actions as suggestions.</p>
            <div className="flex gap-3.5 items-center mt-4 pt-4 border-t border-divider">
                {isSigner ? (
                    <button onClick={sign} disabled={busy}
                    className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                    {busy ? 'Signing…' : `Sign as ${signer} →`}</button>
                ) : <WaitingForSigner />}
                <span className="text-[11px] text-fg-tertiary">
                    {bare ? 'one signature · on record'
                        : `one signature · ${1 + also.length} obligation${also.length ? 's' : ''} · withdrawable, on record`}</span>
                <button onClick={onCancel} className="text-[11px] font-semibold text-fg-tertiary hover:underline ml-auto">Cancel</button>
            </div>
        </div>
    );
}

/* "Also answers…" — link an EXISTING bank document to more obligations
   (Kawi, 15 Jul: for what you learn after the upload; 6 Aug: "i like
   tickboxes better" — tick several, sign ONCE, one record naming all). */
function LinkWalk({ artifactId, exclude, signer, onDone, onCancel }: {
    artifactId: string; exclude: string[]; signer: string;
    onDone: () => void; onCancel: () => void;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const groups = useObligationGroups();
    const [sels, setSels] = useState<string[]>([]);
    const [q, setQ] = useState('');
    const [openG, setOpenG] = useState<Record<string, boolean>>({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    const toggle = (id: string) =>
        setSels(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
    const link = async () => {
        if (sels.length === 0) return;
        setBusy(true); setErr(undefined);
        const r = await fetch(`${API}/api/inventory/artifacts/${artifactId}/link`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ obligation_ids: sels, by: signer }),
        });
        setBusy(false);
        if (!r.ok) { setErr((await r.json()).detail || `${r.status}`); return; }
        onDone();
    };
    /* same scale rule as the pick list: search on top, clusters folded */
    const term = q.trim().toLowerCase();
    const hit = (o: { id: string; title: string }) =>
        !term || o.title.toLowerCase().includes(term) || o.id.toLowerCase().includes(term);
    const pickable = groups
        ?.map(g => ({ ...g, items: g.items.filter(o => !exclude.includes(o.id)) }))
        .filter(g => g.items.length > 0);
    const rowBtn = (o: { id: string; title: string }) => {
        const on = sels.includes(o.id);
        return (
            <label key={o.id}
                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-[13px] cursor-pointer ${
                    on ? 'bg-primary-tint text-primary font-semibold' : 'text-fg-secondary hover:bg-surface-sunken'}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(o.id)} className="shrink-0" />
                <span className="truncate">{o.title}</span>
            </label>
        );
    };
    return (
        <div className="border border-border-strong rounded-lg bg-surface shadow-bark-xs px-3.5 py-3 mt-2">
            <p className="text-[13px] font-bold mb-1">Also answers <span className="font-normal text-fg-tertiary">— tick every obligation this entry also answers; one signature adds them all</span></p>
            {!pickable && <p className="text-[11px] text-fg-tertiary">Loading the obligations…</p>}
            {pickable && pickable.length === 0 && (
                <p className="text-[11px] text-fg-tertiary">This entry already answers every obligation in the register.</p>
            )}
            {pickable && pickable.length > 0 && (
                <div className="border border-border-default rounded-md max-h-72 overflow-y-auto mt-2">
                    <div className="p-2 bg-surface sticky top-0 border-b border-border-default">
                        <input value={q} onChange={e => setQ(e.target.value)}
                            placeholder="Search obligations…"
                            className="text-[13px] h-8 px-3 w-full border border-border-strong rounded-md bg-surface text-fg" />
                    </div>
                    {pickable.map(g => {
                        const inBody = g.items.filter(hit);
                        if (term && inBody.length === 0) return null;
                        const open = term ? true : !!openG[g.cluster];
                        return (
                            <div key={g.cluster}>
                                <button onClick={() => setOpenG(s => ({ ...s, [g.cluster]: !open }))}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] font-semibold tracking-[0.06em] uppercase text-fg-tertiary bg-surface-sunken hover:text-fg-secondary">
                                    {g.cluster}
                                    <span className="font-mono normal-case">{g.items.length}</span>
                                    <span className="ml-auto">{open ? '▴' : '▾'}</span>
                                </button>
                                {open && inBody.map(rowBtn)}
                            </div>
                        );
                    })}
                </div>
            )}
            {err && <p className="text-[11px] text-warning-ink mt-2">{err}</p>}
            <div className="flex gap-3 items-center mt-3">
                {isSigner ? (
                    <button onClick={link} disabled={busy || sels.length === 0}
                    className="h-8 px-4 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-50">
                    {busy ? 'Signing…' : sels.length > 0
                        ? `Add ${sels.length} link${plural(sels.length, { one: '', other: 's' })} — as ${signer}`
                        : `Sign — as ${signer}`}</button>
                ) : <WaitingForSigner />}
                {sels.length > 0 && <span className="text-[11px] text-fg-tertiary">{sels.length} ticked</span>}
                <button onClick={onCancel} className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
            </div>
        </div>
    );
}

/* "Comes from" — connect two bank documents: this one comes from that one
   (Kawi, 15 Jul: the limits come from the appetite). One pick, one
   signature, one record. The engine refuses self, duplicates, circles
   and anything that is not the bank's own. */
function ComesFromWalk({ artifactId, exclude, signer, onDone, onCancel }: {
    artifactId: string; exclude: string[]; signer: string;
    onDone: () => void; onCancel: () => void;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [docs, setDocs] = useState<ArtifactRow[] | null>(null);
    const [sel, setSel] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    useEffect(() => {
        get<{ artifacts: ArtifactRow[] }>('/api/inventory/artifacts')
            .then(d => setDocs(d.artifacts)).catch(() => setDocs([]));
    }, []);
    const connect = async () => {
        if (!sel) return;
        setBusy(true); setErr(undefined);
        const r = await fetch(`${API}/api/inventory/artifacts/${artifactId}/comes-from`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ source_id: sel, by: signer }),
        });
        setBusy(false);
        if (!r.ok) { setErr((await r.json()).detail || `${r.status}`); return; }
        onDone();
    };
    const pickable = docs?.filter(a =>
        a.data_origin === 'real' && a.id !== artifactId && !exclude.includes(a.id));
    return (
        <div className="border border-border-strong rounded-lg bg-surface shadow-bark-xs px-3.5 py-3 mt-2">
            <p className="text-[13px] font-bold mb-1">Governed by <span className="font-normal text-fg-tertiary">— pick the entry that governs this one; the connection is a signed act</span></p>
            {!pickable && <p className="text-[11px] text-fg-tertiary">Loading your register…</p>}
            {pickable && pickable.length === 0 && (
                <p className="text-[11px] text-fg-tertiary">No other entry of your own to connect to — add or claim one first.</p>
            )}
            {pickable && pickable.length > 0 && (
                <div className="border border-border-default rounded-md max-h-56 overflow-y-auto mt-2">
                    {pickable.map(a => (
                        <button key={a.id} onClick={() => setSel(sel === a.id ? null : a.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-[13px] ${
                                sel === a.id ? 'bg-primary-tint text-primary font-semibold' : 'text-fg-secondary hover:bg-surface-sunken'}`}>
                            <span className="truncate">{a.name}</span>
                            <span className="text-[11px] text-fg-tertiary ml-auto whitespace-nowrap">{KIND_LABEL[a.kind] || a.kind}{sel === a.id ? ' · selected ✓' : ''}</span>
                        </button>
                    ))}
                </div>
            )}
            {err && <p className="text-[11px] text-warning-ink mt-2">{err}</p>}
            <div className="flex gap-3 items-center mt-3">
                {isSigner ? (
                    <button onClick={connect} disabled={busy || !sel}
                    className="h-8 px-4 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-50">
                    {busy ? 'Signing…' : `Sign — as ${signer}`}</button>
                ) : <WaitingForSigner />}
                <button onClick={onCancel} className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
            </div>
        </div>
    );
}

/* "Add your own" the way the user said it (Kawi, 6 Aug: "i just want to
   drag and drop or upload — bark does the wiring and then sign and
   done") — as a PAGE with room. BULK from birth (item 6, same day):
   drop ONE file or MANY — each is read by BARK before anything is
   signed; every file becomes a review row (name, kind, the obligations
   it answers with quotes); one click signs them all, one signed act per
   document, in order. Drafts live only here — nothing persists before
   the signature. Unreadable file or model down → that row goes manual,
   never a dead end. */
type DraftPick = { obligation_id: string; title: string; why?: string; quote?: string; source: 'bark' | 'me' };
type DraftDoc = {
    file: File; status: 'reading' | 'ready' | 'manual' | 'signing' | 'done';
    name: string; kind: string; picks: DraftPick[]; ticked: string[];
    note: string; err: string;
};
const BULK_CAP = 20;   // visible cap — reading N files costs N model reads

function AddOwnPage({ signer, onBack, onDone }: {
    signer: string; onBack: () => void; onDone: (newId?: string) => void;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const groups = useObligationGroups();
    const [docs, setDocs] = useState<DraftDoc[]>([]);
    const [drag, setDrag] = useState(false);
    const [capNote, setCapNote] = useState('');
    const [whyFor, setWhyFor] = useState<string | null>(null);   // `${index}:${oid}`
    const [addQFor, setAddQFor] = useState<Record<number, string>>({});
    const [busy, setBusy] = useState(false);

    const patch = (i: number, up: Partial<DraftDoc>) =>
        setDocs(s => s.map((d, j) => (j === i ? { ...d, ...up } : d)));

    const takeFiles = (list: FileList | File[]) => {
        const incoming = Array.from(list);
        if (!incoming.length) return;
        const room = BULK_CAP - docs.length;
        if (incoming.length > room) {
            setCapNote(`${incoming.length - room} file(s) left out — ${BULK_CAP} per batch; sign these first, then drop the rest.`);
        } else { setCapNote(''); }
        const taken = incoming.slice(0, Math.max(0, room));
        const base = docs.length;
        setDocs(s => [...s, ...taken.map(f => ({
            file: f, status: 'reading' as const, name: '', kind: 'Policy',
            picks: [], ticked: [], note: '', err: '',
        }))]);
        taken.forEach((f, k) => readOne(base + k, f));
    };

    const readOne = async (i: number, f: File) => {
        try {
            const fd = new FormData();
            fd.append('file', f);
            const r = await fetch(`${API}/api/inventory/artifacts/preview`,
                { method: 'POST', body: fd, headers: authHeaders() });
            if (!r.ok) throw new Error(`${r.status}`);
            const d = await r.json();
            if (!d.readable) {
                patch(i, { status: 'manual', note: 'BARK could not read this file — name it and pick yourself.' });
                return;
            }
            const picks: DraftPick[] = (d.obligations || []).map((o: any) => ({ ...o, source: 'bark' }));
            patch(i, { status: 'ready', name: d.name || '', kind: d.kind || 'Policy',
                       note: d.kind_raw ? `BARK called it "${d.kind_raw}" — not a register kind, set to Policy; change it if wrong.` : '',
                       picks, ticked: picks.map(o => o.obligation_id) });
        } catch {
            patch(i, { status: 'manual', note: 'BARK could not read this file right now — name it and pick yourself.' });
        }
    };

    /* one click, one signed act per document, in order — a failed row
       keeps its error and the button retries what remains. The loop works
       on a SNAPSHOT taken at the click and tracks the outcome locally:
       state updaters stay pure (the batch-test agent caught onDone firing
       inside one — React may replay updaters), and an unmount mid-batch
       stops the loop instead of stalling it. */
    const aliveRef = useRef(true);
    /* the effect BODY must set true: StrictMode runs mount→cleanup→mount in
       dev, and a cleanup-only effect left the flag false from birth — every
       sign quit on its first line with the button frozen (6 Aug, the
       "Signing… forever" hunt) */
    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);
    const signAll = async () => {
        setBusy(true);
        const snapshot = docs;
        let lastId: string | undefined;
        let failed = 0, remaining = 0;
        for (let i = 0; i < snapshot.length; i++) {
            if (!aliveRef.current) return;   // the page is gone — stop cleanly
            const d = snapshot[i];
            if (d.status === 'done' || d.status === 'reading' || d.status === 'signing') {
                if (d.status !== 'done') remaining++;
                continue;
            }
            if (!d.name.trim()) { failed++; patch(i, { err: 'a name is required' }); continue; }
            patch(i, { status: 'signing', err: '' });
            const fd = new FormData();
            fd.append('file', d.file); fd.append('name', d.name.trim()); fd.append('kind', d.kind);
            fd.append('by', signer);
            d.ticked.forEach(id => fd.append('obligations', id));
            // a dead connection must never freeze the page (6 Aug: 'Signing…'
            // hung for 10 minutes on a killed socket) — 60s is generous for a
            // graph write; on timeout the row says what to do
            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), 60_000);
            const r = await fetch(`${API}/api/inventory/artifacts/own-document`,
                { method: 'POST', body: fd, headers: authHeaders(), signal: ctrl.signal })
                .catch(() => null)
                .finally(() => clearTimeout(timer));
            if (!aliveRef.current) return;
            if (!r || !r.ok) {
                const msg = r ? ((await r.json().catch(() => ({}))).detail || `${r.status}`)
                    : 'no answer from the engine — check the Register before retrying (the same file can never be added twice)';
                failed++;
                patch(i, { status: d.note ? 'manual' : 'ready', err: String(msg) });
                continue;
            }
            lastId = ((await r.json().catch(() => ({}))) as any).id;
            patch(i, { status: 'done' });
        }
        setBusy(false);
        if (failed === 0 && remaining === 0) {
            // one file → its page; a batch → back to the register
            onDone(snapshot.length === 1 ? lastId : undefined);
        }
    };

    const signable = docs.filter(d => d.status === 'ready' || d.status === 'manual');
    const reading = docs.filter(d => d.status === 'reading').length;
    const doneN = docs.filter(d => d.status === 'done').length;
    const tickedTotal = signable.reduce((n, d) => n + d.ticked.length, 0);

    const crumb = (
        <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
            <button onClick={onBack} className="text-link">← Back</button>
            <span className="text-border-strong">·</span>
            <button onClick={onBack} className="text-link">Register</button>
            <span className="text-border-strong">/</span>
            <span className="text-fg font-semibold">Add your own</span>
        </div>
    );

    if (docs.length === 0) return (
        <Wrap>
            {crumb}
            <PageTitle>Add your own</PageTitle>
            <PageSub>Drop one file or a whole folder's worth — BARK reads each one and prepares the acts; you sign once. Nothing counts until you sign.</PageSub>
            <label onDragOver={e => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={e => { e.preventDefault(); setDrag(false); takeFiles(e.dataTransfer.files); }}
                className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg px-8 py-20 cursor-pointer text-center ${
                    drag ? 'border-primary bg-primary-tint' : 'border-border-strong bg-surface-sunken hover:bg-bg-warm'}`}>
                <span className="text-[15px] font-semibold text-fg">Drop files here — or click to choose</span>
                <span className="text-[12.5px] text-fg-tertiary">txt, md, csv and pdf are read; other formats can still be signed in by hand · up to {BULK_CAP} per batch</span>
                <input type="file" multiple className="hidden"
                    onChange={e => { if (e.target.files) takeFiles(e.target.files); }} />
            </label>
        </Wrap>
    );

    return (
        <Wrap>
            {crumb}
            <PageTitle>Add your own</PageTitle>
            <p className="text-[12.5px] text-fg-secondary mb-1 mt-1">
                {reading > 0
                    ? <>BARK is reading — <b className="text-fg">{reading}</b> of {docs.length} still in progress. Rows become editable as they finish.</>
                    : doneN === docs.length
                    ? <>All signed.</>
                    : <>Every row is prepared. Untick what is wrong, fix names, then one click signs them all.</>}
            </p>
            {capNote && <p className="text-[12px] text-warning-ink mb-2">{capNote}</p>}
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mb-4">
                {docs.map((d, i) => (
                    <div key={i} className="px-3.5 py-3">
                        <div className="flex gap-3 items-center flex-wrap">
                            <span className="font-mono text-[11px] text-fg-tertiary w-56 truncate">{d.file.name}</span>
                            {d.status === 'reading' ? (
                                <span className="text-[12.5px] text-fg-secondary">BARK is reading…</span>
                            ) : d.status === 'done' ? (
                                <span className="text-[12px] font-semibold text-secondary-hover">✓ signed{d.ticked.length ? ` · ${d.ticked.length} obligation${d.ticked.length > 1 ? 's' : ''}` : ''}</span>
                            ) : (
                                <>
                                    <input value={d.name} onChange={e => patch(i, { name: e.target.value })} placeholder="Name…"
                                        className="text-[13px] h-8 px-3 w-72 border border-border-strong rounded-md bg-surface text-fg" />
                                    <select value={d.kind} onChange={e => patch(i, { kind: e.target.value })}
                                        className="text-[13px] font-semibold h-8 px-2.5 border border-border-strong rounded-md bg-surface text-fg-secondary">
                                        {(['Policy','Model','Data','Deliverable'] as const).map(k => (
                                            <option key={k} value={k}>{KIND_LABEL[k] || k}</option>
                                        ))}
                                    </select>
                                    <span className="text-[12px] text-fg-tertiary">{d.ticked.length ? `${d.ticked.length} obligation${d.ticked.length > 1 ? 's' : ''}` : 'no obligations'}</span>
                                </>
                            )}
                        </div>
                        {d.note && d.status !== 'done' && <p className="text-[12px] text-warning-ink mt-1.5 mb-0">{d.note}</p>}
                        {d.err && <p className="text-[11px] text-warning-ink mt-1.5 mb-0">{d.err}</p>}
                        {(d.status === 'ready' || d.status === 'manual') && (
                            <div className="mt-2">
                                {d.picks.map(o => (
                                    <div key={o.obligation_id} className="py-1 text-[13px]">
                                        <label className="flex items-center gap-2.5 cursor-pointer">
                                            <input type="checkbox" checked={d.ticked.includes(o.obligation_id)}
                                                onChange={() => patch(i, { ticked: d.ticked.includes(o.obligation_id)
                                                    ? d.ticked.filter(x => x !== o.obligation_id)
                                                    : [...d.ticked, o.obligation_id] })} />
                                            <span className="min-w-0 text-fg">answers {o.title}</span>
                                            {o.source === 'bark' && o.quote ? (
                                                <button onClick={e => { e.preventDefault();
                                                    setWhyFor(w => w === `${i}:${o.obligation_id}` ? null : `${i}:${o.obligation_id}`); }}
                                                    className="text-[11px] font-semibold text-link hover:underline ml-auto whitespace-nowrap">
                                                    {whyFor === `${i}:${o.obligation_id}` ? 'hide' : 'why →'}</button>
                                            ) : (
                                                <span className="text-[11px] text-fg-tertiary ml-auto whitespace-nowrap">added by you</span>
                                            )}
                                        </label>
                                        {whyFor === `${i}:${o.obligation_id}` && o.quote && (
                                            <p className="text-[12px] text-fg-secondary mt-1 mb-0 pl-6">“{o.quote}”</p>
                                        )}
                                    </div>
                                ))}
                                <input value={addQFor[i] || ''} onChange={e => setAddQFor(s => ({ ...s, [i]: e.target.value }))}
                                    placeholder="Add more — search obligations…"
                                    className="text-[12.5px] h-7 px-2.5 mt-1 w-80 border border-border-strong rounded-md bg-surface text-fg" />
                                {(addQFor[i] || '').trim() && groups && groups.flatMap(g => g.items)
                                    .filter(o => !d.picks.some(x => x.obligation_id === o.id)
                                        && o.title.toLowerCase().includes((addQFor[i] || '').trim().toLowerCase()))
                                    .slice(0, 6).map(o => (
                                        <button key={o.id} onClick={() => {
                                            patch(i, { picks: [...d.picks, { obligation_id: o.id, title: o.title, source: 'me' }],
                                                       ticked: [...d.ticked, o.id] });
                                            setAddQFor(s => ({ ...s, [i]: '' }));
                                        }} className="block w-full text-left px-2 py-1 text-[12.5px] text-fg hover:bg-surface-sunken rounded">
                                            + {o.title}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="flex gap-3.5 items-center">
                {isSigner ? (
                    <button onClick={signAll} disabled={busy || reading > 0 || signable.length === 0}
                        className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                        {busy ? 'Signing…'
                            : `Sign ${signable.length} document${plural(signable.length, { one: '', other: 's' })} — as ${signer} →`}</button>
                ) : <WaitingForSigner />}
                <span className="text-[11px] text-fg-tertiary">
                    one signed act per document · {tickedTotal} obligation link{plural(tickedTotal, { one: '', other: 's' })} in total · on record</span>
                <button onClick={onBack} className="text-[11px] font-semibold text-fg-tertiary hover:underline ml-auto">Cancel</button>
            </div>
        </Wrap>
    );
}


/* Long signed descriptions fold — display only, the signed text is never
   changed (Kawi, 15 Jul: one truth folded, not two texts maintained). */
function FoldingText({ text }: { text: string | null }) {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    const LIMIT = 380;
    if (text.length <= LIMIT || open) {
        return (
            <p className="text-[15px] text-fg-secondary max-w-[72ch] mt-1 mb-2.5">
                {text}
                {text.length > LIMIT && (
                    <>{' '}<button onClick={() => setOpen(false)} className="text-[13px] font-semibold text-link hover:underline">Show less</button></>
                )}
            </p>
        );
    }
    // cut at the last sentence end before the limit, so the fold never
    // breaks mid-sentence
    const head = text.slice(0, LIMIT);
    const cut = Math.max(head.lastIndexOf('. '), head.lastIndexOf('." '));
    const shown = cut > 80 ? text.slice(0, cut + 1) : head;
    return (
        <p className="text-[15px] text-fg-secondary max-w-[72ch] mt-1 mb-2.5">
            {shown}{' '}
            <button onClick={() => setOpen(true)} className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Read the full text →</button>
        </p>
    );
}

/* ---------- page 3: one obligation ---------- */

/* THE WIRING (Kawi, 20 Jul: "are we sure those 9 is enough" — the links are
   earned by evidence): which profile facts this obligation's applicability
   depends on. BARK proposes with a reason; a named signer decides; approved
   links live on the obligation and fire flags when the fact changes. */
function WiringBlock({ obligationId, signer, onActions }: { obligationId: string; signer: string; onActions?: () => void }) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    type WState = {
        read_at?: string | null;
        /* auto_added (26 Jul): watch wiring inserts itself — the entry says
           so honestly, with when and from which proposal (the audit trail) */
        reads_facts: { key: string; label: string; why?: string | null; anchor?: string | null;
                       auto_added?: boolean; auto_added_at?: string | null; wp_id?: string | null }[];
        candidates: { wp_id: string; fact_key: string; label: string;
                      why?: string | null; anchor?: string | null }[];
        rejected: { wp_id: string; fact_key: string; label: string }[];
    };
    const [w, setW] = useState<WState | null>(null);
    /* the bank's CURRENT value of a proposed fact — so the verdict is judged
       against reality, not in the abstract (mock blessed 20 Jul) */
    const [factsNow, setFactsNow] = useState<Record<string, string | null>>({});
    useEffect(() => {
        get<{ facts?: Record<string, string | null> }>('/api/inventory/profile')
            .then(d => setFactsNow(d.facts || {})).catch(() => setFactsNow({}));
    }, []);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState('');
    const [err2, setErr2] = useState('');
    const [whyFor, setWhyFor] = useState<string | null>(null);
    /* no one-click runs (Kawi, 22 Jul: "i dont want people to click once
       and something runs") — every read asks first, Cancel visible */
    const [confirmRun, setConfirmRun] = useState(false);
    const load = React.useCallback(() => {
        get<WState>(`/api/inventory/obligations/${obligationId}/wiring`)
            .then(setW).catch(() => setW(null));
    }, [obligationId]);
    useEffect(() => { load(); }, [load]);
    /* the conclusion (Kawi, 22 Jul: "but what does it mean") — BARK's
       reading of the approved facts against this bank's answers; quiet
       when there is nothing to say, never an error */
    const [meaningTxt, setMeaningTxt] = useState('');
    const readsN = w ? w.reads_facts.length : 0;
    useEffect(() => {
        if (readsN === 0) { setMeaningTxt(''); return; }
        get<{ text?: string }>(`/api/inventory/obligations/${obligationId}/meaning`)
            .then(d => setMeaningTxt(d.text || '')).catch(() => setMeaningTxt(''));
    }, [obligationId, readsN]);
    const proposeRun = async () => {
        setBusy(true); setErr2(''); setNote('');
        try {
            const r = await fetch(`${API}/api/inventory/obligations/${obligationId}/wiring/propose`, {
                method: 'POST', headers: authHeaders(),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            const d2 = await r.json();
            const n = d2.stored?.length ?? 0;
            setNote(n > 0 ? `BARK found ${n} deciding fact${plural(n, { one: '', other: 's' })} — watching added by itself, honestly labelled; remove any with a reason.`
                          : 'Nothing decides it — this obligation applies to every bank. BARK read the signed text and found no condition.');
            load();
        } catch (e) { setErr2(String(e)); }
        setBusy(false);
    };
    /* the remove act on auto-added watching (26 Jul, mock panel 1) —
       written reason required; the removal is a durable rejected record */
    const [removeFor, setRemoveFor] = useState<string | null>(null);
    const [removeReason, setRemoveReason] = useState('');
    const [removeBusy, setRemoveBusy] = useState(false);
    const removeAct = async (wpId: string) => {
        setRemoveBusy(true); setErr2('');
        try {
            const r = await fetch(`${API}/api/inventory/wiring/${wpId}/remove`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ reason: removeReason.trim(), by: signer }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
            setRemoveFor(null); setRemoveReason('');
            load();
        } catch (e) { setErr2(String(e)); }
        setRemoveBusy(false);
    };
    if (!w) return null;
    const nothing = w.reads_facts.length === 0 && w.candidates.length === 0;
    return (
        <div className="text-[12.5px] text-fg-secondary mb-1">
            {/* the section is named by what it DOES (Kawi, 4 Aug: header and
                rows must speak the same sentence) — the word appears once */}
            <LaneLabel>Watching</LaneLabel>
            {/* the conclusion LEADS, the plumbing folds (Kawi, 22 Jul:
                "its good but still too messy"); user's words only */}
            {w.reads_facts.length > 0 && meaningTxt && (
                <p className="text-[13px] text-fg max-w-[80ch] mb-1">
                    <b className="font-semibold">For your bank:</b> {meaningTxt}
                    <span className="text-[11px] text-fg-tertiary"> — BARK's reading, from your approved answers</span>
                </p>
            )}
            {/* v2 (29 Jul, obligation-page-v2): ONE sentence per watched
                fact; the full card (basis, this-bank-now, remove with a
                reason) lives behind the quiet details link */}
            {w.reads_facts.map(f => (
                <div key={f.key} className="py-2 border-b border-divider max-w-[80ch]">
                    <p className="flex items-baseline gap-3 text-[13px]">
                        <span className="min-w-0 cursor-pointer"
                            onClick={() => setWhyFor(whyFor === f.key ? null : f.key)}>
                            <b className="text-fg font-semibold">{f.label}</b> —
                            if it changes, this obligation is flagged for review.</span>
                        <span className="ml-auto whitespace-nowrap text-[12px] text-fg-tertiary">
                            {/* the reveal is findable (Kawi, 6 Aug: "too
                                hidden"); the remove stays quiet on purpose */}
                            <button onClick={() => setWhyFor(whyFor === f.key ? null : f.key)}
                                className="font-semibold text-link hover:underline">details</button>
                            {isSigner && f.auto_added && f.wp_id && (<>
                                {' · '}
                                <button onClick={() => { setWhyFor(f.key); setRemoveFor(removeFor === f.wp_id ? null : (f.wp_id || null)); setRemoveReason(''); }}
                                    className="underline hover:text-fg-secondary">remove — with a reason</button>
                            </>)}
                        </span>
                    </p>
                    {whyFor === f.key && (
                        <div className="mt-2 px-4 py-3 rounded-md border border-border-default bg-surface-sunken text-[12.5px]">
                            {f.why && <p><b className="text-fg font-semibold">{f.label}:</b> {f.why}</p>}
                            {f.anchor && <p className="mt-1"><b className="text-fg font-semibold">Basis:</b> {f.anchor} — pinned under Sources below</p>}
                            {factsNow[f.key] != null && (
                                <p className="mt-1"><b className="text-fg font-semibold">This bank now:</b> {f.label} = {prettyValue(f.key, String(factsNow[f.key]))}</p>
                            )}
                            <p className="text-[11px] text-fg-tertiary mt-1.5">
                                {f.auto_added
                                    ? 'Auto-added from the signed catalogue · not individually confirmed · remove anytime'
                                    : 'Approved by a named signer'}
                                {' '}· watching adds review flags only — it never changes what you must do</p>
                            {f.auto_added && removeFor === f.wp_id && f.wp_id && (
                                <span className="inline-flex items-center gap-3 flex-wrap mt-2">
                                    <input value={removeReason} onChange={e => setRemoveReason(e.target.value)}
                                        placeholder="why this obligation should not watch it — required"
                                        className="h-8 px-2.5 w-72 border border-border-strong rounded-md bg-surface text-fg text-[12.5px]" />
                                    <button onClick={() => f.wp_id && removeAct(f.wp_id)}
                                        disabled={!removeReason.trim() || removeBusy}
                                        className="text-[12px] text-fg-tertiary underline hover:text-fg-secondary disabled:opacity-40">
                                        {removeBusy ? 'Removing…' : `Remove — as ${signer}`}</button>
                                    <button onClick={() => { setRemoveFor(null); setRemoveReason(''); }}
                                        className="text-[12px] text-fg-tertiary underline hover:text-fg-secondary">Cancel</button>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            ))}
            {w.reads_facts.length > 0 && (
                /* the re-read stays one quiet link away (Kawi, 22 Jul:
                   "let bark read still there") */
                <p className="mt-1.5">
                    <button onClick={() => setConfirmRun(true)} disabled={busy}
                        className="text-[12px] text-fg-tertiary underline hover:text-fg-secondary disabled:opacity-60">
                        {busy ? 'reading…' : 'check the law again…'}</button>
                </p>
            )}
            {w.reads_facts.length === 0 && (nothing && w.read_at
                /* read, found nothing — an honest state of its own (Kawi,
                   22 Jul: "let bark read still there" after the auto-read) */
                ? <p className="mt-1">
                    <span className="text-fg font-medium">Watching nothing — BARK read the signed text
                    ({w.read_at.slice(0, 10)}) and found no condition; it applies whenever the cluster does.</span>
                    <button onClick={() => setConfirmRun(true)} disabled={busy}
                        className="ml-2 text-[12px] text-fg-tertiary underline hover:text-fg-secondary disabled:opacity-60">
                        {busy ? 'reading…' : 'check the law again…'}</button>
                </p>
                : <p className="mt-1">
                    <span>Not read yet — {' '}
                    {nothing && (
                        <button onClick={() => setConfirmRun(true)} disabled={busy}
                            className="text-fg-tertiary underline hover:text-fg-secondary disabled:opacity-60">
                            {busy ? 'reading…' : 'let BARK read it…'}</button>
                    )}</span>
                </p>)}
            {confirmRun && (
                /* ONE row (Kawi, 29 Jul): text left, both quiet acts right */
                <p className="mt-1.5 px-3.5 py-2.5 rounded-md border border-border-default bg-surface text-[12.5px] flex items-baseline gap-4">
                    <span className="min-w-0 truncate">
                        BARK reads the legal text again — watch findings insert themselves,
                        removable with a reason.</span>
                    <span className="ml-auto whitespace-nowrap">
                        <button onClick={() => { setConfirmRun(false); proposeRun(); }}
                            className="text-[12px] text-fg-tertiary underline hover:text-fg-secondary">Read now →</button>
                        <button onClick={() => setConfirmRun(false)}
                            className="ml-3 text-[12px] text-fg-tertiary underline hover:text-fg-secondary">Cancel</button>
                    </span>
                </p>
            )}
            {note && <p className="text-[13px] text-fg mt-1.5 px-3.5 py-2.5 rounded-md border border-border-default bg-surface-sunken">{note}</p>}
            {err2 && <p className="text-[11.5px] text-warning-ink mt-1">{err2}</p>}
            {/* the queue trail (Kawi, 5 Aug "fix it"): work this page sent to
                the Actions queue is named HERE too — the thread stays visible */}
            {w && w.candidates.length > 0 && (
                <p className="mt-1.5 text-[12.5px] text-fg-tertiary">
                    {w.candidates.length} proposal{w.candidates.length > 1 ? 's' : ''} from this obligation waiting in your Actions queue{onActions
                        ? <>{' '}<button onClick={onActions} className="font-semibold text-link hover:underline">open Actions →</button></>
                        : null}
                </p>
            )}
            {/* the legacy candidate path is GONE (29 Jul: 54 converted, 0
                left) — a proposal can only exist auto-inserted now */}
        </div>
    );
}


function ObligationPage({ id, signer, crumb, onArtifact, onActions }: {
    id: string; signer: string; onArtifact: (id: string) => void;
    crumb: { cluster: ClusterRow; onCluster: () => void; onList: () => void };
    onActions?: () => void;
}) {
    const [d, setD] = useState<OblDetail | null>(null);
    const [err, setErr] = useState<string>();
    const [withdrawFor, setWithdrawFor] = useState<string | null>(null);
    // the ONE panel (redesign v3): null = closed; open carries optional prefill
    const [panel, setPanel] = useState<{ name?: string; kind?: string; fromExample?: string } | null>(null);
    // which source row's proof card is open (the stored law copy)
    const [srcOpen, setSrcOpen] = useState<number | null>(null);
    /* the stored text behind a source row (Kawi, 5 Aug "do it") — fetched
       once per copy when its card opens; keyed by the pack file id */
    const [copyText, setCopyText] = useState<Record<string, string | null>>({});
    useEffect(() => {
        if (srcOpen === null || !d) return;
        const snap = d.citations[srcOpen]?.snapshot;
        if (!snap?.viewable || copyText[snap.id] !== undefined) return;
        get<{ text: string | null }>(`/api/inventory/source-copy?file=${encodeURIComponent(snap.id)}`)
            .then(r => setCopyText(t => ({ ...t, [snap.id]: r.text })))
            .catch(() => setCopyText(t => ({ ...t, [snap.id]: null })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [srcOpen, d]);
    const [reload, setReload] = useState(0);
    /* the per-duty decision (Kawi, 22 Jul, ceremony killed): dismiss one
       with a written reason, or bring a dismissed one back — both signed */
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [dismissOpen, setDismissOpen] = useState(false);
    const [dismissReason, setDismissReason] = useState('');
    const [decideBusy, setDecideBusy] = useState(false);
    const [decideErr, setDecideErr] = useState<string>();
    const decide = async (action: 'dismiss' | 'restore') => {
        setDecideBusy(true); setDecideErr(undefined);
        try {
            const r = await fetch(`${API}/api/inventory/obligations/${id}/adoption`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ action, reason: dismissReason.trim(), by: signer }),
            });
            if (!r.ok) throw new Error(`${r.status}`);
            setDismissOpen(false); setDismissReason(''); setReload(x => x + 1);
        } catch (e) { setDecideErr(`Signing failed (${String(e)}). Nothing was recorded.`); }
        setDecideBusy(false);
    };
    useEffect(() => {
        get<OblDetail>(`/api/inventory/obligations/${id}`)
            .then(setD).catch(e => setErr(String(e)));
    }, [id, reload]);
    if (!d) return <Loading error={err} />;

    /* examples never appear on this page (examples-separation, Kawi 16 Jul) —
       they live on the cluster's examples page; here only the bank's own
       signed documents render */
    const yours = d.answers.filter(a => a.origin === 'human_confirmed' && a.data_origin === 'real');
    const done = () => { setWithdrawFor(null); setPanel(null); setReload(r => r + 1); };
    // gap 6: a dismissed obligation takes no new answers — the acts hide,
    // withdrawing an existing answer stays possible
    const dismissed = d.adoption === 'dismissed';

    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={crumb.onCluster} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <button onClick={crumb.onList} className="text-link">Clusters</button>
                <span className="text-border-strong">/</span>
                <button onClick={crumb.onCluster} className="text-link">{crumb.cluster.name}</button>
                <span className="text-border-strong">/</span>
                <span className="text-fg font-semibold">{d.id}</span>
            </div>
            <EditableTitle value={d.title} onSave={async name => {
                await renameCall(`/api/inventory/obligations/${d.id}/rename`, name, signer);
                setD(prev => (prev ? { ...prev, title: name } : prev));
                setReload(r => r + 1);
            }} />
            <FoldingText text={d.plain_language} />
            <p className="text-[13px] text-fg-tertiary max-w-[72ch]">{d.applies_when}</p>
            {/* group binding (Kawi, 17 Jul): which entity this obligation
                binds, at what level — set by a named signer, regulatory
                judgment. No binding = the institution as a whole (the
                fail-closed default, unchanged behaviour). */}
            {/* ONE 'Needs you' strip (v2, 29 Jul mock): the single
                highest-priority open act, and the page's ONLY button.
                Priority: an open flag > dismissed (restore) > no answer >
                nothing needs you. */}
            {(() => {
                const strip = (text: string, btn?: { label: string; onClick: () => void; disabled?: boolean }) => (
                    <div className="flex items-center gap-3.5 flex-wrap border border-border-default rounded-lg bg-secondary-tint px-[18px] py-3.5 my-5">
                        <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-secondary-hover whitespace-nowrap">Needs you</span>
                        <span className="text-[14px] text-fg min-w-0">{text}</span>
                        {btn && (
                            <button onClick={btn.onClick} disabled={btn.disabled}
                                className="ml-auto h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                                {btn.label}</button>
                        )}
                    </div>
                );
                if (d.flag) return strip(
                    `${(d.flag.method || '').includes('profile')
                        ? 'Your profile change moved this obligation'
                        : 'The law moved under this obligation'} — ${d.flag.note}${
                        d.flag.source ? ` · source: ${d.flag.source}` : ''} · flagged ${(d.flag.at || '').slice(0, 10)}.`);
                if (dismissed) return strip(
                    'Dismissed by your bank — it takes no answers. Bring it back if that changes.',
                    isSigner ? { label: decideBusy ? 'Signing…' : `Bring back — as ${signer}`,
                        onClick: () => decide('restore'), disabled: decideBusy } : undefined);
                if (yours.length === 0) return strip(
                    'No answer attached yet — attach the document or entry that answers this obligation.',
                    { label: 'Answer →', onClick: () => setPanel({}) });
                return strip('Nothing needs you here.');
            })()}
            {decideErr && <p className="-mt-3 mb-3 text-[12.5px] text-danger">{decideErr}</p>}
            <AppliesToLine obligationId={d.id} bindings={d.applies_to || []}
                signer={signer} onChanged={() => setReload(r => r + 1)} />
            {/* your side first, the law behind (Kawi, 5 Aug — ordered
                4 Aug): the answer lane sits right under the strip; Watching
                and Sources follow */}
            {/* v2 (29 Jul): the strip holds the page's ONLY button — the
                lane's duplicate Answer entry point is gone; the flow itself
                (the one panel) is unchanged */}
            <LaneLabel>Your answer</LaneLabel>
            {/* the act opens ON TOP (Kawi, 5 Aug: align with the modal rule —
                act forms open on top, never squeezed between content). Same
                DialogShell pattern as the review ask (E-DLG-02), same one-box
                one-title shape as the example modal (4 Aug). */}
            {panel && !dismissed && (
                <DialogShell open onClose={() => setPanel(null)}
                    backdropPolicy="static" ariaLabel="Your answer to this obligation"
                    overlayClassName="flex items-center justify-center p-4"
                    panelClassName="bg-surface rounded-lg shadow-bark-md max-w-xl w-full max-h-[85vh] overflow-y-auto px-5 py-4 text-left">
                    <p className="text-[15px] font-semibold">Your answer to this obligation</p>
                    <p className="text-[12px] text-fg-tertiary mt-0.5">{d.title}</p>
                    <AnswerPanel obligationId={d.id} signer={signer} bare
                        initialName={panel.name} initialKind={panel.kind} fromExample={panel.fromExample}
                        onDone={done} onCancel={() => setPanel(null)} />
                </DialogShell>
            )}
            {/* one message carries the emptiness (Kawi, 4 Aug): the strip
                already says "No answer attached yet" — no second empty box.
                The box stays only with entries in it, or under a flag (there
                the strip is busy and the quiet answer link lives here) */}
            {(yours.length > 0 || d.flag) && (
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mt-2.5">
                {yours.length === 0 && (
                    <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">No entry yet.
                        {/* the strip is busy with the flag — answering stays
                            one quiet link away */}
                        {!dismissed && !panel && d.flag && (
                            <>{' '}<button onClick={() => setPanel({})}
                                className="underline hover:text-fg-secondary">answer →</button></>
                        )}
                    </div>
                )}
                {yours.map(a => (
                    <React.Fragment key={a.id}>
                        <div className="flex justify-between items-center gap-4 px-3.5 py-2.5">
                            <span className="text-[13px] text-fg min-w-0">
                                <Dot tone="g" />{' '}
                                <button onClick={() => onArtifact(a.id)} className="font-bold hover:underline">{a.name}</button>
                                {d.flag
                                    ? <span className="text-[11px] font-semibold text-warning-ink ml-2">signed · under review</span>
                                    : <span className="text-[11px] font-semibold text-secondary-hover ml-2">✓ signed</span>}
                                <span className="block text-[11px] text-fg-tertiary">
                                    {KIND_LABEL[a.kind || ''] || a.kind}{a.confirmed_by ? ` · signed by ${a.confirmed_by}` : ''} · evidence fingerprinted</span>
                            </span>
                            <button onClick={() => setWithdrawFor(withdrawFor === a.id ? null : a.id)}
                                className="text-[11px] text-fg-tertiary hover:text-fg-secondary hover:underline whitespace-nowrap">withdraw</button>
                        </div>
                        {withdrawFor === a.id && (
                            <WithdrawWalk artifactId={a.id} name={a.name} signer={signer} onDone={done} onCancel={() => setWithdrawFor(null)} />
                        )}
                    </React.Fragment>
                ))}
            </div>
            )}

            {/* the quiet dismiss lives ONLY here, inside Your answer (v2,
                29 Jul): written reason still required, Cancel visible */}
            {!dismissed && isSigner && (
                <div className="mt-2 text-[12.5px] text-fg-tertiary">
                    {dismissOpen ? (
                        <span className="inline-flex items-center gap-3 flex-wrap">
                            <input value={dismissReason} onChange={e => setDismissReason(e.target.value)}
                                placeholder="why it does not apply — required"
                                className="h-8 px-2.5 w-72 border border-border-strong rounded-md bg-surface text-fg text-[12.5px]" />
                            <button onClick={() => decide('dismiss')} disabled={!dismissReason.trim() || decideBusy}
                                className="text-[12px] underline hover:text-fg-secondary disabled:opacity-40">
                                {decideBusy ? 'Signing…' : `Remove — as ${signer}`}</button>
                            <button onClick={() => { setDismissOpen(false); setDismissReason(''); }}
                                className="text-[12px] underline hover:text-fg-secondary">Cancel</button>
                        </span>
                    ) : (
                        /* ONE negative-act pattern page-wide (29 Jul): the
                           same quiet link + reason + confirm-as-signer as the
                           watch remove; the record stays a dismissal */
                        <button onClick={() => setDismissOpen(true)}
                            className="underline hover:text-fg-secondary">
                            does not apply? remove from your list — with a reason</button>
                    )}
                </div>
            )}
            {decideErr && <p className="mt-1 text-[12.5px] text-danger">{decideErr}</p>}

            <WiringBlock obligationId={d.id} signer={signer} onActions={onActions} />


            {/* sources are ALWAYS detailed — the Simple/Detailed toggle removed
                on Kawi's order (15 Jul): "i only want detailed here" */}
            <LaneLabel>Sources</LaneLabel>
            {(
                <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                {['Source', 'Provision', 'Level', 'Version'].map(h => (
                                    <th key={h} className="text-left text-[11px] font-semibold tracking-[0.06em] uppercase text-fg-tertiary bg-surface-sunken px-3.5 py-2.5 border-b border-border-default whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {d.citations.map((c, i) => (
                                <tr key={i} onClick={() => setSrcOpen(srcOpen === i ? null : i)}
                                    className="cursor-pointer hover:bg-surface-sunken"
                                    title="Click for the stored copy — the proof behind this source">
                                    <td className="px-3.5 py-2.5 border-b border-divider text-[13px] font-medium">
                                        {/* U-05: regulation refs render in mono */}
                                        <span className={`font-mono ${isNational(c.instrument) ? 'text-accent-copper' : 'text-fg'}`} title={c.instrument || undefined}>
                                            {shortRef(c.instrument)}</span>
                                    </td>
                                    <td className="px-3.5 py-2.5 border-b border-divider text-[13px] font-mono text-fg-secondary">{c.provision}</td>
                                    <td className="px-3.5 py-2.5 border-b border-divider whitespace-nowrap">
                                        {/* an empty bordered chip reads as a value — a citation without a
                                            tier (every pointer-built one) renders nothing here */}
                                        {c.tier ? <span className="text-[11px] font-medium text-fg-secondary border border-border-strong rounded-sm px-1.5 py-0.5">{c.tier}</span> : null}
                                    </td>
                                    <td className="px-3.5 py-2.5 border-b border-divider text-[11px] text-secondary-hover whitespace-nowrap">
                                        {/* a tick is a claim, and it has to be earned. Every status that
                                            can make one is named; anything else — a draft cluster, an
                                            empty edge property, a word added later — says what it is or
                                            says nothing, and never borrows the tick. */}
                                        {c.pin_status === 'flagged' ? '▲ flagged'
                                            : c.pin_status === 'signed edition' ? 'signed edition ✓'
                                            : c.pin_status === 'current' ? 'current ✓'
                                            : c.pin_status ? c.pin_status
                                            : 'not stated'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* the national layer (Kawi, 5 Aug build order): the country
                material the international rule moved OUT of the row comes
                back per bank, through the profile's Home member state */}
            {(d.national_layers || (d.national_layer ? [d.national_layer] : [])).map(nl => (
                <React.Fragment key={nl.country}>
                    <LaneLabel>National layer — {nl.country_name}</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs px-3.5 py-3 text-[13px]">
                        <p className="text-fg max-w-[80ch]">{nl.text || "—"}</p>
                        <p className="text-[11px] text-fg-tertiary mt-1.5">
                            {nl.instrument
                                ? `${nl.instrument} · ${nl.provision || ''} · `
                                : ''}{nl.activation}
                            {' '}· shown because your Country of authorisation = {nl.country_name}
                        </p>
                        {nl.pack_signed ? (
                            <p className="text-[11px] text-fg-tertiary mt-1">
                                <span className="text-success font-semibold">pack signed ✓</span>
                                {' '}· by {nl.signed_by} · {nl.signed_at}</p>
                        ) : (
                            <>
                                {nl.status_note && (
                                    <p className="text-[11px] text-fg-tertiary mt-1">{nl.status_note}</p>
                                )}
                                <PackSignAct country={nl.country}
                                    countryName={nl.country_name}
                                    n={nl.attachment_count || 0}
                                    signer={signer} onDone={() => setReload(x => x + 1)} />
                            </>
                        )}
                    </div>
                </React.Fragment>
            ))}
            {/* the proof card (Kawi, 16 Jul): five fixed lines behind the source
                row — the exact bytes we verified against, fingerprinted. A
                source without a stored copy says so honestly. */}
            {srcOpen !== null && d.citations[srcOpen] && (() => {
                const c = d.citations[srcOpen];
                const s = c.snapshot;
                const line = (k: string, v: React.ReactNode) => (
                    <div className="flex gap-4 px-3.5 py-2 text-[13px]">
                        <span className="w-28 flex-none text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary">{k}</span>
                        <span className="min-w-0 text-fg-secondary break-all">{v}</span>
                    </div>
                );
                return (
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mt-2">
                        {s ? (
                            <>
                                {line('Source', `${shortRef(c.instrument)} — ${s.version || 'version unlabelled'}`)}
                                {line('Captured', (s.captured || '').slice(0, 10) || '—')}
                                {line('Fingerprint', <span className="font-mono text-[11px]">{s.hash || '—'}</span>)}
                                {line('Stored copy', s.stored || '—')}
                                {line('Used by', `${s.used_by} verification check${plural(s.used_by, { one: '', other: 's' })}`)}
                                {s.viewable && (
                                    <div className="px-3.5 py-3">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-tertiary mb-1.5">
                                            The stored text — the exact bytes behind the fingerprint</p>
                                        <div className="max-h-80 overflow-y-auto border border-border-default rounded-md bg-surface-sunken px-3.5 py-3 text-[12px] leading-relaxed text-fg-secondary whitespace-pre-wrap font-mono">
                                            {copyText[s.id] === undefined ? 'Loading the stored copy…'
                                                : copyText[s.id] ?? 'The stored copy could not be read.'}</div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">
                                No stored copy of this source yet — listed for capture. The citation itself stays signed.
                            </div>
                        )}
                    </div>
                );
            })()}


            <div className="mt-[34px] pt-4 border-t border-border-default flex gap-[26px] items-center flex-wrap text-[11px] text-fg-tertiary">
                <IdChip kind="obligation">{d.id}</IdChip>
                <span><b className="text-fg-secondary">Type</b> · {d.obligation_type}</span>
                {/* 8 Aug: this was a dead em-dash — a duty had no owner
                    anywhere in the product. Now it is a real signed act,
                    per bank, on the duty itself. A failed read must never
                    look like "no owner" (PR #608 review). */}
                {d.owner_unavailable ? (
                    /* S-03 (round 3): a real retry, not "reload the page" —
                       the button refetches the detail in place */
                    <span><b className="text-fg-secondary">Owner</b> · <span className="text-warning-ink">could not be read</span>{' '}
                        {/* U-10 — new control, real 32px hit area (round 4) */}
                        <button onClick={() => setReload(x => x + 1)}
                            className="inline-flex items-center h-8 px-2 text-link font-semibold text-[11px]">try again</button></span>
                ) : (
                    <OwnerAct id={d.id} kind="obligations" label="Owner"
                        owner={d.owner || null} signer={signer}
                        setBy={d.owner_set_by} setAt={d.owner_set_at}
                        onDone={() => setReload(x => x + 1)} />
                )}
                <CycleDateAct id={d.id} bc={d.bank_cycle} signer={signer}
                    onDone={() => setReload(x => x + 1)} />
                {/* the bank renamed its view — BARK's catalogue name stays,
                    always visible here (Kawi, 15 Jul) */}
                {d.catalogue_title && d.catalogue_title !== d.title && (
                    <span><b className="text-fg-secondary">Catalogue name</b> · {d.catalogue_title}</span>
                )}
                {crumb.cluster.signed && crumb.cluster.signed_at && (
                    <span className="inline-flex items-center gap-[7px] font-semibold text-secondary-hover">
                        <Dot tone="g" /> Catalogue signed · {crumb.cluster.signed_at}
                    </span>
                )}
                <span className="ml-auto flex gap-4 items-center">
                    <WatchLink kind="obligation" refId={d.id} signer={signer} />
                    <ReviewAsk kind="obligation" refId={d.id} signer={signer} /></span>
            </div>
        </Wrap>
    );
}

/* ---------- artifact detail page ---------- */

type ArtifactDetail = {
    id: string; name: string; kind: string; data_origin: string | null;
    description?: string | null;
    example_name: string | null; added_by: string | null; lifecycle_status: string;
    content_hash: string | null; storage_location: string | null;
    answers: { id: string; title: string; origin: string | null; confirmed_by: string | null;
               change_status?: string | null; cluster_id?: string | null;
               cluster_name?: string | null }[];
    consumes: { id: string; name: string }[];
    feeds: { id: string; name: string }[];
    comes_from: { id: string; name: string }[];
    governs: { id: string; name: string }[];
    describes: { id: string; name: string }[];
    described_by: { id: string; name: string }[];
    owner: string | null;
    required_by: { id: string; name: string; cluster_id?: string | null }[];
    reg_governed_by: { id: string; name: string }[];
    reg_governs: { id: string; name: string }[];
    assessed_by: { id: string; name: string }[];
    instances: { id: string; name: string }[];
    records: { id: string; outcome: string; note: string | null; by: string | null; at: string | null }[];
    doc_read_at?: string | null; doc_read_note?: string | null;
    doc_proposals?: { dp_id: string; kind: string; ref: string; ref_label: string;
        why: string; quote: string;
        stance?: string | null; value?: string | null; profile_value?: string | null }[];
};

/* the read state, in user words (Kawi, 5 Aug: "it needs to be visible") */
function readNoteText(note?: string | null): string {
    if (!note) return 'BARK read this document.';
    if (note.startsWith('read complete')) return 'BARK read this document — nothing more to suggest.';
    if (note.startsWith('no readable text')) return 'BARK could not read this file format — txt, md, csv and pdf work.';
    if (note.startsWith('read failed')) return 'BARK\'s read failed — sign the file in again to retry.';
    return 'BARK could not run its read on this document.';
}

/* one compact suggestion row on the document's own page — one line, the
   quote folded behind "why" (Kawi, 5 Aug: too much text). Profile rows
   carry their ACT (Kawi, 6 Aug: "always think end to end"): an empty
   field gets "Use this" (one signed click); a conflict shows both values
   and the door; same-value rows never exist — the engine stays silent. */
function DocProposalRow({ dp, signer, onDecided, onProfile }: {
    dp: { dp_id: string; kind: string; ref: string; ref_label: string; why: string; quote: string;
          stance?: string | null; value?: string | null; profile_value?: string | null };
    signer: string; onDecided: () => void; onProfile?: (focus?: ProfileFocus) => void;
}) {
    const [why, setWhy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const decide = async (verdict: 'accepted' | 'rejected') => {
        setBusy(true); setErr('');
        try {
            const r = await fetch(`${API}/api/inventory/doc-proposals/${dp.dp_id}/decide`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ verdict, by: signer }),
            });
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `${r.status}`);
            pokeVerdictsBadge();
            onDecided();
        } catch (e) { setErr(String(e).replace(/^Error:\s*/, '')); setBusy(false); }
    };
    const fill = dp.kind === 'profile_fact' && dp.stance === 'fill';
    const conflict = dp.kind === 'profile_fact' && dp.stance === 'conflict';
    return (
        <div className="px-3.5 py-2.5 text-[13px]">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="min-w-0">
                    {dp.kind === 'answer'
                        ? <>also answers <b className="font-semibold">{dp.ref_label}</b>?</>
                        : dp.kind === 'comes_from'
                        ? <>governed by <b className="font-semibold">{dp.ref_label}</b>?</>
                        : fill
                        ? <>states <b className="font-semibold">{dp.ref_label}</b>: <b className="font-semibold">{dp.value}</b> — the profile has nothing here yet</>
                        : conflict
                        ? <>says <b className="font-semibold">{dp.value}</b> — the profile says <b className="font-semibold">{dp.profile_value}</b></>
                        : <>states something about <b className="font-semibold">{dp.ref_label}</b>?</>}
                </span>
                <span className="ml-auto flex gap-3.5 items-center">
                    <button onClick={() => setWhy(o => !o)}
                        className="text-[11px] font-semibold text-link hover:underline whitespace-nowrap">{why ? 'hide' : 'why →'}</button>
                    {dp.kind === 'answer' || dp.kind === 'comes_from' ? (
                        <button onClick={() => decide('accepted')} disabled={busy}
                            className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                            {busy ? 'Signing…' : `Add — as ${signer}`}</button>
                    ) : fill ? (
                        <button onClick={() => decide('accepted')} disabled={busy}
                            className="h-7 px-3 rounded-md bg-primary text-white text-[12px] font-semibold hover:bg-primary-hover disabled:opacity-40">
                            {busy ? 'Signing…' : `Use this — as ${signer}`}</button>
                    ) : onProfile ? (
                        <button onClick={() => onProfile({ dp_id: dp.dp_id, key: dp.ref,
                            label: dp.ref_label, value: dp.value || '', quote: dp.quote || '' })}
                            className="text-[12px] font-semibold text-link hover:underline">Open the profile →</button>
                    ) : null}
                    <button onClick={() => decide('rejected')} disabled={busy}
                        className="text-[11px] font-semibold text-fg-tertiary hover:underline">dismiss</button>
                </span>
            </div>
            {/* the quote — the document's own words — IS the evidence; the
                model's explanation paragraph stays out (Kawi, 5 Aug: "messy") */}
            {why && (
                <p className="text-[12px] text-fg-secondary mt-1.5 mb-0">“{dp.quote}”</p>
            )}
            {err && <p className="text-[11px] text-warning-ink mt-1 mb-0">{err}</p>}
        </div>
    );
}

function ArtifactPage({ id, signer, onBack, onOpenObligation, onOpenCluster, onOpenArtifact, onProfile }: {
    id: string; signer: string; onBack: () => void;
    onOpenObligation: (clusterId: string, oblId: string) => void;
    onOpenCluster: (clusterId: string) => void;
    onOpenArtifact: (id: string) => void;
    onProfile?: (focus?: ProfileFocus) => void;
}) {
    const [d, setD] = useState<ArtifactDetail | null>(null);
    const [err, setErr] = useState<string>();
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [evidenceOpen, setEvidenceOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [comesOpen, setComesOpen] = useState(false);
    const [unlinkFor, setUnlinkFor] = useState<string | null>(null);
    const [unlinkReason, setUnlinkReason] = useState('');
    const [unlinkBusy, setUnlinkBusy] = useState(false);
    const [unlinkErr, setUnlinkErr] = useState<string>();
    const [rmSrcFor, setRmSrcFor] = useState<string | null>(null);
    const [rmSrcReason, setRmSrcReason] = useState('');
    const [rmSrcBusy, setRmSrcBusy] = useState(false);
    const [rmSrcErr, setRmSrcErr] = useState<string>();
    const [reload, setReload] = useState(0);
    useEffect(() => {
        get<ArtifactDetail>(`/api/inventory/artifacts/${id}`)
            .then(setD).catch(e => setErr(String(e)));
    }, [id, reload]);
    /* the read runs in the background after an upload — while the stamp
       says "reading", the page checks back every few seconds so the line
       turns into the suggestions without a reload (Kawi, 5 Aug: "it needs
       to be visible for the user"). A document with NO stamp is from
       before the read existed — quiet, never forever-reading. */
    useEffect(() => {
        if (!d || d.doc_read_note !== 'reading') return;
        const t = setInterval(() => setReload(r => r + 1), 5000);
        return () => clearInterval(t);
    }, [d]);
    if (!d) return <Loading error={err} />;

    const isYours = d.data_origin === 'real';
    const retired = d.lifecycle_status === 'retired';
    return (
        <Wrap>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <span className="text-fg font-semibold">{d.id}</span>
            </div>
            {/* rename is a bank right on the bank's own documents; example
                names belong to the loaded stack (engine writes a view name
                for claimed examples — gap 2) */}
            {isYours && !retired
                ? <EditableTitle value={d.name} onSave={async name => {
                    await renameCall(`/api/inventory/artifacts/${d.id}/rename`, name, signer);
                    setD(prev => (prev ? { ...prev, name } : prev));
                    setReload(r => r + 1);
                }} />
                : <PageTitle>{d.name}</PageTitle>}
            <p className="text-[13px] text-fg-tertiary mb-0">
                {KIND_LABEL[d.kind] || d.kind}
                {retired
                    ? <span className="text-[11px] font-semibold text-fg-tertiary ml-2">retired — kept for its records, counts nowhere</span>
                    : isYours
                    ? <span className="text-[11px] font-semibold text-secondary-hover ml-2">✓ signed</span>
                    : <ExMark />}
                {isYours && !retired && (
                    <button onClick={() => setWithdrawOpen(o => !o)}
                        className="text-[11px] font-semibold text-fg-tertiary hover:text-fg-secondary hover:underline ml-3">withdraw…</button>
                )}
                <span className="text-[11px] ml-3">
                    <OwnerAct id={d.id} owner={d.owner} signer={signer}
                        onDone={() => setReload(x => x + 1)} /></span>
            </p>
            {d.description && (
                <p className="text-[13px] text-fg-secondary mt-2 mb-0 max-w-[72ch]">{d.description}</p>
            )}
            {withdrawOpen && isYours && !retired && (
                <div className="mt-2 border border-border-default rounded-lg overflow-hidden">
                    <WithdrawWalk artifactId={d.id} name={d.name} signer={signer}
                        onDone={() => { setWithdrawOpen(false); setReload(x => x + 1); }}
                        onCancel={() => setWithdrawOpen(false)} />
                </div>
            )}
            {/* a flag on any duty this piece covers bubbles up here — the piece
                is the face, the duty is where the review happens (reality-first) */}
            {(() => {
                if (retired) return null;
                const f = d.answers.find(a => a.change_status && !['green', 'none'].includes(a.change_status || ''));
                if (!f) return null;
                const cid = f.cluster_id;
                return (
                    <NextCard warn kicker="Needs attention" title={`The law moved under ${f.title}`}
                        detail="check this entry still holds, then review the obligation and clear the flag — on record"
                        action={cid ? { label: 'Review →', onClick: () => onOpenObligation(cid, f.id) } : undefined} />
                );
            })()}

            {/* the visible loop (Kawi, 5 Aug): the read state lives WHERE the
                document lives. Reading → a quiet line that updates itself;
                done → this document's suggestions, decidable on the spot;
                nothing/unreadable → one honest line; no stamp at all →
                a document from before the read existed, shown quiet. */}
            {isYours && !retired && d.doc_read_at && (
                d.doc_read_note === 'reading' ? (
                    <p className="text-[12px] text-fg-tertiary mt-4 mb-0">
                        BARK is reading this document — suggestions appear here when it finishes.</p>
                ) : (d.doc_proposals || []).length > 0 ? (
                    <>
                        <LaneLabel right="suggestions count as nothing until you decide">BARK suggests</LaneLabel>
                        <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
                            {(d.doc_proposals || []).map(dp => (
                                <DocProposalRow key={dp.dp_id} dp={dp} signer={signer}
                                    onDecided={() => setReload(r => r + 1)} onProfile={onProfile} />
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="text-[12px] text-fg-tertiary mt-4 mb-0">{readNoteText(d.doc_read_note)}</p>
                )
            )}
            {/* read again (list item 5, 6 Aug): any finished read can be
                re-run — e.g. after the profile changed. Same background
                read; decided suggestions stay decided. */}
            {isYours && !retired && d.doc_read_at && d.doc_read_note !== 'reading' && (
                <p className="mt-1.5 mb-0">
                    <button onClick={async () => {
                        await fetch(`${API}/api/inventory/artifacts/${d.id}/read-again`,
                            { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() } })
                            .catch(() => undefined);
                        setReload(r => r + 1);
                    }} className="text-[11px] font-semibold text-link hover:underline">
                        Let BARK read it again →</button>
                </p>
            )}

            <LaneLabel>Answers these obligations</LaneLabel>
            <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
                {d.answers.length === 0 && (
                    <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">
                        Nothing directly yet.
                    </div>
                )}
                {d.answers.map(o => (
                    <React.Fragment key={o.id}>
                        <div className="flex justify-between items-baseline gap-4 px-3.5 py-2.5">
                            <span className="text-[13px] font-medium min-w-0">
                                <Dot tone={o.change_status && !['green', 'none'].includes(o.change_status) ? 'a' : 'g'} />{' '}
                                {/* register-first: the entry is the front — the obligation
                                    behind it (with its duties) is one click away; ONE arrow
                                    per row (Kawi, 4 Aug: "too messy") */}
                                {o.cluster_id
                                    ? <button onClick={() => onOpenObligation(o.cluster_id!, o.id)}
                                        className="text-left font-medium text-primary hover:underline">
                                        {o.title}
                                    </button>
                                    : <span className="text-fg">{o.title}</span>}
                                {o.cluster_id && (
                                    <span className="block text-[11px] text-fg-tertiary mt-0.5">
                                        in{' '}
                                        <button onClick={() => onOpenCluster(o.cluster_id!)}
                                            className="font-semibold text-primary hover:underline">
                                            {o.cluster_name || o.cluster_id} →</button>
                                    </span>
                                )}
                            </span>
                            <span className="flex items-baseline gap-2 text-[11px] whitespace-nowrap">
                                {o.change_status && !['green', 'none'].includes(o.change_status) && o.cluster_id && (
                                    <button onClick={() => onOpenObligation(o.cluster_id!, o.id)}
                                        className="font-semibold text-warning-ink hover:underline mr-2">review →</button>
                                )}
                                <span className={o.origin === 'human_confirmed' ? 'font-semibold text-secondary-hover' : 'text-fg-tertiary'}>
                                    {o.origin === 'human_confirmed'
                                        ? `confirmed${o.confirmed_by ? ` · ${o.confirmed_by}` : ''}`
                                        : ORIGIN_LABEL[o.origin || ''] || o.origin || '—'}
                                </span>
                                <span className="text-border-strong">·</span>
                                <span className="font-mono text-[11px] text-fg-tertiary">{o.id}</span>
                                {/* gap 3: one wrong link is removable alone, with a
                                    written reason, on record */}
                                {isYours && !retired && (
                                    <button onClick={() => setUnlinkFor(unlinkFor === o.id ? null : o.id)}
                                        className="font-semibold text-fg-tertiary hover:text-fg-secondary hover:underline">· remove</button>
                                )}
                            </span>
                        </div>
                        {unlinkFor === o.id && (
                            <div className="px-3.5 py-3 bg-bg-warm text-[13px] text-fg">
                                <div><b>Remove the link to {o.title}</b> <span className="text-fg-tertiary">— the entry keeps its other links; the removal is on record</span></div>
                                <div className="mt-2 flex gap-2 items-center flex-wrap">
                                    <input value={unlinkReason} onChange={e => setUnlinkReason(e.target.value)}
                                        placeholder="reason — required" autoFocus
                                        className={`text-[11px] border rounded-md px-2.5 h-7 w-72 bg-surface text-fg ${
                                            unlinkReason.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                    <button disabled={unlinkBusy || !unlinkReason.trim()}
                                        onClick={async () => {
                                            setUnlinkBusy(true); setUnlinkErr(undefined);
                                            const r = await fetch(`${API}/api/inventory/artifacts/${d.id}/unlink`, {
                                                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                                body: JSON.stringify({ obligation_id: o.id, reason: unlinkReason.trim(), by: signer }),
                                            });
                                            setUnlinkBusy(false);
                                            if (!r.ok) { setUnlinkErr((await r.json()).detail || `${r.status}`); return; }
                                            setUnlinkFor(null); setUnlinkReason(''); setReload(x => x + 1);
                                        }}
                                        className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                        {unlinkBusy ? 'Removing…' : `Remove — as ${signer}`}
                                    </button>
                                    <button onClick={() => { setUnlinkFor(null); setUnlinkReason(''); }}
                                        className="h-7 px-3 rounded-md border border-border-strong bg-surface text-fg text-[11px] font-semibold hover:bg-bg-warm">Cancel</button>
                                </div>
                                {unlinkErr && <div className="mt-2 text-[11px] text-warning-ink">{unlinkErr}</div>}
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
            {/* adding a link is a signed act — bank documents only; example
                links belong to the loaded stack; a retired document takes
                no acts */}
            {isYours && !retired && (
                <div className="mt-2 flex items-center gap-5">
                    <button onClick={() => { setLinkOpen(o => !o); setComesOpen(false); }}
                        className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Also answers… →</button>
                    <button onClick={() => { setComesOpen(o => !o); setLinkOpen(false); }}
                        className="text-[13px] font-semibold text-link hover:underline whitespace-nowrap">Governed by… →</button>
                </div>
            )}
            {isYours && !retired && linkOpen && (
                <LinkWalk artifactId={d.id} exclude={d.answers.map(a => a.id)} signer={signer}
                    onDone={() => { setLinkOpen(false); setReload(r => r + 1); }}
                    onCancel={() => setLinkOpen(false)} />
            )}
            {isYours && !retired && comesOpen && (
                <ComesFromWalk artifactId={d.id} exclude={d.comes_from.map(x => x.id)} signer={signer}
                    onDone={() => { setComesOpen(false); setReload(r => r + 1); }}
                    onCancel={() => setComesOpen(false)} />
            )}

            {(d.consumes.length > 0 || d.feeds.length > 0 || d.comes_from.length > 0
                || d.governs.length > 0 || d.describes.length > 0 || d.described_by.length > 0
                || d.required_by.length > 0 || d.reg_governed_by.length > 0
                || d.reg_governs.length > 0 || d.assessed_by.length > 0 || d.instances.length > 0) && (
                <>
                    <LaneLabel>Direct connections</LaneLabel>
                    {/* ONE shape for every connection (Kawi, 7 Aug: "governs
                        by this and arrow....nobody understands"): the
                        relationship is a quiet word in its own column, the
                        name is the only link, and the arrow means one thing
                        — open this. No sentence wraps around the arrow. */}
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
                        {d.required_by.map(x => (
                            <ConnRow key={`rq-${x.id}`} rel="required by" id={x.id}
                                name={x.name}
                                onOpen={x.cluster_id ? () => onOpenObligation(x.cluster_id!, x.id) : undefined} />
                        ))}
                        {d.reg_governed_by.map(x => (
                            <ConnRow key={`rgb-${x.id}`} rel="governed by" id={x.id} name={x.name} />
                        ))}
                        {d.reg_governs.map(x => (
                            <ConnRow key={`rg-${x.id}`} rel="governs" id={x.id} name={x.name} />
                        ))}
                        {d.assessed_by.map(x => (
                            <ConnRow key={`as-${x.id}`} rel="assessed by" id={x.id} name={x.name} />
                        ))}
                        {d.instances.map(x => (
                            <ConnRow key={`in-${x.id}`} rel="run of this" id={x.id} name={x.name} />
                        ))}
                        {d.describes.map(x => (
                            <ConnRow key={`ds-${x.id}`} rel="describes" id={x.id} name={x.name}
                                onOpen={() => onOpenArtifact(x.id)} />
                        ))}
                        {d.described_by.map(x => (
                            <ConnRow key={`db-${x.id}`} rel="described by" id={x.id} name={x.name}
                                onOpen={() => onOpenArtifact(x.id)} />
                        ))}
                        {d.comes_from.map(x => (
                            <React.Fragment key={`cf-${x.id}`}>
                                <ConnRow rel="governed by" id={x.id} name={x.name}
                                    onOpen={() => onOpenArtifact(x.id)}
                                    act={isYours && !retired ? (
                                        <button onClick={() => setRmSrcFor(rmSrcFor === x.id ? null : x.id)}
                                            className="font-semibold text-fg-tertiary hover:text-fg-secondary hover:underline">· remove</button>
                                    ) : undefined} />
                                {rmSrcFor === x.id && (
                                    <div className="px-3.5 py-3 bg-bg-warm text-[13px] text-fg">
                                        <div><b>Remove the connection to {x.name}</b> <span className="text-fg-tertiary">— the removal is on record</span></div>
                                        <div className="mt-2 flex gap-2 items-center flex-wrap">
                                            <input value={rmSrcReason} onChange={e => setRmSrcReason(e.target.value)}
                                                placeholder="reason — required" autoFocus
                                                className={`text-[11px] border rounded-md px-2.5 h-7 w-72 bg-surface text-fg ${
                                                    rmSrcReason.trim() ? 'border-border-strong' : 'border-danger'}`} />
                                            <button disabled={rmSrcBusy || !rmSrcReason.trim()}
                                                onClick={async () => {
                                                    setRmSrcBusy(true); setRmSrcErr(undefined);
                                                    const r = await fetch(`${API}/api/inventory/artifacts/${d.id}/remove-comes-from`, {
                                                        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                                        body: JSON.stringify({ source_id: x.id, reason: rmSrcReason.trim(), by: signer }),
                                                    });
                                                    setRmSrcBusy(false);
                                                    if (!r.ok) { setRmSrcErr((await r.json()).detail || `${r.status}`); return; }
                                                    setRmSrcFor(null); setRmSrcReason(''); setReload(v => v + 1);
                                                }}
                                                className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                                                {rmSrcBusy ? 'Removing…' : `Remove — as ${signer}`}
                                            </button>
                                            <button onClick={() => { setRmSrcFor(null); setRmSrcReason(''); }}
                                                className="h-7 px-3 rounded-md border border-border-strong bg-surface text-fg text-[11px] font-semibold hover:bg-bg-warm">Cancel</button>
                                        </div>
                                        {rmSrcErr && <div className="mt-2 text-[11px] text-warning-ink">{rmSrcErr}</div>}
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                        {d.governs.map(x => (
                            <ConnRow key={`gv-${x.id}`} rel="governs" id={x.id} name={x.name}
                                onOpen={() => onOpenArtifact(x.id)} />
                        ))}
                        {d.consumes.map(x => (
                            <ConnRow key={x.id} rel="uses" id={x.id} name={x.name}
                                onOpen={() => onOpenArtifact(x.id)} />
                        ))}
                        {d.feeds.map(x => (
                            <ConnRow key={x.id} rel="feeds" id={x.id} name={x.name}
                                onOpen={() => onOpenArtifact(x.id)} />
                        ))}
                    </div>
                    <p className="text-[11px] text-fg-tertiary mt-2">Direct connections only — follow them page by page.</p>
                </>
            )}

            {/* the bytes moved DOWN and behind a fold (Kawi, 5 Aug: "messy" —
                content first, evidence near the record it proves) */}
            {isYours && d.content_hash && (
                <div className="mt-4">
                    <button onClick={() => setEvidenceOpen(o => !o)}
                        className="text-[12px] font-semibold text-link hover:underline">
                        Evidence — fingerprint and stored copy {evidenceOpen ? '· hide' : '→'}</button>
                    {evidenceOpen && (
                        <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider mt-2">
                            <div className="px-3.5 py-2.5 text-[13px]">
                                <span className="text-fg-tertiary text-[11px]">Fingerprint</span><br />
                                <span className="font-mono text-[11px] text-fg-secondary break-all">{d.content_hash}</span>
                            </div>
                            <div className="px-3.5 py-2.5 text-[13px]">
                                <span className="text-fg-tertiary text-[11px]">Stored file</span><br />
                                <span className="font-mono text-[11px] text-fg-secondary break-all">{d.storage_location}</span>
                            </div>
                            {d.storage_location && (
                                <div className="px-3.5 py-2.5 text-[13px]">
                                    <button onClick={async () => {
                                        const r = await fetch(`${API}/api/inventory/artifacts/${d.id}/file`,
                                            { headers: authHeaders() }).catch(() => null);
                                        if (!r || !r.ok) return;
                                        const blob = await r.blob();
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = (d.storage_location || 'document').split('__').slice(-1)[0];
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }} className="text-[12px] font-semibold text-link hover:underline">
                                        download the stored copy →</button>
                                    <span className="text-[11px] text-fg-tertiary ml-2.5">exactly the fingerprinted bytes — check the hash against it</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {d.records.length > 0 && (
                <>
                    <LaneLabel>On record</LaneLabel>
                    <div className="border border-border-default rounded-lg bg-surface shadow-bark-xs divide-y divide-divider">
                        {d.records.map(r => (
                            <div key={r.id} className="flex justify-between items-baseline gap-4 px-3.5 py-2.5 text-[13px]">
                                <span className="text-fg-secondary">
                                    <b className="text-fg">{r.outcome}</b>{r.note ? ` — ${r.note}` : ''}
                                </span>
                                <span className="text-[11px] text-fg-tertiary whitespace-nowrap">{r.by} · {r.at?.slice(0, 10)}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <div className="mt-[34px] pt-4 border-t border-border-default flex gap-[26px] items-center text-[11px] text-fg-tertiary">
                <IdChip kind="artifact">{d.id} · {(KIND_LABEL[d.kind] || d.kind || 'entry').toLowerCase()}</IdChip>
                {/* the bank renamed its view of a claimed example — the stack
                    name stays, always visible here (gap 2) */}
                {d.example_name && d.example_name !== d.name && (
                    <span><b className="text-fg-secondary">Example name</b> · {d.example_name}</span>
                )}
                <span className="ml-auto flex gap-4 items-center">
                    <WatchLink kind="entry" refId={d.id} signer={signer} />
                    <ReviewAsk kind="entry" refId={d.id} signer={signer} /></span>
            </div>
        </Wrap>
    );
}

/* signing the country pack (Kawi, 6 Aug: the draft mark said "becomes
   real when a named signer signs the pack" — this is that door). One
   signature covers the whole pack file as it stands; editing the file
   voids it honestly (new edition = draft again). */
function PackSignAct({ country, countryName, n, signer, onDone }: {
    country: string; countryName: string; n: number; signer: string; onDone: () => void;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    if (!isSigner) return null;
    const sign = async () => {
        setBusy(true); setErr(undefined);
        const r = await fetch(`${API}/api/inventory/packs/${country}/sign`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ by: signer }),
        }).catch(() => null);
        setBusy(false);
        if (!r || !r.ok) {
            // every refusal here is a 400 with a STRING detail — but a
            // validation envelope from anywhere else is an array of
            // objects, and rendering that crashes React (round 7)
            const d = r ? (await r.json().catch(() => ({}))).detail : null;
            setErr(!r ? 'network error' : (typeof d === 'string' ? d : `${r.status}`));
            return;
        }
        setOpen(false); onDone();
    };
    if (!open) {
        return (
            <p className="text-[11px] mt-1.5">
                <button onClick={() => setOpen(true)} className="text-link font-semibold">
                    review and sign the pack →</button></p>
        );
    }
    return (
        <div className="border-t border-divider mt-2.5 pt-2.5">
            <p className="text-[12.5px] text-fg max-w-[80ch]">
                One signature covers the whole {countryName} pack — {n} attachment{plural(n, { one: '', other: 's' })},
                the file exactly as it stands. If the file changes later, the pack goes back
                to draft until someone signs the new version.</p>
            <div className="flex gap-3 items-center mt-2">
                <button onClick={sign} disabled={busy}
                    className="h-8 px-3.5 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                    {busy ? 'Signing…' : `Sign the ${countryName} pack — as ${signer}`}</button>
                <button onClick={() => { setOpen(false); setErr(undefined); }}
                    className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
                {err && <span className="text-[11px] text-warning-ink">{err}</span>}
            </div>
        </div>
    );
}

/* the bank's NEXT DUE for one obligation (6 Aug): computed from the
   signed cycle; a signed override with a written reason may set the date
   directly — never a silent edit; clearing returns the computed date. */
function CycleDateAct({ id, bc, signer, onDone }: {
    id: string; bc?: { cycle?: string | null; cycle_waiting?: boolean;
        next_due?: string | null; due_kind?: string | null; lands_weekend?: boolean;
        override_passed?: boolean;
        override_by?: string | null; override_reason?: string | null } | null;
    signer: string; onDone: () => void;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [open, setOpen] = useState(false);
    const [cycVal, setCycVal] = useState('');
    const [cycOffVal, setCycOffVal] = useState('');
    const [dateVal, setDateVal] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    const call = async (path: string, body: object) => {
        setBusy(true); setErr(undefined);
        const r = await fetch(`${API}${path}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body),
        }).catch(() => null);
        setBusy(false);
        if (!r || !r.ok) {
            setErr(r ? ((await r.json().catch(() => ({}))).detail || `${r.status}`) : 'network error');
            return false;
        }
        return true;
    };
    const close = () => { setOpen(false); setCycVal(''); setCycOffVal(''); setDateVal(''); setReason(''); setErr(undefined); };
    const sign = async () => {
        const ok = cycVal
            ? await call(`/api/inventory/obligations/${id}/set-cycle`,
                { cycle: cycVal, offset_days: cycOffVal ? Number(cycOffVal) : null, reason: reason.trim(), by: signer })
            : await call(`/api/inventory/obligations/${id}/cycle-date`,
                { date: dateVal, reason: reason.trim(), by: signer });
        if (ok) { close(); onDone(); }
    };
    const clearDate = async () => {
        if (await call(`/api/inventory/obligations/${id}/cycle-date`, { date: null, reason: '', by: signer })) {
            close(); onDone();
        }
    };
    const shown = bc?.next_due
        ? `${fmtDay(bc.next_due)}${bc.due_kind === 'set' ? ` · set by ${bc.override_by}`
            : bc.due_kind === 'period_end' ? ` · ${bc.cycle} · period end`
            : bc.due_kind === 'due' ? ` · ${bc.cycle} · due` : ''}${
            bc.lands_weekend ? ' · weekend — shift rule not confirmed' : ''}${
            bc.override_passed ? ' · your set date passed — computed calendar resumed' : ''}`
        : bc?.cycle === 'event' ? 'event-driven'
        : bc?.cycle === 'none' ? 'no cycle'
        : bc?.cycle_waiting ? 'suggested — in Actions'
        : null;
    if (!open) {
        return (
            <span title={bc?.override_reason || undefined}>
                <b className="text-fg-secondary">Next due</b> · {shown
                    ? <span className="text-fg-secondary">{shown}</span>
                    : <span className="text-border-strong">—</span>}
                {isSigner && <>{' '}<button onClick={() => setOpen(true)}
                    className="text-link font-semibold">change…</button></>}
            </span>
        );
    }
    /* ONE door (6 Aug: "a bit messy with all the fields"): the rule OR a
       one-off date, one reason, one signature. Both filled = ambiguous,
       the button says so and stays off. */
    const both = !!cycVal && !!dateVal;
    const preview = cycVal && cycVal !== 'event' && cycVal !== 'none'
        ? `${cycVal}${cycOffVal ? ` + ${cycOffVal} days` : ''} — the date computes after signing`
        : cycVal ? (cycVal === 'event' ? 'event-driven — no date' : 'no cycle — no date') : null;
    return (
        <span className="flex gap-2 items-center flex-wrap">
            <b className="text-fg-secondary">Next due</b>
            <select value={cycVal} onChange={e => setCycVal(e.target.value)}
                className="text-[11px] h-7 px-2 border border-border-strong rounded-md bg-surface text-fg">
                <option value="">recurrence…</option>
                {['monthly', 'quarterly', 'semi-annual', 'annual', 'event', 'none'].map(w =>
                    <option key={w} value={w}>{w}</option>)}
            </select>
            <input value={cycOffVal} onChange={e => setCycOffVal(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="offset days" disabled={!cycVal}
                className="text-[11px] h-7 px-2.5 w-24 border border-border-strong rounded-md bg-surface text-fg disabled:opacity-50" />
            <span className="text-[11px] text-fg-tertiary">or a one-off date</span>
            <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)}
                className="text-[11px] h-7 px-2 border border-border-strong rounded-md bg-surface text-fg" />
            <input value={reason} onChange={e => setReason(e.target.value)}
                placeholder="reason — required"
                className={`text-[11px] h-7 px-2.5 w-48 border rounded-md bg-surface text-fg ${
                    reason.trim() ? 'border-border-strong' : 'border-danger'}`} />
            <button onClick={sign}
                disabled={busy || both || (!cycVal && !dateVal) || !reason.trim()}
                title={both ? 'one change at a time — sign twice for both' : undefined}
                className="h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold disabled:opacity-50">
                {busy ? 'Signing…' : both ? 'one change at a time' : `Sign — as ${signer}`}</button>
            {(bc?.due_kind === 'set' || bc?.override_passed) && (
                <button onClick={clearDate} disabled={busy}
                    className="text-[11px] font-semibold text-fg-tertiary hover:underline">
                    {bc?.override_passed ? 'clear the old set date' : 'clear the set date — back to computed'}</button>
            )}
            <button onClick={close}
                className="text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
            {preview && <span className="text-[11px] text-fg-tertiary">{preview}</span>}
            {err && <span className="text-warning-ink text-[11px]">{err}</span>}
        </span>
    );
}

/* the OWNER of an entry — the person or function responsible for the
   document (Kawi, 6 Aug: "dont we need an owner for each artifact").
   Setting it is a signed act; the old record stays. */
function OwnerAct({ id, owner, signer, onDone, kind = 'artifacts', label = 'Owner', setBy, setAt }: {
    id: string; owner: string | null; signer: string; onDone: () => void;
    /* 8 Aug: the same act now serves a DUTY as well as an entry — a duty
       needs a responsible person too, and it is stored per bank. One
       component, one wording, two endpoints. */
    kind?: 'artifacts' | 'obligations'; label?: string;
    /* who named this owner and when — the natural second question on an
       accountability line (PR #608 round 2) */
    setBy?: string | null; setAt?: string | null;
}) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [open, setOpen] = useState(false);
    const [val, setVal] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string>();
    /* E-DLG-01 — a one-click clear of a signed accountability field was
       a misclick away from a signed act (PR #608 round 2): the clear now
       confirms through ConfirmDialog first */
    const [clearConfirm, setClearConfirm] = useState(false);
    /* one sender for both acts — the sign carries the typed name, the
       clear sends '' (also a signed record; PR #608 review: the sign
       button never doubles as the clear) */
    const send = async (name: string) => {
        setBusy(true); setErr(undefined);
        const r = await fetch(`${API}/api/inventory/${kind}/${id}/owner`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ owner: name, by: signer }),
        }).catch(() => null);
        setBusy(false);
        if (!r || !r.ok) {
            // every refusal here is a 400 with a STRING detail — but a
            // validation envelope from anywhere else is an array of
            // objects, and rendering that crashes React (round 7)
            const d = r ? (await r.json().catch(() => ({}))).detail : null;
            setErr(!r ? 'network error' : (typeof d === 'string' ? d : `${r.status}`));
            return;
        }
        setClearConfirm(false); setOpen(false); setVal(''); onDone();
    };
    const sign = () => send(val.trim());
    if (!open) {
        return (
            <span><b className="text-fg-secondary">{label}</b> · {owner
                ? <span className="text-fg-secondary">{owner}</span>
                : <span className="text-border-strong">—</span>}
                {owner && setBy && <span className="text-fg-tertiary"> — set by {setBy}{setAt ? `, ${fmtDay(setAt.slice(0, 10))}` : ''}</span>}
                {/* U-10 — the sole entry point to a signed act; 32px like
                    every other control in this row (round 5) */}
                {isSigner && <>{' '}<button onClick={() => { setVal(owner || ''); setOpen(true); }}
                    className="inline-flex items-center h-8 px-1 text-link font-semibold">{owner ? 'change →' : 'set →'}</button></>}</span>
        );
    }
    return (
        <span className="flex gap-2 items-center">
            <b className="text-fg-secondary">{label}</b>
            {/* 200 matches the backend cap — one limit, both ends (PR #608) */}
            {/* U-10 (round 3): 32px controls — the whole edit row (input,
                Sign, clear trigger) moves to h-8 together; a lone 32px
                control in a 28px row would be worse than either baseline */}
            <input value={val} onChange={e => setVal(e.target.value)} autoFocus
                placeholder="a person or a function…" maxLength={200}
                className="text-[11px] h-8 px-2.5 w-48 border border-border-strong rounded-md bg-surface text-fg" />
            {/* U-01 — text-on-primary is the mapped token; text-white was raw
                Tailwind on a line this PR touches (round 4) */}
            <button onClick={sign}
                disabled={busy || !val.trim()}
                className="h-8 px-3 rounded-md bg-primary text-on-primary text-[11px] font-semibold disabled:opacity-50">
                {busy ? 'Signing…' : `Sign — as ${signer}`}</button>
            {owner && kind === 'obligations' && (
                /* a stale sign/change error must not open inside the clear
                   dialog as if the clear had failed (Codex, round 6) */
                <button onClick={() => { setErr(undefined); setClearConfirm(true); }} disabled={busy}
                    className="h-8 px-3 rounded-md border border-border-strong text-[11px] font-semibold text-fg-secondary hover:bg-surface-sunken">
                    clear the owner — on record</button>
            )}
            {/* U-10 — the whole edit row is 32px, Cancel included (round 6) */}
            <button onClick={() => { setOpen(false); setErr(undefined); }}
                className="inline-flex items-center h-8 px-2 text-[11px] font-semibold text-fg-tertiary hover:underline">Cancel</button>
            {err && !clearConfirm && <span className="text-warning-ink text-[11px]">{err}</span>}
            {/* E-DLG-01 (round 3): ConfirmDialog, never a hand-built shell —
                Cancel holds the default focus, so Enter cannot fire the
                signed clear; the hand-built version autofocused the
                destructive button. */}
            {clearConfirm && (
                <ConfirmDialog open onCancel={() => { if (!busy) { setClearConfirm(false); setErr(undefined); } }}
                    onConfirm={() => send('')}
                    title="Clear the owner of this duty"
                    description={`${owner} stops being the named owner. The clearing is a signed record in your name — the record says who went.`}
                    error={err} confirmLabel={`Clear the owner — as ${signer}`}
                    variant="destructive" submitting={busy} />
            )}
        </span>
    );
}

/* the register sweep door (6 Aug: "when i have more obligations, will
   the old already uploaded policy close them?") — one click re-reads
   every signed document against today's obligations; new matches land
   in Actions as suggestions. Costs one model read per document, so it
   asks once before starting. */
function SweepDoor() {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
    const [err, setErr] = useState<string>();
    const start = async () => {
        setState('busy'); setErr(undefined);
        const r = await fetch(`${API}/api/inventory/register/sweep`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        }).catch(() => null);
        if (!r || !r.ok) { setState('idle'); setErr('could not start — try again'); return; }
        setState('done'); setOpen(false);
    };
    if (state === 'done') {
        return <span className="text-[11.5px] text-fg-tertiary whitespace-nowrap">re-reading — suggestions land in Actions</span>;
    }
    if (!open) {
        return (
            <button onClick={() => setOpen(true)}
                className="text-[11.5px] font-semibold text-fg-tertiary hover:text-fg-secondary hover:underline whitespace-nowrap">
                let BARK re-read everything…</button>
        );
    }
    return (
        <span className="flex gap-2.5 items-baseline text-[11.5px] whitespace-nowrap">
            <span className="text-fg-secondary">re-reads every signed document against today's obligations</span>
            <button onClick={start} disabled={state === 'busy'}
                className="font-semibold text-link hover:underline disabled:opacity-50">
                {state === 'busy' ? 'starting…' : 'start →'}</button>
            <button onClick={() => setOpen(false)}
                className="font-semibold text-fg-tertiary hover:underline">Cancel</button>
            {err && <span className="text-warning-ink">{err}</span>}
        </span>
    );
}

/* the personal pin (6 Aug): watch/unwatch an obligation or entry — no
   signature, no record; what it SHOWS on the Actions watchlist is always
   computed from the signed truth */
function WatchLink({ kind, refId, signer }: { kind: 'obligation' | 'entry'; refId: string; signer: string }) {
    const [watching, setWatching] = useState<boolean | null>(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        let alive = true;
        get<{ items: { kind: string; ref_id: string }[] }>(`/api/inventory/watchlist?by=${encodeURIComponent(signer)}`)
            .then(d => { if (alive) setWatching(d.items.some(i => i.kind === kind && i.ref_id === refId)); })
            .catch(() => { if (alive) setWatching(false); });
        return () => { alive = false; };
    }, [kind, refId, signer]);
    const toggle = async () => {
        setBusy(true);
        const r = await fetch(`${API}/api/inventory/watch`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ kind, ref_id: refId, by: signer }),
        }).catch(() => null);
        setBusy(false);
        if (r && r.ok) setWatching((await r.json()).watching);
    };
    if (watching === null) return null;
    return (
        <button onClick={toggle} disabled={busy}
            className="text-[11px] font-semibold text-fg-tertiary hover:text-fg-secondary hover:underline whitespace-nowrap">
            {watching ? 'watching ✓ · unwatch' : 'watch…'}</button>
    );
}

/* the one generic review act (Kawi, 5 Aug): every inventory page footer
   carries it; the page context rides along; the request lands in Actions */
function ReviewAsk({ kind, refId, signer }: { kind: string; refId: string; signer: string }) {
    const [open, setOpen] = useState(false);
    const [words, setWords] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState<string>();
    if (sent) return <span className="text-[11px] font-semibold text-secondary-hover whitespace-nowrap">Review asked — in your Actions ✓</span>;
    return (
        <>
            <button onClick={() => setOpen(true)}
                className="text-[11px] text-fg-tertiary hover:text-fg-secondary hover:underline whitespace-nowrap">
                Something wrong here? Ask for a review…</button>
            {/* the act as a DIALOG (Kawi 5 Aug; E-DLG-02 via DialogShell:
                Escape closes, focus is trapped and restored) */}
            <DialogShell open={open} onClose={() => setOpen(false)}
                backdropPolicy="static" ariaLabel="Ask for a review"
                overlayClassName="flex items-center justify-center p-4"
                panelClassName="bg-surface rounded-lg shadow-bark-md max-w-xl w-full max-h-[85vh] overflow-y-auto px-5 py-4 text-left">
                <p className="text-[15px] font-semibold">Ask for a review</p>
                <p className="text-[12px] text-fg-tertiary mt-0.5">
                    About: {kind} <span className="font-mono">{refId}</span> — your words go to the Actions queue, with your name.</p>
                <textarea value={words} onChange={e => setWords(e.target.value)} autoFocus rows={5} maxLength={2000}
                    placeholder="what looks wrong, missing, or needs a change — in your own words"
                    className="mt-3 w-full px-3 py-2.5 border border-border-strong rounded-md bg-surface text-fg text-[13px] leading-relaxed" />
                {err && <p className="mt-1 text-[11px] text-warning-ink">{err}</p>}
                <div className="flex gap-3.5 items-center mt-3 pt-3 border-t border-divider">
                    <button disabled={busy || !words.trim()}
                        onClick={async () => {
                            setBusy(true); setErr(undefined);
                            const r = await fetch(`${API}/api/inventory/review-requests`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                                body: JSON.stringify({ kind, ref: refId, words: words.trim(), by: signer }),
                            });
                            setBusy(false);
                            if (!r.ok) { setErr((await r.json().catch(() => ({}))).detail || `${r.status}`); return; }
                            setSent(true); setOpen(false);
                            pokeVerdictsBadge();   // the new request counts NOW
                        }}
                        className="h-9 px-4 rounded-md bg-primary text-white text-[13px] font-semibold hover:bg-primary-hover disabled:opacity-60">
                        {busy ? 'Sending…' : `Send — as ${signer}`}</button>
                    <button onClick={() => { setOpen(false); setWords(''); setErr(undefined); }}
                        className="text-[11px] font-semibold text-fg-tertiary hover:underline ml-auto">Cancel</button>
                </div>
            </DialogShell>
        </>
    );
}

/* ---------- verification records page (the signing history, newest first) ---------- */

type VRecord = {
    id: string; date: string | null; method: string | null; scope: string | null;
    outcome: string; note: string | null; by: string | null; at: string | null;
    verdict_scope: string | null; record_ref: string | null;
    /* which duties the act was about — empty for a cluster-level record.
       A grouped act names many (PR #608 round 6). */
    obligation_ids?: string[]; obligation_titles?: string[];
};

type RecordsReply = {
    records: VRecord[]; more?: boolean;
    /* null on a later page — the engine counts the whole cluster once, on
       the first page, and the counts already on screen stand */
    counts?: { cluster_level: number; duty_level: number } | null;
};

function RecordsPage({ cluster, onBack }: { cluster: ClusterRow; onBack: () => void }) {
    const [rows, setRows] = useState<VRecord[] | null>(null);
    const [more, setMore] = useState(false);
    const [counts, setCounts] = useState<{ cluster_level: number; duty_level: number }>();
    /* '' = everything, 'cluster' = the catalogue signatures alone. The
       per-duty acts outnumber the signatures on a real register, and this
       page is titled for the signatures (round 9). */
    const [only, setOnly] = useState<'' | 'cluster'>('');
    const [loadingMore, setLoadingMore] = useState(false);
    const [err, setErr] = useState<string>();
    /* every fetch carries the generation it was started in. Toggling the
       filter (or leaving the cluster) bumps it, and a reply from an older
       generation is dropped — otherwise a "show older" request still in
       flight appends every-act rows into a catalogue-only list. */
    const gen = React.useRef(0);

    const load = (from: number, scope: '' | 'cluster', append: boolean) => {
        const mine = gen.current;
        return get<RecordsReply>(
            `/api/inventory/clusters/${cluster.id}/records?skip=${from}`
            + (scope ? `&only=${scope}` : ''))
            .then(d => {
                if (mine !== gen.current) return;      // a stale reply
                setRows(prev => (append && prev ? [...prev, ...d.records] : d.records));
                setMore(!!d.more);
                /* the engine counts on the FIRST page only — a later page
                   sends no counts, and the ones already on screen stand */
                if (d.counts) setCounts(d.counts);
                setErr(undefined);                     // a good load clears the last failure
            })
            .catch(e => { if (mine === gen.current) setErr(String(e)); });
    };

    useEffect(() => {
        gen.current += 1;
        /* a failure from the PREVIOUS filter must not be rendered by
           <Loading error={err}/> while the new first page is in flight —
           it would read as "this load already failed" (Codex) */
        setRows(null); setMore(false); setLoadingMore(false); setErr(undefined);
        load(0, only, false);
        return () => { gen.current += 1; };             // unmount: drop replies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cluster.id, only]);
    const retryFirstPage = () => {
        setErr(undefined);
        load(0, only, false);
    };
    const header = (
        <>
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <button onClick={onBack} className="text-link">{cluster.name}</button>
                <span className="text-border-strong">/</span>
                <span className="text-fg font-semibold">The record</span>
            </div>
            <PageTitle>How this catalogue was verified</PageTitle>
            <PageSub>Every check and signature on {cluster.name}, newest first — records are permanent, never edited. Acts on a single duty, like naming its owner, appear here too.</PageSub>
        </>
    );
    if (!rows) return (
        <Wrap>
            {header}
            {err ? (
                <div role="alert" className="border border-danger/40 rounded-lg bg-surface px-4 py-3">
                    <p className="text-[13px] text-danger">Could not load the records.</p>
                    <button onClick={retryFirstPage}
                        className="mt-2 inline-flex items-center h-8 px-2 text-[13px] font-semibold text-link">
                        Try again
                    </button>
                </div>
            ) : <Loading />}
        </Wrap>
    );
    const showOlder = async () => {
        const mine = gen.current;
        setLoadingMore(true);
        await load(rows.length, only, true);
        if (mine === gen.current) setLoadingMore(false);
    };

    return (
        <Wrap>
            {header}

            {rows.length === 0 && (
                <p className="text-[13px] text-fg-tertiary">
                    {only === 'cluster'
                        ? 'No signatures on the catalogue itself yet.'
                        : 'No verification records yet.'}</p>
            )}
            {/* the per-duty acts outnumber the catalogue signatures on a
                real register, so the reader can ask for the signatures
                alone instead of paging past everything else (round 9) */}
            {counts && counts.duty_level > 0 && (
                <div className="flex items-baseline gap-3 mb-2 text-[12px] text-fg-tertiary">
                    <span>
                        {counts.cluster_level} on the catalogue · {counts.duty_level} on single duties
                    </span>
                    <button onClick={() => setOnly(only === 'cluster' ? '' : 'cluster')}
                        className="inline-flex items-center h-8 px-2 text-link">
                        {only === 'cluster' ? 'Show every act' : 'Show catalogue signatures only'}
                    </button>
                </div>
            )}
            <div className="flex flex-col gap-2.5">
                {rows.map(r => (
                    <div key={r.id} className="border border-border-default rounded-lg bg-surface shadow-bark-xs px-4 py-3">
                        {/* the sentence first (Kawi, 21 Jul: "the content
                            doesn't make sense for a user") — same data, the
                            audit strings fold underneath for whoever audits */}
                        <div className="flex justify-between items-baseline gap-4">
                            <span className="text-[13.5px] text-fg">
                                <Dot tone={r.outcome === 'signed' || r.outcome === 'confirmed' ? 'g' : 'mute'} />{' '}
                                <b>{r.by || 'A named person'}</b> {r.outcome} — {r.date || r.at?.slice(0, 10) || 'no date'}
                            </span>
                            <span className="font-mono text-[11px] text-fg-tertiary">{r.id}</span>
                        </div>
                        <p className="mt-1 text-[13px] text-fg-secondary">{r.scope || '—'}</p>
                        {/* which duty the act was about — without this the
                            owner trail was written and unreadable (round 6) */}
                        {(r.obligation_titles || []).length > 0 && (
                            <p className="mt-1 text-[12px] text-fg-tertiary">
                                {(r.obligation_titles || []).length === 1
                                    ? <>On: {r.obligation_titles![0]}</>
                                    : <>On {r.obligation_titles!.length + ' ' + plural(r.obligation_titles!.length, { one: 'duty', other: 'duties' })}: {r.obligation_titles!.slice(0, 3).join(' · ')}
                                        {r.obligation_titles!.length > 3 && ` · +${r.obligation_titles!.length - 3} more`}</>}
                            </p>
                        )}
                        <details className="mt-1.5">
                            <summary className="list-none cursor-pointer text-[11.5px] font-semibold text-link [&::-webkit-details-marker]:hidden">
                                how it was checked ▾</summary>
                            <div className="mt-1.5 grid grid-cols-1 gap-y-1 text-[12.5px] text-fg-secondary">
                                <span><b className="text-fg-tertiary text-[11px] uppercase tracking-wide">How</b> · {r.method || '—'}</span>
                                <span><b className="text-fg-tertiary text-[11px] uppercase tracking-wide">Result note</b> · {r.note || r.outcome}</span>
                                <span><b className="text-fg-tertiary text-[11px] uppercase tracking-wide">Against</b> · {r.record_ref || '—'}</span>
                            </div>
                        </details>
                    </div>
                ))}
            </div>
            {/* the older records are REACHABLE, not merely acknowledged:
                round 8 said "they are just not on this page" and gave no
                way to reach them (round 9) */}
            {more && (
                <div className="mt-3 flex flex-col gap-1">
                    <button onClick={showOlder} disabled={loadingMore}
                        className="inline-flex items-center self-start h-8 px-2 gap-1.5 text-[13px] text-link disabled:opacity-50">
                        {loadingMore ? (
                            <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                                Loading
                            </>
                        ) : 'Show older records'}
                    </button>
                    {/* a FAILED page fetch has to be visible. Before, err was
                        only rendered while rows was null, so once the first
                        page had landed a failure showed nothing at all — and
                        on a signing history "nothing more appeared" reads as
                        "you have reached the end". The audit trail would look
                        complete when the fetch had failed. */}
                    {err && (
                        <span className="text-[12.5px] text-danger">
                            Could not load older records. Try again.
                        </span>
                    )}
                </div>
            )}
        </Wrap>
    );
}

/* ---------- withdraw: a new record with a written reason; nothing erased ---------- */

function WithdrawWalk({ artifactId, name, signer, onDone, onCancel }:
    { artifactId: string; name: string; signer: string; onDone: () => void; onCancel: () => void }) {
    const { is_signer: isSigner } = React.useContext(MeContext);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [missing, setMissing] = useState(false);
    const [err, setErr] = useState<string>();
    const [gone, setGone] = useState<'withdrawn' | 'retired' | null>(null);
    const go = async () => {
        if (!reason.trim()) { setMissing(true); return; }
        setBusy(true); setErr(undefined);
        try {
            const r = await fetch(`${API}/api/inventory/artifacts/${artifactId}/withdraw`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ reason: reason.trim(), by: signer }),
            });
            if (!r.ok) throw new Error(`${r.status}`);
            /* the engine says WHICH shape happened — a claimed example goes
               back to the stack, a bank document retires (audit, 6 Aug: the
               box used to claim example-again for both) */
            const out = await r.json().catch(() => ({} as { outcome?: string }));
            setGone(out.outcome === 'retired' ? 'retired' : 'withdrawn');
        } catch (e) { setErr(`Withdrawing failed (${String(e)}). Nothing changed.`); }
        setBusy(false);
    };
    const ghost = 'h-7 px-3 rounded-md border border-border-strong bg-surface text-fg text-[11px] font-semibold hover:bg-bg-warm';
    const primary = 'h-7 px-3 rounded-md bg-primary text-white text-[11px] font-semibold hover:bg-primary-hover disabled:opacity-60';
    if (gone) {
        return (
            <div className="px-3.5 py-3 bg-bg-warm text-[13px] text-fg">
                <b>{gone === 'retired' ? 'Retired' : 'Withdrawn'}</b> — on record: confirmed · {gone} · {signer} · “{reason.trim()}”
                <span className="text-fg-tertiary">{gone === 'retired'
                    ? ' · the entry leaves the register; its page and records stay'
                    : ' · the entry counts as example again'}</span>
                <button onClick={onDone} className={`${primary} ml-3`}>done →</button>
            </div>
        );
    }
    return (
        /* row-level, same rule as the upload walk */
        <div className="px-3.5 py-3 bg-bg-warm text-[13px] text-fg">
            <div><b>Withdraw the confirmation of {name}</b></div>
            <div className="mt-1 text-fg-secondary max-w-[72ch]">
                A written reason is required. The withdrawal is its own record — the original signature stays in the history.
            </div>
            <div className="mt-2 flex gap-2 items-center flex-wrap">
                <input value={reason} onChange={e => { setReason(e.target.value); setMissing(false); }}
                    placeholder="reason — required"
                    className={`text-[11px] border rounded-md px-2.5 h-7 w-72 bg-surface text-fg ${
                        missing ? 'border-danger' : 'border-border-strong'}`} />
                {isSigner ? (
                    <button onClick={go} disabled={busy} className={primary}>
                    {busy ? 'Withdrawing…' : `Withdraw — as ${signer}`}
                </button>
                ) : <WaitingForSigner />}
                <button onClick={onCancel} className={ghost}>Cancel</button>
            </div>
            {missing && <div className="mt-2 text-[11px] text-warning-ink">A written reason is required.</div>}
            {err && <div className="mt-2 text-[11px] text-warning-ink">{err}</div>}
        </div>
    );
}
